import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useReactTable, getCoreRowModel, flexRender, type ColumnDef } from '@tanstack/react-table'
import { useLocation, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { createPdfHeaderElement } from '../utils/pdfHeaderElement'
import { formatDateTime } from '../lib/utils'
import {
  fetchCodeIndexCodeItems,
  fetchCodeIndexHeader,
  publishCodeIndexCodeItems,
  buildCodeItemsPublishPayload,
  type PublishFormatResponse,
} from '../services/codeIndexService'
import { configuredSwal, showConfirmModal, showSuccessModal, showErrorModal, showLoadingModal, closeLoadingModal, updateLoadingModalMessage } from '../components/ui/ConfirmDialog'
import {
  CodeRow,
  CodeItemGroup,
  RuleConfig,
  RuleNode,
  LoopNode,
  IfNode,
  type CodeItemAttributeDefinition,
  type CodeItemAttributeSchemaScope,
  type CodeItemAuthoringMode,
  type CodeItemSubgroup,
} from '../types/codeIndex'
import {
  escapeHtml,
  escapeHtmlForTextarea,
  getBlockId,
  getColumnValue,
  makeBlockId,
  collectBlockValues,
  normalizeBlocks,
  normalizeRowGroupName,
  normalizeRowsForRowGroups,
  reorderCodeItemGroups,
  removeGroupByIndex,
  reindexCodeItemGroups,
  isCodeItemReferenced,
  buildRowMapFromGroupIds,
  buildRowGroupSections,
  areRowIndicesInSameGroupSection,
  doesBlockCoverRow,
  hasMergedSpanByGroupIds,
  moveRowWithinGroupSections,
  syncRowPositions,
  findMissingRuleValueReferences,
  findRuleValueUsages,
  type RuleValueReferenceIssue,
} from '../utils/codeIndexUtils'
import { CodeIndexHeader } from '../components/code-index/CodeIndexHeader'
import { CodeIndexToolbar } from '../components/code-index/CodeIndexToolbar'
import { UndoToast } from '../components/code-index/UndoToast'
import { EmptyState } from '../components/code-index/EmptyState'
import { ConnectionErrorState } from '../components/code-index/ConnectionErrorState'
import ForbiddenState from '../components/ui/ForbiddenState'
import { AvailabilityBadge } from '../components/code-index/AvailabilityBadge'
import CodeItemEditorDialog, { type CodeItemEditorValue } from '../components/code-index/CodeItemEditorDialog'
import CompositeOptionDialog from '../components/code-index/CompositeOptionDialog'
import { ConditionConfigDrawer } from '../components/code-index/ConditionConfigDrawer'
import AttributeSchemaDialog from '../components/code-index/AttributeSchemaDialog'
import ValueAttributesDialog from '../components/code-index/ValueAttributesDialog'
import { useCodesheetActiveUsers } from '../hooks/useCodesheetActiveUsers'
import { useDemoUser } from '../hooks/useDemoUser'
import { useLiveAppendixConsistency } from '../hooks/useLiveAppendixConsistency'
import { useUnsavedCodeSheetNavigationGuard } from '../hooks/useUnsavedCodeSheetNavigationGuard'
import CommentsDrawer from '../components/code-index/CommentsDrawer'
import ProjectsHistoryDrawer from '../components/code-index/ProjectsHistoryDrawer'
import AppendixDrawer from '../components/code-index/AppendixDrawer'
import AppendixRefBadges from '../components/code-index/appendix/AppendixRefBadges'
import AppendixEditorModal from '../components/code-index/appendix/AppendixEditorModal'
import UnresolvedAppendicesDialog from '../components/code-index/appendix/UnresolvedAppendicesDialog'
import ReferenceAppendixModal, { formatAppendixName } from '../components/code-index/appendix/ReferenceAppendixModal'
import ReferencedAppendicesModal from '../components/code-index/appendix/ReferencedAppendicesModal'
import { fetchCodeIndexAppendices } from '../services/appendixService'
import type {
  AppendixConsistencyReport,
  AppendixSummary,
  ProposedRule,
  UnresolvedAppendixItem,
  UnselectableRuleLine,
  UnselectableValueItem,
  UnselectableValuesReport,
} from '../types/codeIndex'
import UnselectableValuesBanner, { UNSELECTABLE_VALUES_FIX_INSTRUCTION } from '../components/code-index/UnselectableValuesBanner'
import UnselectableValueBadge from '../components/code-index/UnselectableValueBadge'
import AppendixDisagreementBadge from '../components/code-index/AppendixDisagreementBadge'
import {
  formatOutstandingReviewMessage,
  getConsistencyResult,
  getRuleDisagreements,
} from '../utils/appendixConsistency'
import { normalizeCodeItemReference } from '../utils/codeReference'
import {
  addAppendixRef,
  buildAppendixSummariesByKey,
  describeAppendixRefTarget,
  normalizeGroupAppendixRefs,
  rekeyGroupAppendixRefs,
  removeAppendixRef,
  resolveAppendixRefs,
  type ResolvedAppendixRef,
} from '../utils/appendixRefs'
import { getRecentAppendixPicks, pushRecentAppendixPick } from '../utils/recentAppendixPicks'
import CreateProjectDialog from '../components/code-index/CreateProjectDialog'
import SubmitForReviewDialog from '../components/code-index/SubmitForReviewDialog'
import CodeSheetStatusBanner from '../components/code-index/CodeSheetStatusBanner'
import { Menu, MenuItem } from '@mui/material'
import { CircleMinus, CirclePlus, GripVertical, MoreVertical, Plus, SlidersHorizontal } from 'lucide-react'
import {
  deriveCompositeOptionRanges,
  findInvalidPositionableRuleReferences,
  getCompositeGroupStructureErrors,
  getCodeItemDigits,
  isCompositeCodeItem,
  normalizeCompositeSubgroups,
  positionSubgroups,
  remapCompositeRulePositions,
  resolveCodeItemSubgroups,
  transformCompositeGroupStructure,
  transformCodeItemResize,
  validateCompositeCodeItem,
} from '../utils/compositeCodeUtils'
import { validateRuleLogic } from '../utils/ruleValidation'
import { getInfoBadgeClassName } from '../components/ui/InfoBadge'
import { useSecurity } from '../security/SecurityContext'
import { getCodesheetActionAccess } from '../security/codesheetAccess'
import { logCodesheetView, logSystemError } from '../services/usageService'
import { getApiMessage } from '../utils/apiMessage'
import {
  getApplicableAttributeDefinitions,
  getAttributeDisplayLabel,
  getAttributeValueValidationError,
  getCodeItemAttributeDefinitions,
  getRowGroupAttributeDefinitions,
  hasAnyAttributeSchemas,
  normalizeAttributeSchemaScopes,
  normalizeAttributeValues,
  pruneAttributeValues,
  validateAttributeSchemaScopes,
} from '../utils/codeItemAttributes'
import {
  cancelProject,
  fetchProjectComparison,
  fetchProjectBranchTree,
  fetchProjectCancellationPreview,
  fetchProjectBranch,
  fetchProjectBranches,
  updateProjectBranchTree,
  type BranchTree,
  type ProjectBranch,
  type ProjectArrivedChange,
  type ProjectMergeConflict,
} from '../services/projectService'
import { createPullRequest, extractAppendixConsistencyReport, extractUnresolvedAppendices, extractUnselectableValuesReport, fetchPullRequests, mergePullRequest, resubmitPullRequestChange, type PullRequestDTO } from '../services/pullRequestService'
import { buildBranchTreeUpdateRequest, mapBranchTreeToCodeItemGroups } from '../utils/projectBranchMapper'
import type { Change } from '../components/code-index/ChangesRequestedDrawer'
import CodeSheetDiffBar from '../components/code-index/CodeSheetDiffBar'
import {
  buildCodeSheetDiffModel,
  formatDiffValue,
  type FieldDiff,
  type LogicalRowDiff,
} from '../utils/codeSheetDiff'

// Reusable typography classes for table consistency
const TABLE_HEADER_TYPOGRAPHY = 'font-medium leading-none tracking-normal'
const TABLE_BODY_TYPOGRAPHY = 'font-normal leading-none tracking-normal'
const TABLE_HEADER_TYPOGRAPHY_STYLE = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontSize: '15px',
  lineHeight: '100%',
  letterSpacing: '0%'
}
const TABLE_BODY_TYPOGRAPHY_STYLE = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontSize: '14px',
  lineHeight: '100%',
  letterSpacing: '0%'
}
const TABLE_SELECTION_BG = 'bg-[#e6f7ff]'
const GROUP_DRAG_BG_COLOR = '#eef6ff'
const ROW_GROUP_HEADER_TYPOGRAPHY_CLASS = 'text-sm font-semibold uppercase tracking-[0.12em] text-[#1D74D8]'
const OPTION_ERROR_TEXT_CLASS = 'text-[11px] leading-3 text-[#B3261E]'
const UNSELECTABLE_ROW_OUTLINE_STYLE = { outline: '2px solid #B3261E', outlineOffset: '-2px' } as const
const TITLE_ROW_INSERT_KEY = '__code-item-title__'
const REQUIRED_FIELD_ERROR = 'Required.'
const OPTION_CODE_ALLOWED_CHARACTERS_ERROR = 'Use only letters A-Z, digits 0-9, *, or #.'
const OPTION_CODE_SINGLE_CHARACTER_VALUES = new Set(['*', '#'])
const STATUS_ALLOWED_VALUES = ['', 'X', 'NS', 'NA'] as const
const STATUS_ALLOWED_VALUE_SET = new Set<string>(STATUS_ALLOWED_VALUES)
const STATUS_ALLOWED_VALUES_ERROR = 'Use X, NS, NA, or leave blank.'
const TEMPORARY_SUCCESS_TOAST_PROPS = {
  autoHideDurationMs: 2000,
  showCloseButton: false,
} as const
const PROJECT_COMPLETED_STATUSES = new Set(['Published', 'Cancelled'])
const PROJECT_READY_TO_MERGE_STATUS = 'AllApproved'
const DIFF_UNAVAILABLE_PROJECT_STATUSES = new Set(['Merged', 'Unpublished', 'Published', 'Cancelled'])
const CHANGE_NAVIGATION_FOCUS_DURATION_MS = 10_000

function DiffText({ diff, className = '' }: { diff: FieldDiff; className?: string }) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`} data-testid="modified-field-diff">
      <span className="break-words text-[#B3261E] line-through">{formatDiffValue(diff.before)}</span>
      <span className="break-words font-medium text-[#1E7A3F]">{formatDiffValue(diff.after)}</span>
    </div>
  )
}

// Appendix references are stored as appendix ids; the diff shows their letters, as the badges do.
function toAppendixLetterDiff(diff: FieldDiff, summariesByKey: { get(key: string): { letter: string } | undefined }): FieldDiff {
  const toLetters = (value: unknown): string[] =>
    Array.isArray(value) ? value.map((key) => summariesByKey.get(String(key))?.letter || '?') : []
  return { before: toLetters(diff.before), after: toLetters(diff.after) }
}

function InlineDiffText({ diff, className = '' }: { diff: FieldDiff; className?: string }) {
  return (
    <span
      className={`flex shrink-0 items-center gap-1 whitespace-nowrap ${className}`}
      data-testid="modified-field-diff"
      data-diff-layout="inline"
    >
      <span className="text-[#B3261E] line-through">{formatDiffValue(diff.before)}</span>
      <span className="text-gray-400" aria-hidden="true">→</span>
      <span className="font-medium text-[#1E7A3F]">{formatDiffValue(diff.after)}</span>
    </span>
  )
}

function ArrivedText({ value, revision, className = '' }: { value: unknown; revision?: string | null; className?: string }) {
  const hasValue = value !== null && value !== undefined && value !== ''
  return (
    <div className={`flex min-w-0 flex-col gap-1 ${className}`} data-testid="arrived-field-change">
      {hasValue ? <span className="break-words text-gray-700">{formatDiffValue(value)}</span> : null}
      <span className="text-[11px] font-medium leading-tight text-[#315B78]">
        {revision ? `From revision ${revision}` : 'From later revision'}
      </span>
    </div>
  )
}

function ConflictLabel({ revision }: { revision?: string | null }) {
  return (
    <div className="mt-1 rounded border border-[#B3261E] bg-[#FFF1F1] px-1.5 py-1 text-[11px] font-semibold leading-tight text-[#B3261E]" data-testid="field-conflict">
      Conflicts with {revision ? `revision ${revision}` : 'a later revision'}
    </div>
  )
}

function DiffIndicator({ status }: { status: LogicalRowDiff['status'] }) {
  if (status === 'added') {
    return <CirclePlus className="h-4 w-4 shrink-0 text-[#1E7A3F]" aria-label="Added row" />
  }
  if (status === 'deleted') {
    return <CircleMinus className="h-4 w-4 shrink-0 text-[#B3261E]" aria-label="Deleted row" />
  }
  return null
}

function normalizeOptionCode(value: string | null | undefined): string {
  return String(value ?? '').toUpperCase().replace(/[^A-Z0-9*#]/g, '')
}

function getLiveOptionCodeInputValue(
  nextValue: string | null | undefined,
  previousValue: string | null | undefined,
  digits: number,
  inputType?: string,
  inputData?: string | null
): { value: string; removedInvalidCharacters: boolean } {
  const maxLength = Math.max(1, digits)
  const upperValue = String(nextValue || '').toUpperCase()
  const normalizedValue = normalizeOptionCode(upperValue)
  const removedInvalidCharacters = upperValue !== normalizedValue

  if (!normalizedValue) {
    return {
      value: '',
      removedInvalidCharacters,
    }
  }

  if (!/^\d+$/.test(normalizedValue)) {
    return {
      value: normalizedValue.slice(0, maxLength),
      removedInvalidCharacters,
    }
  }

  const previousNormalizedValue = normalizeOptionCode(previousValue)
  const previousWasNumeric = /^\d+$/.test(previousNormalizedValue)

  if (
    inputType === 'insertText' &&
    previousWasNumeric &&
    typeof inputData === 'string' &&
    /^\d$/.test(inputData) &&
    normalizedValue.length > maxLength
  ) {
    const significantPreviousDigits = previousNormalizedValue.replace(/^0+/, '')
    const nextDigits = `${significantPreviousDigits}${inputData}`.slice(-maxLength)

    return {
      value: nextDigits.padStart(maxLength, '0'),
      removedInvalidCharacters,
    }
  }

  if (inputType === 'deleteContentBackward' && previousWasNumeric) {
    const significantPreviousDigits = previousNormalizedValue.replace(/^0+/, '')
    const nextDigits = significantPreviousDigits.slice(0, -1)

    return {
      value: nextDigits ? nextDigits.padStart(maxLength, '0') : '',
      removedInvalidCharacters,
    }
  }

  return {
    value: normalizedValue.slice(-maxLength).padStart(maxLength, '0'),
    removedInvalidCharacters,
  }
}

function finalizeOptionCode(value: string | null | undefined, digits: number): string {
  const normalizedOptionCode = normalizeOptionCode(value)
  if (!normalizedOptionCode) return ''
  if (OPTION_CODE_SINGLE_CHARACTER_VALUES.has(normalizedOptionCode)) return normalizedOptionCode

  if (/^\d+$/.test(normalizedOptionCode) && normalizedOptionCode.length < digits) {
    return normalizedOptionCode.padStart(digits, '0')
  }

  return normalizedOptionCode
}

function getOptionCodeSaveValidationError(
  optionCode: string | null | undefined,
  digits: number
): string | null {
  const finalizedOptionCode = finalizeOptionCode(optionCode, digits)

  if (!finalizedOptionCode) {
    return REQUIRED_FIELD_ERROR
  }

  if (OPTION_CODE_SINGLE_CHARACTER_VALUES.has(finalizedOptionCode)) {
    return null
  }

  if (finalizedOptionCode.length !== digits) {
    return `Use ${digits} digit${digits === 1 ? '' : 's'}.`
  }

  return null
}

function getOptionCodeLengthValidationError(
  optionCode: string | null | undefined,
  digits: number
): string | null {
  const rawOptionCode = String(optionCode ?? '').toUpperCase()
  if (/[^A-Z0-9*#]/.test(rawOptionCode)) {
    return OPTION_CODE_ALLOWED_CHARACTERS_ERROR
  }

  const normalizedOptionCode = normalizeOptionCode(optionCode)

  if (!normalizedOptionCode) {
    return REQUIRED_FIELD_ERROR
  }

  if (OPTION_CODE_SINGLE_CHARACTER_VALUES.has(normalizedOptionCode)) {
    return null
  }

  if (normalizedOptionCode.length !== digits) {
    return `Use ${digits} digit${digits === 1 ? '' : 's'}.`
  }

  return null
}

function getDescriptionValidationError(value: string | null | undefined): string | null {
  return String(value || '').trim() ? null : REQUIRED_FIELD_ERROR
}

function normalizeStatusValue(value: string | null | undefined): string {
  const normalizedValue = String(value ?? '').trim().toUpperCase()
  return normalizedValue === 'EMPTY' ? '' : normalizedValue
}

function isAllowedStatusValue(value: string | null | undefined): boolean {
  return STATUS_ALLOWED_VALUE_SET.has(normalizeStatusValue(value))
}

function getStatusValidationError(value: string | null | undefined): string | null {
  return isAllowedStatusValue(value) ? null : STATUS_ALLOWED_VALUES_ERROR
}

function getMissingRuleValueErrorMessage(issue: RuleValueReferenceIssue): string {
  return `Cannot save rule. Value ${issue.missingValue} does not exist on code item ${issue.referencedCodeItemCode}.`
}

function formatRuleReferenceUsageLabel(ruleLabel: string): string {
  const normalizedRuleLabel = String(ruleLabel || '').trim()
  if (!normalizedRuleLabel) return 'Rule'
  return /^rule\b/i.test(normalizedRuleLabel) ? normalizedRuleLabel : `Rule ${normalizedRuleLabel}`
}

function buildRuleReferenceUsageDescription(usage: RuleValueReferenceIssue): string {
  const sourceValue = String(usage.sourceOptionCode || '').trim() || '(blank)'
  const sourceCodeItem = String(usage.sourceCodeItemCode || '').trim() || 'Unknown'
  return `${formatRuleReferenceUsageLabel(usage.ruleLabel)} attached to value ${sourceValue} of code item ${sourceCodeItem}`
}

function buildBrokenRuleReferenceDescription(issue: RuleValueReferenceIssue): string {
  return getMissingRuleValueErrorMessage(issue)
}

function buildReferencedValuePrimaryErrorMessage(
  action: 'remove' | 'change',
  valueIdentifier: string,
  usage: RuleValueReferenceIssue
): string {
  const normalizedValueIdentifier = String(valueIdentifier || '').trim() || '(blank)'
  return `Cannot ${action} value ${normalizedValueIdentifier}. The following rule(s) reference this value: ${buildRuleReferenceUsageDescription(usage)}`
}

function appendBoxShadow(
  baseShadow: string | undefined,
  nextShadow: string
): string {
  return baseShadow ? `${baseShadow}, ${nextShadow}` : nextShadow
}

function getRowHighlightShadow(position: 'start' | 'middle' | 'end' | 'single'): string {
  switch (position) {
    case 'start':
      return 'inset 2px 0 0 #B3261E, inset 0 2px 0 #B3261E, inset 0 -2px 0 #B3261E'
    case 'middle':
      return 'inset 0 2px 0 #B3261E, inset 0 -2px 0 #B3261E'
    case 'end':
      return 'inset -2px 0 0 #B3261E, inset 0 2px 0 #B3261E, inset 0 -2px 0 #B3261E'
    default:
      return 'inset 0 0 0 2px #B3261E'
  }
}

function buildExpandableModalDescriptionHtml(
  summaryDescription: string,
  items: Array<{ id: string; description: string }>,
  expandedLabel: string
): string {
  const hasExpandableList = items.length > 1
  const listItemsHtml = items.map((item, index) => `
    <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:12px 0; ${index < items.length - 1 ? 'border-bottom:1px solid #D6E4F2;' : ''}">
      <div style="flex:1; text-align:left; color:#334155; font-size:13px; line-height:1.5;">
        ${escapeHtml(item.description)}
      </div>
      <button
        type="button"
        data-reference-navigation-index="${index}"
        style="flex-shrink:0; border:1px solid #1D5176; background:#FFFFFF; color:#1D5176; border-radius:6px; padding:6px 10px; font-size:12px; font-weight:600; cursor:pointer;"
      >
        Go to this reference
      </button>
    </div>
  `).join('')

  return `
    <div class="py-6 px-5 rounded-xl bg-[#F1F7FD]">
      <div class="text-center text-gray-600 text-sm leading-relaxed">${escapeHtml(summaryDescription)}</div>
      ${hasExpandableList ? `
        <div style="margin-top:12px; text-align:center;">
          <button
            type="button"
            data-reference-expand-toggle="true"
            data-expanded-label="${escapeHtml(expandedLabel)}"
            data-collapsed-label="Hide all references"
            style="border:none; background:transparent; color:#1D74D8; font-size:12px; font-weight:600; cursor:pointer; padding:0;"
          >
            ${escapeHtml(expandedLabel)}
          </button>
        </div>
        <div
          data-reference-expand-panel="true"
          style="display:none; margin-top:12px; max-height:240px; overflow-y:auto; border:1px solid #D6E4F2; border-radius:10px; background:#FFFFFF; padding:0 14px;"
        >
          ${listItemsHtml}
        </div>
      ` : ''}
    </div>
  `
}

type DraftRowGroupSection = {
  id: string
  name: string
}

type RowGroupInsertTarget = {
  gIdx: number
  sectionKey: string
}

type RowFocusTarget = {
  gIdx: number
  rIdx: number
}

type ReferenceBlockedModalItem = {
  id: string
  description: string
  navigationTarget: RowFocusTarget | null
}

type RuleValidationIssue = {
  gIdx: number
  rIdx: number
  ruleKey: string
  severity: 'error' | 'warning'
  codeItemCode: string
  optionCode: string
  message: string
}

/** Identifies a rule across row reorders, so "edited this session" survives moving rows around. */
function getRuleKey(group: CodeItemGroup, row: CodeRow): string {
  return `${group.id ?? group.localKey ?? group.codeItemPos.code}|${row.rowId}`
}

/**
 * Key a flagged value and a row the same way, so a report line can find its row even when the
 * server id is unknown: the normalized code item reference, the option code and the slot start.
 */
function getUnselectableValueKey(
  codeItemCode: string | null | undefined,
  optionCode: string | null | undefined,
  valuePosition: number | null | undefined
): string {
  const position = Number.isInteger(valuePosition) ? Number(valuePosition) : ''
  return `${normalizeCodeItemReference(codeItemCode)}|${String(optionCode ?? '').trim().toUpperCase()}|${position}`
}

type UnselectableRowEntry = {
  item: UnselectableValueItem
  // The flagged value or a rule it names changed since the last save; only a save can re-evaluate it.
  needsRecheck: boolean
}

/** What a recheck compares: the rule, declared unavailability and where the value sits. */
function getUnselectableRowSignature(row: CodeRow): string {
  return JSON.stringify({
    rule: row.rule ?? null,
    isNA: normalizeStatusValue(row.status) === 'NA',
    optionCode: String(row.optionCode ?? '').trim().toUpperCase(),
    valuePosition: row.valuePosition ?? null,
    valuePositionEnd: row.valuePositionEnd ?? null,
  })
}

type UnselectableRowRef = { gIdx: number; rIdx: number; row: CodeRow }

type UnselectableRowIndex = {
  byId: Map<string, UnselectableRowRef>
  byKey: Map<string, UnselectableRowRef>
  // Code item + option only, for report lines that do not carry the slot start.
  byCodeAndOption: Map<string, UnselectableRowRef>
}

function indexRowsForUnselectableLookup(groups: CodeItemGroup[]): UnselectableRowIndex {
  const byId = new Map<string, UnselectableRowRef>()
  const byKey = new Map<string, UnselectableRowRef>()
  const byCodeAndOption = new Map<string, UnselectableRowRef>()
  groups.forEach((group, gIdx) => {
    group.rows.forEach((row, rIdx) => {
      if (row.isNew) return
      const entry = { gIdx, rIdx, row }
      if (row.id) byId.set(row.id, entry)
      const key = getUnselectableValueKey(group.codeItemPos.code, row.optionCode, row.valuePosition)
      if (!byKey.has(key)) byKey.set(key, entry)
      const looseKey = getUnselectableValueKey(group.codeItemPos.code, row.optionCode, null)
      if (!byCodeAndOption.has(looseKey)) byCodeAndOption.set(looseKey, entry)
    })
  })
  return { byId, byKey, byCodeAndOption }
}

function findUnselectableRow(
  index: UnselectableRowIndex,
  formatValueId: string | null | undefined,
  codeItemCode: string,
  optionCode: string,
  valuePosition: number | null | undefined
): UnselectableRowRef | null {
  return (formatValueId ? index.byId.get(formatValueId) : undefined)
    ?? index.byKey.get(getUnselectableValueKey(codeItemCode, optionCode, valuePosition))
    ?? index.byCodeAndOption.get(getUnselectableValueKey(codeItemCode, optionCode, null))
    ?? null
}

/**
 * Flagged values mapped onto the rows currently shown, with whether each one needs a recheck. A
 * flagged value whose row no longer exists is counted in `missingCount`; it still blocks submit.
 */
function mapUnselectableReportToRows(
  report: UnselectableValuesReport | null,
  groups: CodeItemGroup[],
  originalGroups: CodeItemGroup[]
): { byRowKey: Record<string, UnselectableRowEntry>; missingCount: number } {
  const byRowKey: Record<string, UnselectableRowEntry> = {}
  if (!report || !Array.isArray(report.values) || report.values.length === 0) {
    return { byRowKey, missingCount: 0 }
  }

  const current = indexRowsForUnselectableLookup(groups)
  const original = indexRowsForUnselectableLookup(originalGroups)

  // A named row has changed when it is gone, or when its rule or placement differs from the saved copy.
  const hasRowChanged = (
    formatValueId: string | null | undefined,
    codeItemCode: string,
    optionCode: string,
    valuePosition: number | null | undefined
  ): boolean => {
    const currentEntry = findUnselectableRow(current, formatValueId, codeItemCode, optionCode, valuePosition)
    if (!currentEntry) return true
    const originalEntry = findUnselectableRow(
      original,
      currentEntry.row.id,
      codeItemCode,
      currentEntry.row.optionCode,
      currentEntry.row.valuePosition
    )
    if (!originalEntry) return true
    return getUnselectableRowSignature(currentEntry.row) !== getUnselectableRowSignature(originalEntry.row)
  }

  let missingCount = 0
  report.values.forEach((item) => {
    const target = findUnselectableRow(current, item.formatValueId, item.codeItemCode, item.optionCode, item.valuePosition)
    if (!target) {
      missingCount += 1
      return
    }

    const needsRecheck = hasRowChanged(item.formatValueId, item.codeItemCode, item.optionCode, item.valuePosition)
      || (item.rules ?? []).some(line => hasRowChanged(line.formatValueId, line.codeItemCode, line.optionCode, null))

    byRowKey[`${target.gIdx}:${target.rIdx}`] = { item, needsRecheck }
  })

  return { byRowKey, missingCount }
}

/** Text of the save-success modal when the server returned a check result. */
function getSavedUnselectableValuesDescription(report: UnselectableValuesReport): string {
  if (report.count === 0) {
    return 'Changes saved. No unexpected unselectable values were found.'
  }
  return `Changes saved. ${report.count} value${report.count === 1 ? '' : 's'} can no longer be selected. ${UNSELECTABLE_VALUES_FIX_INSTRUCTION}`
}

/** The save-success text with one sentence per check the server ran on the save. */
function getSavedChecksDescription(
  unselectable: UnselectableValuesReport | null,
  appendix: AppendixConsistencyReport | null
): string | null {
  const hasAppendixLine = Boolean(appendix && appendix.appendices.length > 0)
  if (!unselectable && !hasAppendixLine) return null

  const sentences: string[] = []
  sentences.push(unselectable ? getSavedUnselectableValuesDescription(unselectable) : 'Changes saved.')
  if (appendix && hasAppendixLine) {
    sentences.push(appendix.count === 0
      ? 'The rules agree with every appendix that defines valid combinations.'
      : `${appendix.count} disagreement${appendix.count === 1 ? '' : 's'} between the rules and an appendix. Open the appendix to resolve ${appendix.count === 1 ? 'it' : 'them'}.`)
  }
  return sentences.join(' ')
}

function buildRuleValidationIssueDescription(issue: RuleValidationIssue): string {
  return `Code ${issue.codeItemCode} value ${issue.optionCode || '(blank)'}: ${issue.message}`
}

type UndoToastState = {
  message: string
  autoHideDurationMs?: number
  showCloseButton?: boolean
}

type AddMenuAnchorState = {
  element: HTMLElement
  gIdx: number
  rIdx: number | null
  sectionKey: string | null
  insertIndex: number | null
  rowGroupName: string | null
  draftSectionId: string | null
}

type CodeItemActionMenuState = {
  element: HTMLElement
  gIdx: number
}

type ValueAttributesTarget = {
  gIdx: number
  rIdx: number
  attributeId: string | null
}

// What an appendix reference is being added to or listed for.
type AppendixRefTarget =
  | { kind: 'codeItem'; gIdx: number }
  | { kind: 'value'; gIdx: number; rIdx: number }
  | { kind: 'group'; gIdx: number; sectionName: string }

type InlineEditState =
  | {
      kind: 'optionCode'
      gIdx: number
      rIdx: number
      originalValue: string
      draftValue: string
      error: string | null
    }
  | {
      kind: 'status'
      gIdx: number
      rIdx: number
      originalValue: string
      draftValue: string
      error: string | null
    }
  | {
      kind: 'description'
      gIdx: number
      rIdx: number
      blockId: string
      originalValue: string
      draftValue: string
      error: string | null
    }
  | {
      kind: 'groupTitle'
      gIdx: number
      originalValue: string
      draftValue: string
      error: string | null
    }
  | {
      kind: 'rowGroupTitle'
      gIdx: number
      sectionKey: string
      rowIndices: number[]
      draftId: string | null
      originalValue: string
      draftValue: string
      error: string | null
    }

function getTableCellBackgroundClass(isSelected: boolean): string {
  return isSelected ? TABLE_SELECTION_BG : 'bg-white'
}

function isHttpNotFoundError(error: unknown): boolean {
  return typeof error === 'object'
    && error !== null
    && (error as { response?: { status?: unknown } }).response?.status === 404
}

function isProjectEditable(project: ProjectBranch | null): boolean {
  if (!project) return false
  if (isMainProject(project)) return isInitialSetupProject(project)
  return String(project.status || '') === 'Open'
}

function isProjectReadyToMerge(project: ProjectBranch | null): boolean {
  return String(project?.status || '') === PROJECT_READY_TO_MERGE_STATUS
}

function isInitialSetupProject(project: ProjectBranch): boolean {
  return String(project.status || '') === 'InitialSetup'
}

function isMainProject(project: ProjectBranch): boolean {
  return String(project.name || '').trim().toLowerCase() === 'main'
}

function getProjectReadOnlyReason(project: ProjectBranch | null): string | null {
  if (!project) return 'Project details are still loading.'
  if (isMainProject(project) && !isInitialSetupProject(project)) {
    return 'Main is view-only after the initial Code Sheet setup.'
  }
  if (isProjectReadyToMerge(project)) {
    return null
  }
  if (String(project.status || '') === 'SubmittedForReview') {
    return 'Submitted projects are view-only. Rejected changes can be revised from Changes Requested.'
  }
  if (PROJECT_COMPLETED_STATUSES.has(String(project.status || ''))) {
    return 'Published and cancelled projects are view-only.'
  }
  return null
}

function normalizePullRequestState(state: unknown): string {
  return String(state || '').replace(/[\s_-]+/g, '').toLowerCase()
}

function findReadyToMergePullRequest(projectId: string, pullRequests: PullRequestDTO[]): PullRequestDTO | null {
  return pullRequests.find((pullRequest) => {
    if (!pullRequest.id) return false
    if (pullRequest.sourceBranch?.id !== projectId) return false

    const state = normalizePullRequestState(pullRequest.state)
    return state === 'approved' || state === 'open'
  }) || null
}

function getProjectSubmitForReviewReason(project: ProjectBranch | null, hasChanges: boolean): string | null {
  if (!project) return 'Project details are still loading.'

  const status = String(project.status || '')
  if (status === 'Open' || status === 'InitialSetup') {
    return hasChanges ? 'Save changes before submitting this project for review.' : null
  }

  return `Project cannot be submitted for review while it is ${status || 'in this state'}.`
}

function getCodeSheetStructureSignature(groups: CodeItemGroup[]): string {
  return JSON.stringify(groups.map(group => ({
    id: group.id,
    rows: group.rows.map(row => ({ id: row.id, ruleId: row.rule?.id ?? null })),
  })))
}

type RowDropPosition = 'before' | 'after'

function getRowDropIndicatorStyle(isActive: boolean, position: RowDropPosition | null): React.CSSProperties | undefined {
  if (!isActive || !position) return undefined

  return {
    boxShadow: position === 'before'
      ? 'inset 0 2px 0 #1D74D8'
      : 'inset 0 -2px 0 #1D74D8'
  }
}

function getGroupDragHighlightStyle(isActive: boolean): React.CSSProperties | undefined {
  if (!isActive) return undefined

  return {
    backgroundColor: GROUP_DRAG_BG_COLOR,
  }
}


function createDraftRowGroupSection(name: string): DraftRowGroupSection {
  return {
    id: `draft-row-group-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name,
  }
}

function createNewCodeRow(
  codeItemCode: string,
  position: number,
  rowGroup: string | null = null,
  valuePosition: number | null = null,
  valuePositionEnd: number | null = null
): CodeRow {
  const rowId = `${codeItemCode}-NEW-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

  return {
    rowId,
    optionCode: '',
    status: '',
    group: normalizeRowGroupName(rowGroup),
    valuePosition,
    valuePositionEnd,
    position,
    description: null,
    descriptionBlockId: rowId,
    availabilityBlockId: rowId,
    regions: [],
    isNew: true,
    rule: undefined,
    attributeValues: {},
  }
}

function cloneState<T>(value: T): T {
  return JSON.parse(JSON.stringify(value))
}

// SweetAlert2's open animation is ~300ms; the capture blocks the main thread, so let the
// loading modal finish appearing before it starts.
const PDF_LOADING_MODAL_PAINT_MS = 350

const CodeIndexDetailsPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const activeProjectId = String(searchParams.get('projectId') || '').trim()
  const isProjectMode = Boolean(activeProjectId)
  const { user, roles, permissions, has, loading: securityLoading } = useSecurity()
  const { demoUserEmail } = useDemoUser()
  const canViewCodeSheets = has('View_Codesheets')
  const [groups, setGroups] = useState<CodeItemGroup[]>([])
  const [originalGroups, setOriginalGroups] = useState<CodeItemGroup[]>([])
  const [liveComparisonGroups, setLiveComparisonGroups] = useState<CodeItemGroup[]>([])
  const [baseComparisonGroups, setBaseComparisonGroups] = useState<CodeItemGroup[]>([])
  const [projectComparisonTree, setProjectComparisonTree] = useState<BranchTree | null>(null)
  const [baseComparisonTree, setBaseComparisonTree] = useState<BranchTree | null>(null)
  const [reviewBaseComparisonTree, setReviewBaseComparisonTree] = useState<BranchTree | null>(null)
  const [reviewBaseComparisonGroups, setReviewBaseComparisonGroups] = useState<CodeItemGroup[]>([])
  const [comparisonConflicts, setComparisonConflicts] = useState<ProjectMergeConflict[]>([])
  const [liveComparisonTree, setLiveComparisonTree] = useState<BranchTree | null>(null)
  const [arrivedComparisonChanges, setArrivedComparisonChanges] = useState<ProjectArrivedChange[]>([])
  const [comparisonHasConflicts, setComparisonHasConflicts] = useState(false)
  const [comparisonBaseUnavailable, setComparisonBaseUnavailable] = useState(false)
  const [comparisonLoading, setComparisonLoading] = useState(false)
  const [comparisonError, setComparisonError] = useState<string | null>(null)
  const [diffVisible, setDiffVisible] = useState(true)
  const [changedRowsOnly, setChangedRowsOnly] = useState(false)
  const [activeDiffEntryKey, setActiveDiffEntryKey] = useState<string | null>(null)
  const [activeChangeFocusTone, setActiveChangeFocusTone] = useState<'navigation' | 'revision'>('navigation')
  const [activeChangeFocusRequest, setActiveChangeFocusRequest] = useState(0)
  const latestGroupsRef = useRef<CodeItemGroup[]>([])
  const loggedCodesheetViewRef = useRef<string | null>(null)
  const [codesheetViewReadyId, setCodesheetViewReadyId] = useState<string | null>(null)
  const [headerLoadAttempted, setHeaderLoadAttempted] = useState(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [loadError, setLoadError] = useState<boolean>(false)
  const [loadErrorKind, setLoadErrorKind] = useState<'not_found' | 'generic' | null>(null)
  const [codeItemEditorTarget, setCodeItemEditorTarget] = useState<
    | { mode: 'create'; insertAfter: number | null }
    | { mode: 'edit'; gIdx: number; autoFocusCompositeAdd?: boolean }
    | null
  >(null)
  const [header, setHeader] = useState<{ partNumber: string; businessUnit: string; productLine: string; version?: string; lastMergedText: string; format: string }>({
    partNumber: '--',
    businessUnit: '--',
    productLine: '--',
    version: '--',
    lastMergedText: '--',
    format: '--',
  })
  const [activeProject, setActiveProject] = useState<ProjectBranch | null>(null)
  // Result of the last unselectable-values check on this project; replaced only by a save or a load.
  const [unselectableReport, setUnselectableReport] = useState<UnselectableValuesReport | null>(null)
  // Result of the last comparison of the rules against the valid-combination appendices; replaced
  // by a save (codesheet or appendix) or a load, like the unselectable-values report.
  const [savedAppendixConsistencyReport, setAppendixConsistencyReport] = useState<AppendixConsistencyReport | null>(null)
  // An appendix to open as soon as the appendix drawer opens (from a disagreement on the codesheet).
  const [appendixToOpen, setAppendixToOpen] = useState<string | null>(null)
  // A rule drafted from an appendix, shown in the condition drawer for the engineer to edit and accept.
  const [proposedRule, setProposedRule] = useState<ProposedRule | null>(null)
  const [projects, setProjects] = useState<ProjectBranch[]>([])
  const [projectsDrawerOpen, setProjectsDrawerOpen] = useState(false)
  const [appendixDrawerOpen, setAppendixDrawerOpen] = useState(false)
  // Bumped after each codesheet save so appendix editors read the saved code items and values again.
  const [codesheetSavedAt, setCodesheetSavedAt] = useState(0)
  // Appendices that blocked the last submit for review, and the one opened from that list.
  const [unresolvedAppendices, setUnresolvedAppendices] = useState<UnresolvedAppendixItem[] | null>(null)
  const [incompleteAppendixId, setIncompleteAppendixId] = useState<string | null>(null)
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [submitForReviewOpen, setSubmitForReviewOpen] = useState(false)
  const [revisionTarget, setRevisionTarget] = useState<Change | null>(null)
  const [revisionTree, setRevisionTree] = useState<BranchTree | null>(null)
  const [revisionSaved, setRevisionSaved] = useState(false)
  const [changesRequestedRefreshKey, setChangesRequestedRefreshKey] = useState(0)
  const [mergedLiveBannerCodeIndexId, setMergedLiveBannerCodeIndexId] = useState<string | null>(null)
  const isPreviewMode = false
  const codesheetBusinessUnit = header.businessUnit && header.businessUnit !== '--'
    ? header.businessUnit
    : null
  const appendixDrawerSubtitle = useMemo(() => {
    const format = header.format && header.format !== '--' ? `Format ${header.format}` : null
    const productLine = header.productLine && header.productLine !== '--' ? header.productLine : null
    return [[format, productLine].filter(Boolean).join(' - '), codesheetBusinessUnit]
      .filter(Boolean)
      .join(' – ')
  }, [codesheetBusinessUnit, header.format, header.productLine])
  const codesheetAccessContext = useMemo(() => ({
    user,
    roles,
    permissions,
    codesheetBusinessUnit,
  }), [codesheetBusinessUnit, permissions, roles, user])
  const editCodesheetAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'edit_codesheet'), [codesheetAccessContext])
  const deleteSelectionAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'delete_selection'), [codesheetAccessContext])
  const saveChangesAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'save_changes'), [codesheetAccessContext])
  const submitChangesAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'submit_changes'), [codesheetAccessContext])
  const commentsAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'manage_comments'), [codesheetAccessContext])
  const changesRequestedAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'view_changes_requested'), [codesheetAccessContext])
  const reviewChangesetAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'review_changeset'), [codesheetAccessContext])
  const cancelProjectAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'cancel_project'), [codesheetAccessContext])
  const reviseChangeAccess = useMemo(() => getCodesheetActionAccess(codesheetAccessContext, 'revise_change'), [codesheetAccessContext])
  const projectReadOnlyReason = useMemo(() => isProjectMode ? getProjectReadOnlyReason(activeProject) : 'Open or create a project to edit this Code Sheet.', [activeProject, isProjectMode])
  const isActiveProjectReadyToMerge = isProjectMode && isProjectReadyToMerge(activeProject)
  const isInitialMainSetup = isProjectMode && Boolean(activeProject && isMainProject(activeProject) && isInitialSetupProject(activeProject))
  const canEditActiveProject = isProjectMode && isProjectEditable(activeProject)
  const displayedProjectStatus = activeProject && !isMainProject(activeProject)
    ? activeProject.status || null
    : null
  const isRevisingRejectedChange = Boolean(revisionTarget && String(activeProject?.status || '') === 'SubmittedForReview')
  const canMutateCodeSheet = editCodesheetAccess.allowed && (canEditActiveProject || isRevisingRejectedChange) && !isPreviewMode
  const canModifyCodeSheetStructure = editCodesheetAccess.allowed && canEditActiveProject && !isPreviewMode

  useEffect(() => {
    if (isProjectMode) {
      setMergedLiveBannerCodeIndexId(null)
      return
    }

    const navigationState = location.state as { mergedToMain?: boolean; codeIndexId?: string } | null
    if (!id || !navigationState?.mergedToMain || navigationState.codeIndexId !== id) return

    setMergedLiveBannerCodeIndexId(id)
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null })
  }, [id, isProjectMode, location.hash, location.pathname, location.search, location.state, navigate])

  const readOnlyBannerContent = useMemo(() => {
    if (!isProjectMode && !editCodesheetAccess.blockedBy) {
      return (
        <>
          <span>View only — click </span>
          <strong>New Project</strong>
          <span> to start editing.</span>
        </>
      )
    }

    if (isProjectMode && projectReadOnlyReason && !editCodesheetAccess.blockedBy) {
      return (
        <>
          <span>{projectReadOnlyReason}</span>
        </>
      )
    }

    if (!editCodesheetAccess.blockedBy) return null

    if (editCodesheetAccess.blockedBy === 'wrong_business_unit') {
      const normalizedCodesheetBusinessUnit = codesheetBusinessUnit || 'another Business Unit'

      return (
        <>
          <span>This Code Sheet belongs to </span>
          <strong>{normalizedCodesheetBusinessUnit}</strong>
          <span>. You can view it, but you can only modify Code Sheets in your own </span>
          <strong>Business Unit</strong>
          <span>. You are currently in </span>
          <strong>read-only mode</strong>
          <span>.</span>
        </>
      )
    }

    return (
      <>
        <span>Your current role does not include the permission required to modify this Code Sheet. You are currently in </span>
        <strong>read-only mode</strong>
        <span>.</span>
      </>
    )
  }, [codesheetBusinessUnit, editCodesheetAccess.blockedBy, isProjectMode, projectReadOnlyReason])
  const pageMutationDisabledReason = !editCodesheetAccess.allowed
    ? editCodesheetAccess.message
    : !canEditActiveProject && !isRevisingRejectedChange
      ? projectReadOnlyReason
      : null
  const canViewChangesRequested = changesRequestedAccess.allowed
  const canReviewChangesets = reviewChangesetAccess.allowed && ['SubmittedForReview', 'AllApproved'].includes(String(activeProject?.status || ''))
  const canManageComments = commentsAccess.allowed && !isPreviewMode

  const applyGroupsUpdate = useCallback((updater: React.SetStateAction<CodeItemGroup[]>) => {
    setGroups((prev) => {
      const next = typeof updater === 'function'
        ? (updater as (current: CodeItemGroup[]) => CodeItemGroup[])(prev)
        : updater
      latestGroupsRef.current = next
      return next
    })
  }, [])

  useEffect(() => {
    latestGroupsRef.current = groups
  }, [groups])

  useEffect(() => {
    setDiffVisible(true)
    setChangedRowsOnly(false)
    setActiveDiffEntryKey(null)
    setActiveChangeFocusTone('navigation')
    setActiveChangeFocusRequest(0)
  }, [activeProjectId])

  useEffect(() => {
    loggedCodesheetViewRef.current = null
    setCodesheetViewReadyId(null)
    setHeaderLoadAttempted(false)
  }, [id, user?.emailAddress, user?.name])

  const clearComparisonData = useCallback(() => {
    setLiveComparisonGroups([])
    setBaseComparisonGroups([])
    setProjectComparisonTree(null)
    setBaseComparisonTree(null)
    setReviewBaseComparisonTree(null)
    setReviewBaseComparisonGroups([])
    setComparisonConflicts([])
    setLiveComparisonTree(null)
    setArrivedComparisonChanges([])
    setComparisonHasConflicts(false)
    setComparisonBaseUnavailable(false)
    setComparisonError(null)
    setComparisonLoading(false)
  }, [])

  const loadComparisonData = useCallback(async (
    formatId: string,
    projectId: string,
    _mainProjectId?: string
  ) => {
    setComparisonLoading(true)
    setComparisonError(null)
    try {
      const [projectTree, comparison] = await Promise.all([
        fetchProjectBranchTree(formatId, projectId),
        fetchProjectComparison(formatId, projectId),
      ])
      setLiveComparisonGroups(mapBranchTreeToCodeItemGroups(comparison.liveTree))
      setBaseComparisonGroups(mapBranchTreeToCodeItemGroups(comparison.baseTree))
      setReviewBaseComparisonTree(comparison.reviewBaseTree || comparison.baseTree)
      setReviewBaseComparisonGroups(mapBranchTreeToCodeItemGroups(comparison.reviewBaseTree || comparison.baseTree))
      setComparisonConflicts(comparison.conflicts?.conflicts || [])
      setProjectComparisonTree(projectTree)
      setBaseComparisonTree(comparison.baseTree)
      setLiveComparisonTree(comparison.liveTree)
      setArrivedComparisonChanges(comparison.arrivedChanges)
      setComparisonHasConflicts(Boolean(comparison.conflicts?.hasConflicts))
      setComparisonBaseUnavailable(false)
    } catch (error) {
      console.error('Failed to load Code Sheet comparison.', error)
      setLiveComparisonGroups([])
      setBaseComparisonGroups([])
      setProjectComparisonTree(null)
      setBaseComparisonTree(null)
      setReviewBaseComparisonTree(null)
      setReviewBaseComparisonGroups([])
      setComparisonConflicts([])
      setLiveComparisonTree(null)
      setArrivedComparisonChanges([])
      setComparisonHasConflicts(false)
      const message = getApiMessage(error) || 'Unable to compare this project with the live Code Sheet.'
      setComparisonError(message)
      setComparisonBaseUnavailable(message.toLowerCase().includes('reliable immutable base'))
    } finally {
      setComparisonLoading(false)
    }
  }, [])

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    setLoadErrorKind(null)
    try {
      const formatId = id || ''
      const projectsResult = await fetchProjectBranches(formatId)
      setProjects(projectsResult)

      if (!activeProjectId) {
        const initialSetupProject = projectsResult.find(isInitialSetupProject)
        if (initialSetupProject?.id) {
          navigate(`/details/${encodeURIComponent(formatId)}?projectId=${encodeURIComponent(initialSetupProject.id)}`, { replace: true })
          return
        }
      }

      if (activeProjectId) {
        const [project, projectGroups, projectAppendices] = await Promise.all([
          fetchProjectBranch(formatId, activeProjectId),
          fetchCodeIndexCodeItems(formatId, activeProjectId),
          fetchCodeIndexAppendices(formatId, { projectId: activeProjectId }),
        ])
        setAppendixSummaries(Array.isArray(projectAppendices) ? projectAppendices : [])
        applyGroupsUpdate(projectGroups as CodeItemGroup[])
        setOriginalGroups(cloneState(projectGroups as CodeItemGroup[]))
        setActiveProject(project)
        setUnselectableReport(project.unselectableValues ?? null)
        setAppendixConsistencyReport(project.appendixConsistency ?? null)
        setCodesheetViewReadyId(formatId || null)
        setPublished(project.status === 'Published')
        setHasChanges(false)
        setUndoStack([])
        const mainProject = projectsResult.find(isMainProject)
        if (!isMainProject(project) && mainProject?.id) {
          await loadComparisonData(formatId, activeProjectId, mainProject.id)
        } else if (!isMainProject(project)) {
          clearComparisonData()
          setComparisonError('The live Main project is not available for comparison.')
        } else {
          clearComparisonData()
        }
        return
      }

      clearComparisonData()
      setActiveProject(null)
      setUnselectableReport(null)
      setAppendixConsistencyReport(null)
      const [result, liveAppendices] = await Promise.all([
        fetchCodeIndexCodeItems(formatId),
        fetchCodeIndexAppendices(formatId),
      ])
      setAppendixSummaries(Array.isArray(liveAppendices) ? liveAppendices : [])
      applyGroupsUpdate(result as CodeItemGroup[])
      setOriginalGroups(cloneState(result as CodeItemGroup[]))
      setCodesheetViewReadyId(formatId || null)
      setPublished(true)
      setHasChanges(false)
      setUndoStack([])
    } catch (err) {
      console.error('Failed to load Code Sheet details.', err)
      logSystemError(user, {
        action: activeProjectId ? 'load_codesheet_project_details' : 'load_codesheet_details',
        error: err,
        resource: 'codesheet',
        resourceId: id,
        context: activeProjectId ? { projectId: activeProjectId } : undefined,
      })
      setLoadError(true)
      setLoadErrorKind(isHttpNotFoundError(err) ? 'not_found' : 'generic')
    } finally {
      setLoading(false)
    }
  }, [activeProjectId, applyGroupsUpdate, clearComparisonData, id, loadComparisonData, navigate, user])

  const loadHeader = useCallback(async () => {
    try {
      if (!id) return
      setHeaderLoadAttempted(false)
      const h = await fetchCodeIndexHeader(id)
      setHeader({
        partNumber: h.partNumber || '--',
        businessUnit: h.businessUnit || '--',
        productLine: h.productLine || '--',
        version: (h.version ?? '') || '--',
        lastMergedText: h.lastMergedText || '--',
        format: h.format_Id || '--',
      })
    } catch (err) {
      console.error('Failed to load Code Sheet header.', err)
      logSystemError(user, {
        action: 'load_codesheet_header',
        error: err,
        resource: 'codesheet',
        resourceId: id,
      })
      if (isHttpNotFoundError(err)) {
        setLoadError(true)
        setLoadErrorKind('not_found')
      }
    } finally {
      setHeaderLoadAttempted(true)
    }
  }, [id, user])

  useEffect(() => {
    if (!canViewCodeSheets || !codesheetViewReadyId || !headerLoadAttempted || user === null || loadErrorKind === 'not_found') return

    const identityKey = user?.emailAddress || user?.name || 'unknown-user'
    const viewKey = `${codesheetViewReadyId}:${identityKey}`
    if (loggedCodesheetViewRef.current === viewKey) return

    loggedCodesheetViewRef.current = viewKey
    logCodesheetView(user, codesheetViewReadyId, {
      source: 'CodeIndexDetailsPage',
      format: header.format && header.format !== '--' ? header.format : null,
      businessUnit: header.businessUnit && header.businessUnit !== '--' ? header.businessUnit : null,
      productLine: header.productLine && header.productLine !== '--' ? header.productLine : null,
      partNumber: header.partNumber && header.partNumber !== '--' ? header.partNumber : null,
      version: header.version && header.version !== '--' ? header.version : null,
    })
  }, [canViewCodeSheets, codesheetViewReadyId, header, headerLoadAttempted, loadErrorKind, user])

  useEffect(() => {
    if (securityLoading || user === null) return
    if (!canViewCodeSheets) {
      setLoading(false)
      return
    }

    loadData()
  }, [canViewCodeSheets, loadData, securityLoading, user])

  useEffect(() => {
    setReportedAdvisoryRuleKeys(new Set<string>())
  }, [id, activeProjectId])

  useEffect(() => {
    if (securityLoading || user === null || !canViewCodeSheets) return
    loadHeader()
  }, [canViewCodeSheets, loadHeader, securityLoading, user])

  type ColumnKey = 'description' | 'availability'
  const [cellSelection, setCellSelection] = useState<Record<string, boolean>>({})
  const [anchor, setAnchor] = useState<{ gIdx: number | null; rIdx: number | null; col: ColumnKey | null }>({ gIdx: null, rIdx: null, col: null })

  const [insertTarget, setInsertTarget] = useState<{ gIdx: number | null; rIdx: number | null }>({ gIdx: null, rIdx: null })

  const [published, setPublished] = useState<boolean>(true)

  const [hasChanges, setHasChanges] = useState<boolean>(false)
  const hasGroupChanges = useMemo(
    () => JSON.stringify(groups) !== JSON.stringify(originalGroups),
    [groups, originalGroups]
  )
  const hasEffectiveChanges = hasChanges || hasGroupChanges
  const { report: appendixConsistencyReport, message: appendixCheckMessage } = useLiveAppendixConsistency(
    id, activeProjectId, groups, hasEffectiveChanges, savedAppendixConsistencyReport,
    isProjectMode && !pageMutationDisabledReason
  )

  const [saving, setSaving] = useState<boolean>(false)
  const unselectableRows = useMemo(
    () => mapUnselectableReportToRows(isProjectMode ? unselectableReport : null, groups, originalGroups),
    [isProjectMode, unselectableReport, groups, originalGroups]
  )
  const unselectableByRowKey = unselectableRows.byRowKey
  // Every value the last save flagged counts until the next save, edited or not.
  const outstandingUnselectableCount = isProjectMode ? (unselectableReport?.count ?? 0) : 0
  const unselectableNeedsRecheck = unselectableRows.missingCount > 0
    || Object.values(unselectableByRowKey).some(entry => entry.needsRecheck)
  // Unsaved edits use the live preview; saved data uses the persisted comparison.
  const outstandingAppendixDisagreementCount = isProjectMode ? (appendixConsistencyReport?.count ?? 0) : 0
  const appendixConsistencyResults = useMemo(
    () => (isProjectMode ? appendixConsistencyReport?.appendices ?? [] : []),
    [isProjectMode, appendixConsistencyReport]
  )
  const appendixNeedsRecheck = Boolean(appendixCheckMessage)
  const ruleAppendixDisagreements = useMemo(
    () => getRuleDisagreements(isProjectMode ? appendixConsistencyReport : null),
    [isProjectMode, appendixConsistencyReport]
  )
  // Unselectable values and appendix disagreements share the Submit gate; the reason tells them apart.
  const outstandingUnselectableReason = outstandingUnselectableCount + outstandingAppendixDisagreementCount > 0
    ? formatOutstandingReviewMessage(outstandingUnselectableCount, outstandingAppendixDisagreementCount)
    : null
  const immutableBaseDisabledReason = comparisonBaseUnavailable
    ? 'This project has no reliable immutable base. Recreate the project before submitting or merging changes.'
    : null
  const submitForReviewDisabledReason = immutableBaseDisabledReason
    || pageMutationDisabledReason
    || outstandingUnselectableReason
    || submitChangesAccess.message
    || (isProjectMode ? getProjectSubmitForReviewReason(activeProject, hasEffectiveChanges) : null)
  const revisionResubmitDisabledReason = !revisionSaved
    ? 'Save the rejected change before resubmitting it.'
    : hasEffectiveChanges
      ? 'Save the latest revision before resubmitting it.'
      : null
  const cancellableProjectStatuses = new Set(['Open', 'SubmittedForReview', 'AllApproved'])
  const canCancelActiveProject = Boolean(activeProject && cancellableProjectStatuses.has(String(activeProject.status || '')))
  const cancelProjectDisabledReason = !cancelProjectAccess.allowed
    ? cancelProjectAccess.message
    : hasEffectiveChanges
      ? 'Save or discard unsaved changes before cancelling the project.'
      : null
  const requiresSafeMergeComparison = Boolean(isProjectMode && activeProject && !isMainProject(activeProject))
  const comparisonSafetyDisabledReason = requiresSafeMergeComparison && (
    comparisonLoading || (!comparisonError && (!projectComparisonTree || !baseComparisonTree || !liveComparisonTree))
  )
    ? 'Checking this project against the latest Main revision.'
    : requiresSafeMergeComparison && comparisonError && !comparisonBaseUnavailable
      ? 'The latest Main comparison is unavailable. Retry before merging.'
      : null
  const mergeToMainDisabledReason = immutableBaseDisabledReason
    || comparisonSafetyDisabledReason
    || (comparisonHasConflicts ? 'Resolve the conflicting fields before merging this project.' : null)
    || (!isActiveProjectReadyToMerge
      ? 'Project must have all changes approved before it can be merged.'
      : null)

  const [groupInsertTarget, setGroupInsertTarget] = useState<number | null>(null)
  const tableScrollRef = useRef<HTMLDivElement | null>(null)
  const tableExportRef = useRef<HTMLTableElement | null>(null)

  const [searchQuery, setSearchQuery] = useState<string>('')

  const [draftRowGroupsByCodeItemKey, setDraftRowGroupsByCodeItemKey] = useState<Record<string, DraftRowGroupSection[]>>({})
  const [undoStack, setUndoStack] = useState<Array<{ groups: CodeItemGroup[]; draftRowGroupsByCodeItemKey: Record<string, DraftRowGroupSection[]>; hasChanges: boolean }>>([])
  const [undoToast, setUndoToast] = useState<UndoToastState | null>(null)
  const [inlineEdit, setInlineEdit] = useState<InlineEditState | null>(null)
  const [newRowOptionValidation, setNewRowOptionValidation] = useState<{ gIdx: number; rIdx: number; error: string } | null>(null)
  const [newRowDescriptionValidation, setNewRowDescriptionValidation] = useState<{ gIdx: number; rIdx: number; error: string } | null>(null)
  const [availabilityConfigRow, setAvailabilityConfigRow] = useState<{ gIdx: number; rIdx: number } | null>(null)
  const [compositeOptionTarget, setCompositeOptionTarget] = useState<{ gIdx: number; rIdx: number } | null>(null)
  const [hoveredGroupReorder, setHoveredGroupReorder] = useState<number | null>(null)
  const [draggedGroup, setDraggedGroup] = useState<number | null>(null)
  const [groupDropTarget, setGroupDropTarget] = useState<{ gIdx: number; position: RowDropPosition } | null>(null)
  const [hoveredReorderRow, setHoveredReorderRow] = useState<{ gIdx: number; rIdx: number } | null>(null)
  const [draggedRow, setDraggedRow] = useState<{ gIdx: number; rIdx: number } | null>(null)
  const [draggedCompositeSubgroup, setDraggedCompositeSubgroup] = useState<{ gIdx: number; subgroupKey: string } | null>(null)
  const [rowDropTarget, setRowDropTarget] = useState<{ gIdx: number; rIdx: number; position: RowDropPosition } | null>(null)
  const [rowGroupInsertTarget, setRowGroupInsertTarget] = useState<RowGroupInsertTarget | null>(null)
  const inlineEditInputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null)
  const setInlineEditInputElementRef = useCallback((node: HTMLInputElement | null) => {
    inlineEditInputRef.current = node
  }, [])
  const setInlineEditSelectElementRef = useCallback((node: HTMLSelectElement | null) => {
    inlineEditInputRef.current = node
  }, [])
  const inlineEditBlurActionRef = useRef<'commit' | 'cancel' | null>(null)
  const newRowBlurActionRef = useRef<'commit' | 'cancel' | null>(null)
  const commitInlineEditRef = useRef<() => Promise<boolean>>(async () => true)
  const inlineEditTargetKey = useMemo(() => {
    if (!inlineEdit) return null

    switch (inlineEdit.kind) {
      case 'optionCode':
      case 'status':
      case 'description':
        return `${inlineEdit.kind}:${inlineEdit.gIdx}:${inlineEdit.rIdx}`
      case 'groupTitle':
        return `${inlineEdit.kind}:${inlineEdit.gIdx}`
      case 'rowGroupTitle':
        return `${inlineEdit.kind}:${inlineEdit.gIdx}:${inlineEdit.sectionKey}`
      default:
        return null
    }
  }, [inlineEdit])

  useLayoutEffect(() => {
    if (!inlineEditTargetKey) return

    const input = inlineEditInputRef.current
    if (!input) return

    input.focus()
    if (input instanceof HTMLInputElement) {
      input.select()
    }
  }, [inlineEditTargetKey])

  const handleMainMerged = useCallback(() => {
    if (!id || !activeProjectId || !activeProject || isMainProject(activeProject)) return
    void loadComparisonData(id, activeProjectId)
    setChangesRequestedRefreshKey(previous => previous + 1)
  }, [activeProject, activeProjectId, id, loadComparisonData])

  // Real-time active users tracking via SignalR
  const { activeUsers, isConnected, connectionError } = useCodesheetActiveUsers({
    formatId: id,
    enabled: true,
    identityKey: demoUserEmail || 'real',
    onMainMerged: handleMainMerged,
  })

  // Filter out duplicate users (backend should already handle this, but we double-check)
  const filteredActiveUsers = useMemo(() => {
    if (!activeUsers || activeUsers.length === 0) {
      return []
    }

    const uniqueUsers = new Map<string, typeof activeUsers[0]>()
    activeUsers.forEach(user => {
      if (user && user.id && !uniqueUsers.has(user.id)) {
        uniqueUsers.set(user.id, user)
      }
    })
    
    const result = Array.from(uniqueUsers.values())
    
    if (result.length !== activeUsers.length) {
      console.log('⚠️ Filtered duplicate users:', {
        original: activeUsers.length,
        filtered: result.length,
        users: result.map(u => ({ id: u.id, name: u.name }))
      })
    }
    
    return result
  }, [activeUsers])

  // Log connection errors for debugging
  useEffect(() => {
    if (connectionError) {
      console.warn('SignalR connection error:', connectionError)
    }
  }, [connectionError])

  const getCodeItemLocalKey = useCallback((group: CodeItemGroup | null | undefined, gIdx: number): string => {
    return String(group?.localKey || group?.id || `code-item:${gIdx}:${group?.codeItemPos.code || 'unknown'}`)
  }, [])

  const updateDraftRowGroupsForCodeItem = useCallback((
    codeItemLocalKey: string,
    updater: (current: DraftRowGroupSection[]) => DraftRowGroupSection[]
  ) => {
    setDraftRowGroupsByCodeItemKey((prev) => {
      const current = prev[codeItemLocalKey] || []
      const nextDraftSections = updater(current)
      if (nextDraftSections.length === 0) {
        const { [codeItemLocalKey]: _removed, ...rest } = prev
        return rest
      }

      return {
        ...prev,
        [codeItemLocalKey]: nextDraftSections,
      }
    })
  }, [])

  const addToHistory = useCallback((currentGroups: CodeItemGroup[], currentHasChanges: boolean) => {
    setUndoStack(prev => [...prev, {
      groups: JSON.parse(JSON.stringify(currentGroups)),
      draftRowGroupsByCodeItemKey: JSON.parse(JSON.stringify(draftRowGroupsByCodeItemKey)),
      hasChanges: currentHasChanges
    }])
  }, [draftRowGroupsByCodeItemKey])

  const getDraftRowGroupsForCodeItem = useCallback((group: CodeItemGroup | null | undefined, gIdx: number) => {
    return draftRowGroupsByCodeItemKey[getCodeItemLocalKey(group, gIdx)] || []
  }, [draftRowGroupsByCodeItemKey, getCodeItemLocalKey])

  function getExistingRowGroupNames(
    group: CodeItemGroup,
    draftSections: DraftRowGroupSection[],
    options?: { excludeRowIndices?: number[]; excludeDraftId?: string | null }
  ): string[] {
    const excludeRowIndexSet = new Set(options?.excludeRowIndices || [])
    const excludedDraftId = options?.excludeDraftId || null
    const names = new Map<string, string>()

    group.rows.forEach((row, rowIndex) => {
      if (excludeRowIndexSet.has(rowIndex)) return
      const rowGroupName = normalizeRowGroupName(row.group)
      if (!rowGroupName) return

      const key = rowGroupName.toLowerCase()
      if (!names.has(key)) {
        names.set(key, rowGroupName)
      }
    })

    draftSections.forEach((draftSection) => {
      if (draftSection.id === excludedDraftId) return
      const rowGroupName = normalizeRowGroupName(draftSection.name)
      if (!rowGroupName) return

      const key = rowGroupName.toLowerCase()
      if (!names.has(key)) {
        names.set(key, rowGroupName)
      }
    })

    return Array.from(names.values())
  }

  // Condition Config Drawer state
  const [conditionDrawerOpen, setConditionDrawerOpen] = useState(false)
  const [conditionDrawerTarget, setConditionDrawerTarget] = useState<{ gIdx: number; rIdx: number } | null>(null)
  // Rules edited in this session: their validation issues block the save, older ones only warn.
  const [touchedRuleKeys, setTouchedRuleKeys] = useState<Set<string>>(() => new Set<string>())
  // Rules the advisory report has already named, so it opens once rather than on every save attempt.
  const [reportedAdvisoryRuleKeys, setReportedAdvisoryRuleKeys] = useState<Set<string>>(() => new Set<string>())

  // Comments Drawer state
  const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false)
  const [commentsDrawerTarget, setCommentsDrawerTarget] = useState<{ gIdx: number; rIdx: number } | null>(null)

  // Highlighted row state for comment highlighting
  const [highlightedRow, setHighlightedRow] = useState<{ gIdx: number; rIdx: number } | null>(null)
  const highlightedRowTimeoutRef = useRef<number | null>(null)

  // Menu state for + button
  const [addMenuAnchor, setAddMenuAnchor] = useState<AddMenuAnchorState | null>(null)
  const [codeItemActionMenu, setCodeItemActionMenu] = useState<CodeItemActionMenuState | null>(null)
  const [attributeSchemaTarget, setAttributeSchemaTarget] = useState<number | null>(null)
  const [valueAttributesTarget, setValueAttributesTarget] = useState<ValueAttributesTarget | null>(null)
  // Appendix references: the codesheet's appendices (for letters), hover state of value-group
  // headers, and the targets of the two reference modals.
  const [appendixSummaries, setAppendixSummaries] = useState<AppendixSummary[]>([])
  // The appendix editor opened from the changes drawer: read-only for "Go to", or revising a rejected change.
  const [appendixReviewEditor, setAppendixReviewEditor] = useState<{
    appendixId: string
    mode: 'readonly' | 'revision'
    change: Change
  } | null>(null)
  const [hoveredSection, setHoveredSection] = useState<string | null>(null)
  const [referenceAppendixTarget, setReferenceAppendixTarget] = useState<AppendixRefTarget | null>(null)
  const [referencedAppendicesTarget, setReferencedAppendicesTarget] = useState<AppendixRefTarget | null>(null)
  const [recentAppendixPicks, setRecentAppendixPicks] = useState<string[]>(() => getRecentAppendixPicks(id))
  const appendixSummariesByKey = useMemo(() => buildAppendixSummariesByKey(appendixSummaries), [appendixSummaries])

  // Get available references for condition config (all code item codes)
  const availableReferences = useMemo(() => {
    return groups.map(g => g.codeItemPos.code)
  }, [groups])

  // Get code items data with their options
  const codeItemsData = useMemo(() => {
    return groups.map(g => {
      const positionedSubgroups = isCompositeCodeItem(g)
        ? positionSubgroups(resolveCodeItemSubgroups(g), getCodeItemDigits(g))
        : []
      return {
        code: g.codeItemPos.code,
        options: g.rows.map(r => r.optionCode),
        rows: g.rows.map(row => ({
          optionCode: row.optionCode,
          group: row.group ?? null,
          valuePosition: row.valuePosition ?? null,
          valuePositionEnd: row.valuePositionEnd ?? null,
        })),
        composite: positionedSubgroups.length > 0,
        subgroups: deriveCompositeOptionRanges(g),
      }
    })
  }, [groups])
  // Get current code item being configured
  const currentCodeItem = useMemo(() => {
    if (!conditionDrawerTarget) return undefined
    const { gIdx } = conditionDrawerTarget
    const group = groups[gIdx]
    return group?.codeItemPos.code
  }, [conditionDrawerTarget, groups])

  const ruleValidationContext = useMemo(() => {
    if (!conditionDrawerTarget) return undefined

    return {
      groups,
      targetGroupIndex: conditionDrawerTarget.gIdx,
      targetRowIndex: conditionDrawerTarget.rIdx,
    }
  }, [conditionDrawerTarget, groups])

  // Handle opening the condition config drawer
  const handleOpenConditionDrawer = useCallback((gIdx: number, rIdx: number) => {
    if (!canMutateCodeSheet) return
    setConditionDrawerTarget({ gIdx, rIdx })
    setConditionDrawerOpen(true)
  }, [canMutateCodeSheet])

  const focusTableRow = useCallback((target: RowFocusTarget) => {
    if (highlightedRowTimeoutRef.current !== null) {
      window.clearTimeout(highlightedRowTimeoutRef.current)
    }

    setHighlightedRow(target)

    window.requestAnimationFrame(() => {
      const rowId = `${target.gIdx}:${target.rIdx}`
      const rowElement = document.querySelector(`[data-row-id="${rowId}"]`) as HTMLElement | null
      rowElement?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })

    highlightedRowTimeoutRef.current = window.setTimeout(() => {
      setHighlightedRow(null)
      highlightedRowTimeoutRef.current = null
    }, 3000)
  }, [])

  useEffect(() => {
    return () => {
      if (highlightedRowTimeoutRef.current !== null) {
        window.clearTimeout(highlightedRowTimeoutRef.current)
      }
    }
  }, [])

  // ── Ways out of an appendix disagreement that lead to the codesheet ──────────────────────

  const handleOpenAppendix = useCallback((appendixId: string) => {
    setAppendixToOpen(appendixId)
    setAppendixDrawerOpen(true)
  }, [])

  // The save response carries the fresh comparison; the reload brings the rest of the codesheet.
  const handleAppendixSavedFromDrawer = (appendix: AppendixSummary) => {
    if (appendix.appendixConsistency) setAppendixConsistencyReport(appendix.appendixConsistency)
    void loadData()
  }

  const openRuleOfValue = useCallback((
    formatValueId: string | null | undefined,
    codeItemCode: string,
    optionCode: string,
    valuePosition: number | null | undefined,
    proposal: ProposedRule | null
  ) => {
    const target = findUnselectableRow(indexRowsForUnselectableLookup(groups), formatValueId, codeItemCode, optionCode, valuePosition)
    if (!target) {
      void showErrorModal({
        title: 'Value not found',
        description: `Code ${codeItemCode} value ${optionCode} is no longer on the codesheet. Save to recheck the appendix.`,
        confirmText: 'OK',
      })
      return
    }

    focusTableRow({ gIdx: target.gIdx, rIdx: target.rIdx })
    setProposedRule(proposal)
    handleOpenConditionDrawer(target.gIdx, target.rIdx)
  }, [focusTableRow, groups, handleOpenConditionDrawer])

  const handleEditRuleFromAppendix = useCallback((line: UnselectableRuleLine) => {
    openRuleOfValue(line.formatValueId, line.codeItemCode, line.optionCode, null, null)
  }, [openRuleOfValue])

  const handleProposeRuleFromAppendix = useCallback((proposal: ProposedRule) => {
    openRuleOfValue(proposal.formatValueId, proposal.codeItemCode, proposal.optionCode, proposal.valuePosition, proposal)
  }, [openRuleOfValue])

  const handleGoToCodeItemFromAppendix = useCallback((code: string) => {
    const wanted = normalizeCodeItemReference(code)
    const gIdx = groups.findIndex(group => normalizeCodeItemReference(group.codeItemPos.code) === wanted)
    if (gIdx >= 0 && groups[gIdx].rows.length > 0) focusTableRow({ gIdx, rIdx: 0 })
  }, [focusTableRow, groups])

  const showReferencedValueBlockedModal = useCallback(async (
    title: string,
    description: string,
    items: ReferenceBlockedModalItem[],
    navigationTarget: RowFocusTarget | null,
    navigationText = 'Go to referenced value'
  ): Promise<boolean> => {
    let navigated = false
    const summaryItems = items.length > 0 ? items : [{ id: 'summary', description, navigationTarget }]
    const expandLabel = `View all references (${summaryItems.length})`
    const showNavigationButton = Boolean(
      navigationTarget && (
        navigationText !== 'Go to referenced value' ||
        summaryItems.length === 1
      )
    )
    const result = await configuredSwal.fire({
      title,
      html: buildExpandableModalDescriptionHtml(description, summaryItems.map((item) => ({
        id: item.id,
        description: item.description,
      })), expandLabel),
      showCloseButton: true,
      showCancelButton: showNavigationButton,
      reverseButtons: true,
      confirmButtonText: 'OK',
      cancelButtonText: navigationText,
      width: 480,
      didOpen: () => {
        const toggleButton = document.querySelector('[data-reference-expand-toggle="true"]') as HTMLButtonElement | null
        const expandPanel = document.querySelector('[data-reference-expand-panel="true"]') as HTMLDivElement | null

        if (toggleButton && expandPanel) {
          toggleButton.addEventListener('click', () => {
            const isExpanded = expandPanel.style.display !== 'none'
            expandPanel.style.display = isExpanded ? 'none' : 'block'
            toggleButton.textContent = isExpanded
              ? toggleButton.dataset.expandedLabel || ''
              : toggleButton.dataset.collapsedLabel || ''
          })
        }

        const navigationButtons = document.querySelectorAll<HTMLButtonElement>('[data-reference-navigation-index]')
        navigationButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const index = Number(button.dataset.referenceNavigationIndex)
            const item = summaryItems[index]
            if (!item?.navigationTarget) return

            navigated = true
            Swal.close()
            focusTableRow(item.navigationTarget)
          })
        })
      },
    })

    if (result.dismiss === Swal.DismissReason.cancel && navigationTarget) {
      navigated = true
      focusTableRow(navigationTarget)
    }

    return navigated
  }, [focusTableRow])

  // Handle comment click to highlight the corresponding row
  const handleCommentClick = useCallback((block: string, section: string) => {
    // Find the group index that matches the block (code)
    const gIdx = groups.findIndex(g => g.codeItemPos.code === block)
    if (gIdx === -1) {
      console.warn(`No group found with block: ${block}`)
      return
    }

    // Find the row index that matches the section (optionCode)
    const rIdx = groups[gIdx].rows.findIndex(r => r.optionCode === section)
    if (rIdx === -1) {
      console.warn(`No row found with section: ${section} in block: ${block}`)
      return
    }

    focusTableRow({ gIdx, rIdx })
  }, [focusTableRow, groups])

  // Get initial config for the condition drawer (memoized to avoid new references on every render)
  const conditionDrawerInitialConfig = useMemo((): RuleConfig | null => {
    if (!conditionDrawerTarget) return null
    const { gIdx, rIdx } = conditionDrawerTarget
    const group = groups[gIdx]
    if (!group) return null
    const row = group.rows[rIdx]
    if (!row) return null

    // A rule drafted from an appendix: shown for review and editing; nothing changes until Apply.
    // The value's existing description is kept, as the server did when it checked the draft.
    if (proposedRule) {
      const description = row.rule?.description || null
      return {
        type: 'if',
        rule: {
          type: 'if',
          id: row.rule?.id ?? null,
          description,
          conditionGroup: proposedRule.conditionGroup,
          consequenceGroups: null,
        },
        description,
      }
    }

    const rule = row.rule
    if (!rule) return { type: 'always' }

    if (rule.type === 'while') {
      return {
        type: 'while',
        rule: rule,
        description: rule.description || null,
      }
    }

    if (rule.type === 'if') {
      return {
        type: 'if',
        rule: rule,
        description: rule.description || null,
      }
    }

    if (rule.type === 'text') {
      return {
        type: 'text',
        rule: rule,
        description: rule.description || null,
        freeText: rule.freeText || rule.description || null
      }
    }

    return { type: 'always' }
  }, [conditionDrawerTarget, groups, proposedRule])
  // Handle applying the condition config
  const handleApplyConditionConfig = useCallback(async (config: RuleConfig) => {
    if (!canMutateCodeSheet) return
    if (!conditionDrawerTarget) return

    const { gIdx, rIdx } = conditionDrawerTarget
    const group = groups[gIdx]
    if (!group) return

    const touchedRow = group.rows[rIdx]
    if (touchedRow) {
      const touchedKey = getRuleKey(group, touchedRow)
      setTouchedRuleKeys(previous => new Set(previous).add(touchedKey))
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Availability rule updated', ...TEMPORARY_SUCCESS_TOAST_PROPS })

    applyGroupsUpdate(prev => {
      const next = [...prev]
      const nextGroup = { ...next[gIdx] }
      const rows = nextGroup.rows.map((existingRow, existingRowIdx) => {
        const nextRow = { ...existingRow }

        if (existingRowIdx !== rIdx) {
          return nextRow
        }

        if (config.type === 'always') {
          nextRow.rule = undefined
          return nextRow
        }

        // Keep persisted metadata until the backend migration removes legacy links.
        const legacyAppendix = existingRow.rule && 'appendix' in existingRow.rule
          ? { appendix: existingRow.rule.appendix }
          : {}

        if (config.type === 'text') {
          nextRow.rule = {
            type: 'text',
            description: config.description || config.freeText || null,
            ...legacyAppendix,
            freeText: config.freeText || null
          }
          return nextRow
        }

        if (config.type === 'while' && config.rule) {
          const whileRule = config.rule as LoopNode
          const description = config.description || `${whileRule.reference} ${whileRule.condition} "${whileRule.value}"`
          nextRow.rule = {
            ...whileRule,
            description,
            ...legacyAppendix,
            freeText: config.freeText || null
          }
          return nextRow
        }

        if (config.type === 'if' && config.rule) {
          const ifRule = config.rule as IfNode
          const description = config.description || ifRule.description || 'IF rule'
          nextRow.rule = {
            ...ifRule,
            description,
            ...legacyAppendix,
            freeText: config.freeText || null
          }
        }

        return nextRow
      })

      nextGroup.rows = rows
      next[gIdx] = nextGroup
      return next
    })

    setHasChanges(true)
    setPublished(false)
    setInlineEdit(current => (
      current && current.draftValue === current.originalValue ? null : current
    ))
    setAvailabilityConfigRow(null)
    setConditionDrawerOpen(false)
    setConditionDrawerTarget(null)
  }, [canMutateCodeSheet, conditionDrawerTarget, groups, hasChanges, addToHistory, applyGroupsUpdate])

  const handleUndo = () => {
    setUndoToast(null)
    setInlineEdit(null)
    setNewRowOptionValidation(null)
    setNewRowDescriptionValidation(null)
    setAvailabilityConfigRow(null)
    setAddMenuAnchor(null)
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setUndoStack(prev => {
      if (prev.length === 0) return prev
      const newStack = [...prev]
      const lastState = newStack.pop()
      if (lastState) {
        applyGroupsUpdate(lastState.groups)
        setDraftRowGroupsByCodeItemKey(lastState.draftRowGroupsByCodeItemKey)
        setHasChanges(lastState.hasChanges)
      }
      return newStack
    })
  }

  const getReferencedValueActionGuard = useCallback((
    targets: Array<{ gIdx: number; rIdx: number; codeItemCode: string; optionCode: string }>,
    action: 'remove' | 'change'
  ) => {
    for (const target of targets) {
      const normalizedOptionCode = String(target.optionCode || '').trim()
      if (!normalizedOptionCode) continue

      const usages = findRuleValueUsages(groups, target.codeItemCode, normalizedOptionCode)
      if (usages.length === 0) continue

      const firstUsage = usages[0]
      return {
        message: buildReferencedValuePrimaryErrorMessage(action, normalizedOptionCode, firstUsage),
        items: usages.map((usage, usageIndex) => ({
          id: `${target.gIdx}:${target.rIdx}:${usageIndex}`,
          description: buildRuleReferenceUsageDescription(usage),
          navigationTarget: usage.sourceGroupIndex === undefined || usage.sourceRowIndex === undefined
            ? null
            : {
                gIdx: usage.sourceGroupIndex,
                rIdx: usage.sourceRowIndex,
              },
        })),
        navigationTarget: firstUsage.sourceGroupIndex === undefined || firstUsage.sourceRowIndex === undefined
          ? null
          : {
              gIdx: firstUsage.sourceGroupIndex,
              rIdx: firstUsage.sourceRowIndex,
            },
      }
    }

    return null
  }, [groups])

  async function promptDescriptionMergeValue(
    gIdx: number,
    selectedIndices: number[]
  ): Promise<{ confirmed: boolean; value: string | null }> {
    const group = groups[gIdx]
    if (!group) return { confirmed: false, value: null }
    const sorted = [...selectedIndices].sort((a, b) => a - b)
    const entries = sorted
      .map((rowIdx) => {
        const row = group.rows[rowIdx]
        if (!row) return null
        const key = row.rowId || `${gIdx}-${rowIdx}`
        const rawOptionCode = row.optionCode ?? ''
        const label = rawOptionCode.trim().length > 0 ? rawOptionCode.trim() : `Row ${rowIdx + 1}`
        return {
          key,
          label,
          description: row.description ?? '',
        }
      })
      .filter((entry): entry is { key: string; label: string; description: string } => Boolean(entry))

    if (entries.length === 0) return { confirmed: false, value: null }

    let defaultKey = entries[0].key
    const firstWithContent = entries.find(entry => entry.description.trim().length > 0)
    if (firstWithContent) defaultKey = firstWithContent.key

    const containerId = `desc-merge-${Date.now()}`
    const optionsHtml = entries
      .map((entry, index) => {
        const borderStyle = index === entries.length - 1 ? 'none' : '1px solid #E2E8F0'
        return `
          <div class="desc-merge-item" style="display:flex; gap:12px; padding:12px 0; border-bottom:${borderStyle};">
            <div style="display:flex; flex-direction:column; gap:6px; min-width:140px;">
              <label style="display:flex; align-items:center; gap:8px; font-weight:600; color:#1D5176; cursor:pointer;">
                <input type="radio" name="desc-merge-option" value="${escapeHtml(entry.key)}" ${entry.key === defaultKey ? 'checked' : ''} />
                <span>${escapeHtml(entry.label)}</span>
              </label>
            </div>
            <textarea style="flex:1; border:1px solid #CBD5E1; border-radius:4px; padding:6px; font-size:13px; line-height:1.45; resize:vertical; min-height:96px; max-height:220px; overflow-y:auto;" placeholder="Enter description...">${escapeHtmlForTextarea(entry.description)}</textarea>
          </div>
        `
      })
      .join('')

    const html = `
      <div style="text-align:left; font-size:13px; color:#475569;">
        <p style="margin-bottom:8px;">Choose which description should remain after merging. You can review and edit each option before applying.</p>
        <div id="${containerId}" style="max-height:260px; overflow-y:auto; padding-right:4px;">
          ${optionsHtml}
        </div>
      </div>
    `

    const result = await configuredSwal.fire<{ key: string; value: string | null }>({
      title: 'Merge descriptions',
      html,
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: 'Cancel',
      focusConfirm: false,
      width: 540,
      preConfirm: () => {
        const container = document.getElementById(containerId)
        if (!container) {
          return { key: defaultKey, value: entries[0]?.description ?? '' }
        }
        const selected = container.querySelector<HTMLInputElement>('input[name="desc-merge-option"]:checked')
        if (!selected) {
          configuredSwal.showValidationMessage('Select a description option')
          return false as unknown as { key: string; value: string | null }
        }
        const item = selected.closest('.desc-merge-item') as HTMLElement | null
        const textarea = item?.querySelector<HTMLTextAreaElement>('textarea')
        const rawValue = textarea?.value ?? ''
        if (getDescriptionValidationError(rawValue)) {
          configuredSwal.showValidationMessage('Description is required')
          return false as unknown as { key: string; value: string | null }
        }
        return { key: selected.value, value: rawValue }
      },
      didOpen: () => {
        const container = document.getElementById(containerId)
        if (container) container.scrollTop = 0
      },
    })

    if (!result.isConfirmed || !result.value) return { confirmed: false, value: null }

    const finalText = result.value.value ?? ''
    const normalized = finalText.trim().length > 0 ? finalText : null
    return { confirmed: true, value: normalized }
  }

  async function promptAvailabilityMergeValue(
    gIdx: number,
    selectedIndices: number[]
  ): Promise<{ confirmed: boolean; value: RuleNode | null | undefined }> {
    const group = groups[gIdx]
    if (!group) return { confirmed: false, value: undefined }
    const sorted = [...selectedIndices].sort((a, b) => a - b)
    const entries = sorted
      .map((rowIdx) => {
        const row = group.rows[rowIdx]
        if (!row) return null
        const key = row.rowId || `${gIdx}-${rowIdx}`
        const rawOptionCode = row.optionCode ?? ''
        const label = rawOptionCode.trim().length > 0 ? rawOptionCode.trim() : `Row ${rowIdx + 1}`
        return {
          key,
          label,
          rule: row.rule,
        }
      })
      .filter((entry): entry is { key: string; label: string; rule: RuleNode | null | undefined } => Boolean(entry))

    if (entries.length === 0) return { confirmed: false, value: undefined }

    let defaultKey = entries[0].key
    const firstWithContent = entries.find(entry => entry.rule)
    if (firstWithContent) defaultKey = firstWithContent.key

    const containerId = `avail-merge-${Date.now()}`
    const optionsHtml = entries
      .map((entry, index) => {
        const borderStyle = index === entries.length - 1 ? 'none' : '1px solid #E2E8F0'
        const ruleText = entry.rule?.description || 'Always available'
        return `
          <div class="avail-merge-item" style="display:flex; gap:12px; padding:12px 0; border-bottom:${borderStyle};">
            <div style="display:flex; flex-direction:column; gap:6px; min-width:140px;">
              <label style="display:flex; align-items:center; gap:8px; font-weight:600; color:#1D5176; cursor:pointer;">
                <input type="radio" name="avail-merge-option" value="${escapeHtml(entry.key)}" ${entry.key === defaultKey ? 'checked' : ''} />
                <span>${escapeHtml(entry.label)}</span>
              </label>
            </div>
            <div style="flex:1; border:1px solid #CBD5E1; border-radius:4px; padding:8px; font-size:13px; line-height:1.45; background-color:#F8FAFC;">
              ${escapeHtml(ruleText)}
            </div>
          </div>
        `
      })
      .join('')

    const html = `
      <div style="text-align:left; font-size:13px; color:#475569;">
        <p style="margin-bottom:8px;">Choose which availability rule should remain after merging.</p>
        <div id="${containerId}" style="max-height:260px; overflow-y:auto; padding-right:4px;">
          ${optionsHtml}
        </div>
      </div>
    `

    const result = await configuredSwal.fire<{ key: string }>({
      title: 'Merge availability rules',
      html,
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: 'Cancel',
      focusConfirm: false,
      width: 540,
      preConfirm: () => {
        const container = document.getElementById(containerId)
        if (!container) {
          return { key: defaultKey }
        }
        const selected = container.querySelector<HTMLInputElement>('input[name="avail-merge-option"]:checked')
        if (!selected) {
          configuredSwal.showValidationMessage('Select an availability rule option')
          return false as unknown as { key: string }
        }
        return { key: selected.value }
      },
      didOpen: () => {
        const container = document.getElementById(containerId)
        if (container) container.scrollTop = 0
      },
    })

    if (!result.isConfirmed || !result.value) return { confirmed: false, value: undefined }

    const selectedEntry = entries.find(e => e.key === result.value?.key)
    return { confirmed: true, value: selectedEntry?.rule }
  }

  function normalizeGroupTitle(value: string): string | null {
    const trimmed = value.trim()
    return trimmed ? trimmed : null
  }

  const getCodeItemDraftRowGroups = useCallback((gIdx: number): DraftRowGroupSection[] => {
    return getDraftRowGroupsForCodeItem(groups[gIdx], gIdx)
  }, [getDraftRowGroupsForCodeItem, groups])

  function insertDraftRowGroupSection(gIdx: number, initialName: string) {
    const group = groups[gIdx]
    if (!group) return

    const codeItemLocalKey = getCodeItemLocalKey(group, gIdx)
    const nextName = normalizeRowGroupName(initialName)
    if (!nextName) return

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Group created successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    updateDraftRowGroupsForCodeItem(codeItemLocalKey, (current) => [...current, createDraftRowGroupSection(nextName)])
    setHasChanges(true)
  }

  async function promptNewRowGroupName(gIdx: number): Promise<string | null> {
    const group = groups[gIdx]
    if (!group) return null

    const existingNames = new Set(
      getExistingRowGroupNames(group, getCodeItemDraftRowGroups(gIdx)).map((name) => name.toLowerCase())
    )

    const result = await configuredSwal.fire<string>({
      title: 'Add New Group',
      input: 'text',
      inputLabel: 'Group name',
      inputPlaceholder: 'Enter a group name',
      showCancelButton: true,
      confirmButtonText: 'Create',
      cancelButtonText: 'Cancel',
      focusConfirm: false,
      preConfirm: (value) => {
        const normalizedValue = normalizeRowGroupName(value)
        if (!normalizedValue) {
          configuredSwal.showValidationMessage('Enter a group name')
          return false as unknown as string
        }

        if (existingNames.has(normalizedValue.toLowerCase())) {
          configuredSwal.showValidationMessage(`The group "${normalizedValue}" already exists in this Code Item.`)
          return false as unknown as string
        }

        return normalizedValue
      },
    })

    if (!result.isConfirmed) return null
    return normalizeRowGroupName(result.value)
  }

  async function handleCreateRowGroup(gIdx: number) {
    if (!canMutateCodeSheet) return
    const group = groups[gIdx]
    if (group && isCompositeCodeItem(group)) {
      setCodeItemEditorTarget({ mode: 'edit', gIdx, autoFocusCompositeAdd: true })
      return
    }
    const groupName = await promptNewRowGroupName(gIdx)
    if (!groupName) return
    insertDraftRowGroupSection(gIdx, groupName)
  }

  function removeDraftRowGroupSection(gIdx: number, draftId: string) {
    const group = groups[gIdx]
    if (!group) return

    const codeItemLocalKey = getCodeItemLocalKey(group, gIdx)
    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Group removed' })
    updateDraftRowGroupsForCodeItem(codeItemLocalKey, (current) => current.filter((section) => section.id !== draftId))
    setHasChanges(true)
  }

  function getGroupDigits(group: CodeItemGroup | null | undefined): number {
    if (!group) return 1
    const [start, end] = group.codeItemPos.posRange
    return Math.max(1, end - start + 1)
  }

  function getOptionCodePlaceholder(digits: number): string {
    return `Code (${digits} digit${digits === 1 ? '' : 's'})`
  }

  function getDuplicateOptionCodeError(codeItemCode: string, optionCode: string): string {
    return `Duplicate in ${codeItemCode}: "${optionCode}".`
  }

  function resizeGroupDigits(currentGroups: CodeItemGroup[], gIdx: number, digits: number): CodeItemGroup[] {
    const targetGroup = currentGroups[gIdx]
    if (!targetGroup) return currentGroups

    const normalizedDigits = Math.max(1, Math.floor(digits) || 1)
    const currentDigits = getGroupDigits(targetGroup)
    if (normalizedDigits === currentDigits) return currentGroups

    const delta = normalizedDigits - currentDigits

    return currentGroups.map((group, index) => {
      const [start, end] = group.codeItemPos.posRange

      if (index < gIdx) {
        return group
      }

      if (index === gIdx) {
        const nextEnd = start + normalizedDigits - 1
        return {
          ...group,
          codeItemPos: {
            ...group.codeItemPos,
            posRange: [start, nextEnd],
            label: `${group.codeItemPos.code} [${start}-${nextEnd}]`,
          },
        }
      }

      const nextStart = start + delta
      const nextEnd = end + delta

      return {
        ...group,
        codeItemPos: {
          ...group.codeItemPos,
          posRange: [nextStart, nextEnd],
          label: `${group.codeItemPos.code} [${nextStart}-${nextEnd}]`,
        },
      }
    })
  }

  function hasDuplicateOptionCodeInGroup(group: CodeItemGroup, rowIdx: number, optionCode: string): boolean {
    const normalizedOptionCode = finalizeOptionCode(optionCode, getGroupDigits(group))
    if (!normalizedOptionCode) return false

    return group.rows.some((row, index) =>
      index !== rowIdx && finalizeOptionCode(row.optionCode, getGroupDigits(group)) === normalizedOptionCode
    )
  }

  function showReferencedCodeItemDeleteError(code: string) {
    void showErrorModal({
      title: 'Unable to Delete Code Item',
      description: `Code Item ${code} is used in Availability rules. Update those rules before deleting this Code Item.`,
      confirmText: 'OK'
    })
  }

  function getGroupsAfterRemovingGroup(currentGroups: CodeItemGroup[], gIdx: number): CodeItemGroup[] | null {
    const targetGroup = currentGroups[gIdx]
    if (!targetGroup) return null

    const targetCode = targetGroup.codeItemPos.code
    if (isCodeItemReferenced(currentGroups, targetCode)) {
      showReferencedCodeItemDeleteError(targetCode)
      return null
    }

    const next = removeGroupByIndex(currentGroups, gIdx)
    if (next === currentGroups) return null

    return reindexCodeItemGroups(next)
  }

  function createCodeItemFromEditor(insertAfter: number | null, value: CodeItemEditorValue) {
    if (!canMutateCodeSheet) return
    if (hasPendingRowChanges) return
    const d = Math.max(1, Math.floor(value.digits) || 1)
    const groupTitle = normalizeGroupTitle(value.title)
    if (!groupTitle) return
    const composite = value.authoringMode === 'composite'
    if (composite && (
      getCompositeGroupStructureErrors(value.subgroups, d).length > 0
    )) return
    const initialDraftRowGroups = composite ? [] : value.simpleGroupNames.map(name => createDraftRowGroupSection(name))
    const localKey = `local-code-item-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`

    cancelAllNewRows()
    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Code Item created successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    applyGroupsUpdate(prev => {
      const insertionIndex = insertAfter === null ? 0 : Math.min(prev.length, insertAfter + 1)
      if (insertAfter === null && prev.length > 0) return prev
      const previousEnd = insertionIndex === 0 ? 0 : prev[insertionIndex - 1].codeItemPos.posRange[1]
      const newStart = previousEnd + 1
      const newEnd = newStart + d - 1
      const code = String(insertionIndex + 1).padStart(2, '0')
      const newGroup: CodeItemGroup = {
        id: null,
        localKey,
        codeItemPos: { code, posRange: [newStart, newEnd], label: `${code} [${newStart}-${newEnd}]`, isDefault: false },
        title: groupTitle,
        authoringMode: composite ? 'composite' : 'simple',
        subgroups: composite ? normalizeCompositeSubgroups(value.subgroups, d) : [],
        rows: composite || initialDraftRowGroups.length > 0 ? [] : [createNewCodeRow(code, 0)]
      }
      const next = [...prev]
      next.splice(insertionIndex, 0, newGroup)
      let runningEnd = 0
      return reindexCodeItemGroups(next).map((item) => {
        const length = getGroupDigits(item)
        const start = runningEnd + 1
        const end = start + length - 1
        runningEnd = end
        return { ...item, codeItemPos: { ...item.codeItemPos, posRange: [start, end], label: `${item.codeItemPos.code} [${start}-${end}]` } }
      })
    })
    if (initialDraftRowGroups.length > 0) {
      setDraftRowGroupsByCodeItemKey(prev => ({ ...prev, [localKey]: initialDraftRowGroups }))
    }
    setCodeItemEditorTarget(null)
    setHasChanges(true)
  }

  function isContiguous(indices: number[]): boolean {
    if (indices.length < 2) return false
    for (let i = 0; i < indices.length - 1; i++) if (indices[i + 1] !== indices[i] + 1) return false
    return true
  }

  const sel = useMemo(() => {
    const ids = Object.keys(cellSelection).filter(k => cellSelection[k])
    if (ids.length === 0) return { groupIdx: null, indices: [], column: null as ColumnKey | null }

    const parsed = ids.map(id => {
      const [gStr, rStr, col] = id.split(':')
      return { gIdx: Number(gStr), rIdx: Number(rStr), col: col as ColumnKey }
    })
    const gSet = new Set(parsed.map(p => p.gIdx))
    const cSet = new Set(parsed.map(p => p.col))

    if (gSet.size !== 1 || cSet.size !== 1) {
      return { groupIdx: null, indices: [], column: null as ColumnKey | null }
    }

    return {
      groupIdx: parsed[0].gIdx,
      indices: parsed.map(p => p.rIdx).sort((a, b) => a - b),
      column: parsed[0].col,
    }
  }, [cellSelection])
  const selectionValid = sel.groupIdx !== null && sel.indices.length > 0 && sel.column !== null
  const selectionSize = sel.indices.length
  const selectionWithinSameRowGroupSection = selectionValid && sel.groupIdx !== null
    ? areRowIndicesInSameGroupSection(groups[sel.groupIdx].rows, sel.indices)
    : false

  const canMergeDescription = sel.column === 'description' && selectionSize >= 2 && isContiguous(sel.indices) && selectionWithinSameRowGroupSection
  const canSplitDescription = selectionValid && sel.groupIdx !== null && sel.column === 'description'
    ? selectionWithinSameRowGroupSection && hasMergedSpanByGroupIds(groups[sel.groupIdx].rows, 'description', sel.indices[0] ?? 0, sel.indices[sel.indices.length - 1] ?? 0)
    : false

  const canMergeAvailability = sel.column === 'availability' && selectionSize >= 2 && isContiguous(sel.indices) && selectionWithinSameRowGroupSection
  const canSplitAvailability = selectionValid && sel.groupIdx !== null && sel.column === 'availability'
    ? selectionWithinSameRowGroupSection && hasMergedSpanByGroupIds(groups[sel.groupIdx].rows, 'availability', sel.indices[0] ?? 0, sel.indices[sel.indices.length - 1] ?? 0)
    : false

  const canSplit = canModifyCodeSheetStructure && ((sel.column === 'description' && canSplitDescription) || (sel.column === 'availability' && canSplitAvailability))
  const canMerge = canModifyCodeSheetStructure && ((sel.column === 'description' && canMergeDescription) || (sel.column === 'availability' && canMergeAvailability))

  const actMerge = async (column: 'description' | 'availability') => {
    if (!canMutateCodeSheet) return
    if (!selectionValid || sel.groupIdx === null || sel.indices.length < 2 || !selectionWithinSameRowGroupSection) return
    const gIdx = sel.groupIdx
    const start = sel.indices[0]
    const end = sel.indices[sel.indices.length - 1]

    if (column === 'description') {
      const promptResult = await promptDescriptionMergeValue(gIdx, sel.indices)
      if (!promptResult.confirmed) return
      const chosenDescription = promptResult.value ?? null

      addToHistory(groups, hasChanges)
      setUndoToast({ message: `${end - start + 1} cells merged successfully` })

      applyGroupsUpdate(prev => {
        const next = [...prev]
        const g = { ...next[gIdx] }
        const rows = [...g.rows]
        const affectedBlockIds = new Set<string>()
        for (let idx = start; idx <= end; idx++) {
          affectedBlockIds.add(getBlockId(rows[idx], column))
        }
        for (let i = 0; i < rows.length; i++) {
          if (i >= start && i <= end) continue
          const blockId = getBlockId(rows[i], column)
          if (!affectedBlockIds.has(blockId)) continue
          rows[i] = { ...rows[i], descriptionBlockId: makeBlockId(rows[i], 'description', i) }
        }
        const newBlockId = makeBlockId(rows[start], column, start)
        for (let i = start; i <= end; i++) {
          rows[i] = {
            ...rows[i],
            descriptionBlockId: newBlockId,
            description: chosenDescription ?? null,
          }
        }
        g.rows = normalizeRowsForRowGroups(rows)
        next[gIdx] = g
        console.log(next)
        return next
      })
      setHasChanges(true)
    } else if (column === 'availability') {
      const promptResult = await promptAvailabilityMergeValue(gIdx, sel.indices)
      if (!promptResult.confirmed) return
      const chosenRule = promptResult.value

      addToHistory(groups, hasChanges)
      setUndoToast({ message: `${end - start + 1} availability rules merged successfully` })

      applyGroupsUpdate(prev => {
        const next = [...prev]
        const g = { ...next[gIdx] }
        const rows = [...g.rows]
        const affectedBlockIds = new Set<string>()
        for (let idx = start; idx <= end; idx++) {
          affectedBlockIds.add(getBlockId(rows[idx], column))
        }
        for (let i = 0; i < rows.length; i++) {
          if (i >= start && i <= end) continue
          const blockId = getBlockId(rows[i], column)
          if (!affectedBlockIds.has(blockId)) continue
          rows[i] = { ...rows[i], availabilityBlockId: makeBlockId(rows[i], 'availability', i) }
        }
        const newBlockId = makeBlockId(rows[start], column, start)
        for (let i = start; i <= end; i++) {
          rows[i] = {
            ...rows[i],
            availabilityBlockId: newBlockId,
            rule: chosenRule,
          }
        }
        g.rows = normalizeRowsForRowGroups(rows)
        next[gIdx] = g
        console.log(next)
        return next
      })
      setHasChanges(true)
    }
  }

  const actSplit = (column: 'description' | 'availability') => {
    if (!canMutateCodeSheet) return
    if (!selectionValid || sel.groupIdx === null || !selectionWithinSameRowGroupSection) return

    addToHistory(groups, hasChanges)
    setUndoToast({ message: column === 'description' ? 'Cells split successfully' : 'Availability rules split successfully' })

    const gIdx = sel.groupIdx
    applyGroupsUpdate(prev => {
      const next = [...prev]
      const g = { ...next[gIdx] }
      const rows = [...g.rows]
      const rowCount = rows.length
      const groupIdSet = new Set<string>()
      for (const idx of sel.indices) {
        const gid = getBlockId(rows[idx], column)
        groupIdSet.add(gid)
      }
      for (const gid of Array.from(groupIdSet)) {
        let leaderIndex = rows.findIndex(r => getBlockId(r, column) === gid)
        if (leaderIndex === -1) continue
        let endIdx = leaderIndex
        for (let j = leaderIndex + 1; j < rowCount; j++) {
          if (getBlockId(rows[j], column) !== gid) break
          endIdx = j
        }
        const leaderValue = getColumnValue(rows[leaderIndex], column)
        for (let j = leaderIndex; j <= endIdx; j++) {
          if (column === 'description') {
            rows[j] = { ...rows[j], descriptionBlockId: makeBlockId(rows[j], 'description', j), description: leaderValue as any }
          } else if (column === 'availability') {
            rows[j] = { ...rows[j], availabilityBlockId: makeBlockId(rows[j], 'availability', j), rule: leaderValue as any }
          }
        }
      }
      g.rows = normalizeRowsForRowGroups(rows)
      next[gIdx] = g
      console.log(next)
      return next
    })
    setHasChanges(true)
  }

  const hasCellSelection = Object.values(cellSelection).some(Boolean)
  const rowDeleteTarget =
    insertTarget.gIdx !== null && insertTarget.rIdx !== null
      ? { gIdx: insertTarget.gIdx, rIdx: insertTarget.rIdx }
      : null
  const groupDeleteTarget = groupInsertTarget !== null ? groupInsertTarget : null
  const rowGroupDeleteTarget =
    rowGroupInsertTarget && rowGroupInsertTarget.sectionKey !== TITLE_ROW_INSERT_KEY
      ? rowGroupInsertTarget
      : null
  const canDeleteRow = !isPreviewMode && rowDeleteTarget !== null && !hasCellSelection
  const canDeleteRowGroup =
    !isPreviewMode &&
    rowGroupDeleteTarget !== null &&
    rowDeleteTarget === null &&
    groupDeleteTarget === null &&
    !hasCellSelection
  const canDeleteGroup =
    !isPreviewMode &&
    groupDeleteTarget !== null &&
    rowDeleteTarget === null &&
    rowGroupDeleteTarget === null &&
    !hasCellSelection
  const deleteDisabled = !canModifyCodeSheetStructure || !deleteSelectionAccess.allowed || (!canDeleteRow && !canDeleteGroup && !canDeleteRowGroup)
  const deleteTooltip = pageMutationDisabledReason || deleteSelectionAccess.message || (
    canDeleteRow ? 'Delete selected option' :
      canDeleteRowGroup ? 'Delete selected group' :
        canDeleteGroup ? 'Delete selected code item' :
          'Select a Code Item, Group, or Option to enable delete'
  )

  function deleteGroupAtIndex(gIdx: number) {
    const prev = groups
    const targetGroup = prev[gIdx]
    if (targetGroup) {
      const referencedValueGuard = getReferencedValueActionGuard(
        targetGroup.rows.map((row, rIdx) => ({
          gIdx,
          rIdx,
          codeItemCode: targetGroup.codeItemPos.code,
          optionCode: String(row.optionCode ?? ''),
        })),
        'remove'
      )

      if (referencedValueGuard) {
        void showReferencedValueBlockedModal(
          'Unable to Delete Code Item',
          referencedValueGuard.message,
          referencedValueGuard.items,
          referencedValueGuard.navigationTarget
        )
        return
      }
    }

    const next = getGroupsAfterRemovingGroup(prev, gIdx)
    if (!next) return

    addToHistory(prev, hasChanges)
    setUndoToast({ message: 'Role deleted successfully' })
    applyGroupsUpdate(next)
    if (targetGroup) {
      const codeItemLocalKey = getCodeItemLocalKey(targetGroup, gIdx)
      setDraftRowGroupsByCodeItemKey((prevDraftSections) => {
        const { [codeItemLocalKey]: _removed, ...rest } = prevDraftSections
        return rest
      })
    }

    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setInsertTarget({ gIdx: null, rIdx: null })
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setHasChanges(true)
  }

  function deleteRowAtIndex(gIdx: number, rIdx: number) {
    const prev = groups
    const group = prev[gIdx]
    if (!group) return
    if (rIdx < 0 || rIdx >= group.rows.length) return

    const referencedValueGuard = getReferencedValueActionGuard([
      {
        gIdx,
        rIdx,
        codeItemCode: group.codeItemPos.code,
        optionCode: String(group.rows[rIdx]?.optionCode ?? ''),
      },
    ], 'remove')

    if (referencedValueGuard) {
      void showReferencedValueBlockedModal(
        'Unable to Delete Row',
        referencedValueGuard.message,
        referencedValueGuard.items,
        referencedValueGuard.navigationTarget
      )
      return
    }

    const descValues = collectBlockValues<string>(group.rows, 'description')
    const availValues = collectBlockValues<any>(group.rows, 'availability')
    const remainingRows = [...group.rows]
    remainingRows.splice(rIdx, 1)
    const draftSections = getCodeItemDraftRowGroups(gIdx)

    let next: CodeItemGroup[] = []

    if (remainingRows.length === 0 && draftSections.length === 0) {
      const withoutGroup = getGroupsAfterRemovingGroup(prev, gIdx)
      if (!withoutGroup) return
      next = withoutGroup
    } else {
      let updatedRows = normalizeBlocks(remainingRows, 'description', descValues)
      updatedRows = normalizeBlocks(updatedRows, 'availability', availValues)
      updatedRows = normalizeRowsForRowGroups(updatedRows)
      next = [...prev]
      next[gIdx] = { ...group, rows: updatedRows }
    }

    addToHistory(prev, hasChanges)
    setUndoToast({ message: 'Row deleted successfully' })
    applyGroupsUpdate(next)

    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setHasChanges(true)
  }

  function deleteRowGroupAtIndex(gIdx: number, sectionKey: string) {
    const targetGroup = groups[gIdx]
    if (!targetGroup) return

    const draftSection = getCodeItemDraftRowGroups(gIdx).find((section) => `draft:${section.id}` === sectionKey)
    if (draftSection) {
      setRowGroupInsertTarget(null)
      removeDraftRowGroupSection(gIdx, draftSection.id)
      return
    }

    const persistedSection = buildRowGroupSections(targetGroup.rows).find((section) => section.key === sectionKey)
    if (!persistedSection) return

    const referencedValueGuard = getReferencedValueActionGuard(
      persistedSection.rowIndices.map((rowIndex) => ({
        gIdx,
        rIdx: rowIndex,
        codeItemCode: targetGroup.codeItemPos.code,
        optionCode: String(targetGroup.rows[rowIndex]?.optionCode ?? ''),
      })),
      'remove'
    )

    if (referencedValueGuard) {
      void showReferencedValueBlockedModal(
        'Unable to Delete Group',
        referencedValueGuard.message,
        referencedValueGuard.items,
        referencedValueGuard.navigationTarget
      )
      return
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Group removed successfully' })
    applyGroupsUpdate((prev) => {
      const next = [...prev]
      const currentGroup = next[gIdx]
      if (!currentGroup) return prev

      const nextRows = currentGroup.rows.map((row, rowIndex) => (
        persistedSection.rowIndices.includes(rowIndex)
          ? { ...row, group: null }
          : row
      ))

      next[gIdx] = {
        ...currentGroup,
        rows: normalizeRowsForRowGroups(nextRows),
      }

      return next
    })
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setAddMenuAnchor(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setHasChanges(true)
  }

  const handleExportPdf = useCallback(async () => {
    const table = tableExportRef.current
    if (!table) return

    const partNumber = header.partNumber && header.partNumber !== '--' ? header.partNumber : 'code-index'

    showLoadingModal({ title: 'Export to PDF', message: 'Preparing the Code Sheet...' })
    // The capture blocks the main thread, so let the modal finish opening before it starts.
    await new Promise((resolve) => { window.setTimeout(resolve, PDF_LOADING_MODAL_PAINT_MS) })

    // `user` comes from useSecurity(), which is re-resolved whenever the demo user selector in
    // the header changes, so this is the person the app currently acts as.
    const exportedBy = user?.name?.trim() || user?.emailAddress || 'Unknown user'
    const exportedByNote = `Exported by ${exportedBy}`
      + `${user?.emailAddress ? ` (${user.emailAddress})` : ''}`
      + ` - ${formatDateTime(new Date().toISOString())}`

    const pdfHeader = createPdfHeaderElement({
      sources: [
        document.querySelector<HTMLElement>('[data-pdf-app-header]'),
        document.querySelector<HTMLElement>('[data-pdf-codesheet-header]'),
      ],
      note: exportedByNote,
      widthPx: table.offsetWidth,
    })

    try {
      // Loaded on demand: this module pulls in html2canvas-pro and jsPDF, which are far too
      // heavy to sit in the page's initial bundle for a button most visits never press.
      const { exportTableToPdf } = await import('../utils/tablePdfExport')

      await exportTableToPdf(table, `${partNumber}-code-index.pdf`, {
        firstPageHeader: pdfHeader,
        onProgress: (done, total) => {
          updateLoadingModalMessage(`Rendering page ${done} of ${total}...`)
        },
      })
      closeLoadingModal()
    } catch (error) {
      closeLoadingModal()
      logSystemError(user, {
        action: 'export_codesheet_pdf',
        error,
        resource: 'codesheet',
        resourceId: id,
      })
      void showErrorModal({
        title: 'Unable to Export PDF',
        description: 'The Code Index table could not be exported. Please try again.',
        confirmText: 'OK'
      })
    } finally {
      pdfHeader.remove()
    }
  }, [header.partNumber, id, user])

  function handleDelete() {
    if (!canMutateCodeSheet) return
    if (canDeleteRow && rowDeleteTarget) {
      deleteRowAtIndex(rowDeleteTarget.gIdx, rowDeleteTarget.rIdx)
    } else if (canDeleteRowGroup && rowGroupDeleteTarget) {
      deleteRowGroupAtIndex(rowGroupDeleteTarget.gIdx, rowGroupDeleteTarget.sectionKey)
    } else if (canDeleteGroup && groupDeleteTarget !== null) {
      deleteGroupAtIndex(groupDeleteTarget)
    }
  }

  function formatPositionLabel(start: number, end: number): string {
    return start === end ? String(start) : `${start}-${end}`
  }

  type TableRow = {
    gIdx: number
    rIdx: number
    optionCode: string
    status: string
    rowGroup: string | null
    groupCode: string
    positionLabel: string
    isGroupFirstRow: boolean
    groupRowCount: number
    descriptionCell: { rowSpan: number; value: string | null }
    availabilityCell: { rowSpan: number; value: RuleNode | null | undefined }
    isNewRow: boolean
    posRangeLen: number
  }

  type CodeItemRenderSection = {
    key: string
    name: string | null
    rowIndices: number[]
    isDraft: boolean
    draftId: string | null
    valueStart: number | null
    valueEnd: number | null
  }

  type CodeItemRenderModel = {
    gIdx: number
    group: CodeItemGroup
    positionLabel: string
    posRangeLen: number
    titleText: string
    showTitleRow: boolean
    descMap: Array<{ rowSpan: number; value: string | null }>
    availMap: Array<{ rowSpan: number; value: RuleNode | null | undefined }>
    sections: CodeItemRenderSection[]
    renderedRowCount: number
  }

  // Helper to check if there are any unsaved new rows
  const hasUnsavedNewRows = useMemo(() => {
    return groups.some(g => g.rows.some(r => r.isNew))
  }, [groups])

  const persistedOptionValidationErrors = useMemo<Record<string, string>>(() => {
    return groups.reduce<Record<string, string>>((accumulator, group, gIdx) => {
      const digits = getGroupDigits(group)

      group.rows.forEach((row, rIdx) => {
        if (row.isNew) return

        const error = getOptionCodeLengthValidationError(row.optionCode, digits)
        if (error) {
          accumulator[`${gIdx}:${rIdx}`] = error
        }
      })

      return accumulator
    }, {})
  }, [groups])

  const persistedDescriptionValidationErrors = useMemo<Record<string, string>>(() => {
    return groups.reduce<Record<string, string>>((accumulator, group, gIdx) => {
      const descriptionMap = buildRowMapFromGroupIds<string>(group.rows, 'description')

      group.rows.forEach((row, rIdx) => {
        if (row.isNew) return
        if ((descriptionMap[rIdx]?.rowSpan ?? 0) <= 0) return

        const error = getDescriptionValidationError(descriptionMap[rIdx]?.value as string | null)
        if (error) {
          accumulator[`${gIdx}:${rIdx}`] = error
        }
      })

      return accumulator
    }, {})
  }, [groups])

  const persistedStatusValidationErrors = useMemo<Record<string, string>>(() => {
    return groups.reduce<Record<string, string>>((accumulator, group, gIdx) => {
      group.rows.forEach((row, rIdx) => {
        if (row.isNew) return

        const error = getStatusValidationError(row.status)
        if (error) {
          accumulator[`${gIdx}:${rIdx}`] = error
        }
      })

      return accumulator
    }, {})
  }, [groups])

  const persistedTitleValidationErrors = useMemo<Record<number, string>>(() => {
    return groups.reduce<Record<number, string>>((accumulator, group, gIdx) => {
      const error = getDescriptionValidationError(group.title)
      if (error) {
        accumulator[gIdx] = error
      }

      return accumulator
    }, {})
  }, [groups])

  const persistedAttributeValidationErrors = useMemo<Record<string, string>>(() => {
    return groups.reduce<Record<string, string>>((accumulator, group, gIdx) => {
      group.rows.forEach((row, rIdx) => {
        if (row.isNew) return

        getApplicableAttributeDefinitions(group, row).forEach((attribute) => {
          const value = normalizeAttributeValues(row.attributeValues)[attribute.id]
          const error = getAttributeValueValidationError(attribute, value)
          if (error) {
            accumulator[`${gIdx}:${rIdx}:${attribute.id}`] = error
          }
        })
      })

      return accumulator
    }, {})
  }, [groups])

  const brokenRuleReferenceIssues = useMemo(() => {
    return findMissingRuleValueReferences(groups)
  }, [groups])
  const firstBrokenRuleReferenceIssue = brokenRuleReferenceIssues[0] || null
  const invalidPositionableRuleIssues = useMemo(() => findInvalidPositionableRuleReferences(groups), [groups])
  const compositeValidationErrors = useMemo(() => groups.flatMap((group, gIdx) => (
    validateCompositeCodeItem(group).map(message => ({ gIdx, code: group.codeItemPos.code, message }))
  )), [groups])

  // Sheet-wide rule checks. Kept off the keystroke path: the drawer validates only the rule being
  // edited. `followAllRequiredValues` turns on the chain search for every rule, which the save path
  // pays for once; the continuous pass follows requirements only for the rules this session edited.
  const collectRuleValidationIssues = useCallback((followAllRequiredValues: boolean): RuleValidationIssue[] => {
    const issues: RuleValidationIssue[] = []

    groups.forEach((group, gIdx) => {
      group.rows.forEach((row, rIdx) => {
        if (!row.rule) return

        const ruleKey = getRuleKey(group, row)
        // The continuous pass checks each rule on its own: a value made unreachable by rules working
        // together is never a reason to refuse a save, and the server flags it after the save.
        const { errors, warnings } = validateRuleLogic(row.rule, {
          groups,
          targetGroupIndex: gIdx,
          targetRowIndex: rIdx,
          followRequiredValues: followAllRequiredValues,
        })
        const pushIssue = (severity: RuleValidationIssue['severity'], message: string) => {
          issues.push({
            gIdx,
            rIdx,
            ruleKey,
            severity,
            codeItemCode: group.codeItemPos.code,
            optionCode: row.optionCode,
            message,
          })
        }

        errors.forEach(error => pushIssue('error', error.message))
        warnings
          .filter(warning => warning.type === 'dead-trigger')
          .forEach(warning => pushIssue('warning', warning.message))
      })
    })

    return issues
  }, [groups])

  const ruleValidationIssues = useMemo(() => collectRuleValidationIssues(false), [collectRuleValidationIssues])

  const blockingRuleValidationIssues = useMemo(() => (
    ruleValidationIssues.filter(issue => issue.severity === 'error' && touchedRuleKeys.has(issue.ruleKey))
  ), [ruleValidationIssues, touchedRuleKeys])
  const hasBlockingValidationErrors =
    Object.keys(persistedOptionValidationErrors).length > 0 ||
    Object.keys(persistedDescriptionValidationErrors).length > 0 ||
    Object.keys(persistedStatusValidationErrors).length > 0 ||
    Object.keys(persistedTitleValidationErrors).length > 0 ||
    Object.keys(persistedAttributeValidationErrors).length > 0 ||
    compositeValidationErrors.length > 0 ||
    invalidPositionableRuleIssues.length > 0 ||
    brokenRuleReferenceIssues.length > 0 ||
    blockingRuleValidationIssues.length > 0
  const blockingValidationReason = hasBlockingValidationErrors
    ? 'Fix invalid Titles, Descriptions, Options, composite subgroups, Status values, Attribute values, broken rule references, or the availability rules you edited before saving or submitting changes. Options must use only A-Z, 0-9, *, or # and match the Code Item number of digits. Status must be X, NS, NA, or blank.'
    : null

  const hasPendingRowChanges = hasUnsavedNewRows || inlineEdit !== null
  const hasInlineEditUnsavedChanges = useMemo(() => {
    if (!inlineEdit) return false
    return inlineEdit.draftValue !== inlineEdit.originalValue
  }, [inlineEdit])
  const hasUnsavedCodeSheetChanges = hasEffectiveChanges || hasUnsavedNewRows || hasInlineEditUnsavedChanges

  const discardUnsavedCodeSheetChanges = useCallback(() => {
    newRowBlurActionRef.current = null
    applyGroupsUpdate(cloneState(originalGroups))
    setHasChanges(false)
    setTouchedRuleKeys(new Set<string>())
    setPublished(true)
    setUndoStack([])
    setUndoToast(null)
    setInlineEdit(null)
    setNewRowOptionValidation(null)
    setNewRowDescriptionValidation(null)
    setDraftRowGroupsByCodeItemKey({})
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setAvailabilityConfigRow(null)
    setHoveredGroupReorder(null)
    setDraggedGroup(null)
    setGroupDropTarget(null)
    setHoveredReorderRow(null)
    setDraggedRow(null)
    setDraggedCompositeSubgroup(null)
    setRowDropTarget(null)
    setCodeItemActionMenu(null)
    setAttributeSchemaTarget(null)
    setValueAttributesTarget(null)
    setHoveredSection(null)
    setReferenceAppendixTarget(null)
    setReferencedAppendicesTarget(null)
  }, [applyGroupsUpdate, originalGroups])

  const { allowNavigationWithoutPrompt } = useUnsavedCodeSheetNavigationGuard({
    hasUnsavedChanges: hasUnsavedCodeSheetChanges,
    onDiscardChanges: discardUnsavedCodeSheetChanges,
  })

  const hasAttributeColumn = useMemo(() => hasAnyAttributeSchemas(groups), [groups])
  const visibleColumnCount = hasAttributeColumn ? 7 : 6

  // Helper to cancel all existing new rows
  const cancelAllNewRows = useCallback(() => {
    setNewRowOptionValidation(null)
    setNewRowDescriptionValidation(null)
    applyGroupsUpdate(prev => {
      const next = prev
        .map((group, gIdx) => ({
          ...group,
          rows: group.rows.filter(r => !r.isNew)
        }))
        .filter((group, gIdx) => group.rows.length > 0 || isCompositeCodeItem(group) || getDraftRowGroupsForCodeItem(group, gIdx).length > 0)
      return next
    })
  }, [applyGroupsUpdate, getDraftRowGroupsForCodeItem])

  const tableRows: TableRow[] = useMemo(() => {
    const rows: TableRow[] = []
    groups.forEach((g, gIdx) => {
      const rowCount = g.rows.length
      const descMap = buildRowMapFromGroupIds<string>(g.rows, 'description')
      const availMap = buildRowMapFromGroupIds<RuleNode | null | undefined>(g.rows, 'availability')
      const [start, end] = g.codeItemPos.posRange
      const posRangeLen = Math.max(0, (end - start + 1))
      const positionLabel = formatPositionLabel(start, end)
      for (let rIdx = 0; rIdx < rowCount; rIdx++) {
        rows.push({
          gIdx,
          rIdx,
          optionCode: g.rows[rIdx].optionCode,
          status: normalizeStatusValue(g.rows[rIdx].status),
          rowGroup: normalizeRowGroupName(g.rows[rIdx].group),
          groupCode: g.codeItemPos.code,
          positionLabel,
          isGroupFirstRow: rIdx === 0,
          groupRowCount: rowCount,
          descriptionCell: { rowSpan: descMap[rIdx].rowSpan, value: (descMap[rIdx].value as string | null) },
          availabilityCell: { rowSpan: availMap[rIdx].rowSpan, value: (availMap[rIdx].value as RuleNode | null | undefined) },
          isNewRow: !!g.rows[rIdx].isNew,
          posRangeLen,
        })
      }
    })
    return rows
  }, [groups])

  const columns = useMemo<ColumnDef<TableRow>[]>(() => [
      { id: 'codeItemPos', header: 'Code Item' },
      { id: 'position', header: 'Position' },
      { id: 'optionCode', header: 'Options' },
      { id: 'description', header: 'Description' },
      ...(hasAttributeColumn ? [{ id: 'attrs', header: 'Attributes' } as ColumnDef<TableRow>] : []),
      { id: 'availability', header: 'Availability Rules' },
      { id: 'status', header: 'Status' },
  ], [hasAttributeColumn])

  function ruleToString(rule: RuleNode | null | undefined): string {
    if (!rule) return ''
    return rule.description || ''
  }

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return tableRows
    const rows: TableRow[] = []
    groups.forEach((g, gIdx) => {
      const rowCount = g.rows.length
      const descMap = buildRowMapFromGroupIds<string>(g.rows, 'description')
      const availMap = buildRowMapFromGroupIds<RuleNode | null | undefined>(g.rows, 'availability')
      const [start, end] = g.codeItemPos.posRange
      const positionLabel = formatPositionLabel(start, end)
      const draftSectionNames = getCodeItemDraftRowGroups(gIdx).map((section) => section.name)
      const attributeSearchTerms = normalizeAttributeSchemaScopes(g.attributeSchemas).flatMap((schema) => [
        schema.group || '',
        ...schema.attributes.flatMap((attribute) => [
          attribute.name,
          attribute.type,
          attribute.unit || '',
          ...(attribute.allowedValues || []),
        ])
      ])
      const hasMatch = g.rows.some((row, rIdx) => {
        const fields = [
          String(row.optionCode || ''),
          normalizeStatusValue(row.status),
          String(row.group || ''),
          g.codeItemPos.code,
          positionLabel,
          String(g.title || ''),
          String(descMap[rIdx].value || ''),
          ruleToString(row.rule),
          ...getApplicableAttributeDefinitions(g, row).map((attribute) => String(normalizeAttributeValues(row.attributeValues)[attribute.id] || '')),
        ]
        return fields.some(val => String(val).toLowerCase().includes(q))
      }) || draftSectionNames.some((name) => String(name || '').toLowerCase().includes(q)) ||
        attributeSearchTerms.some((term) => String(term || '').toLowerCase().includes(q))
      if (hasMatch) {
        const posRangeLen = Math.max(0, (end - start + 1))
        for (let rIdx = 0; rIdx < rowCount; rIdx++) {
          rows.push({
            gIdx,
            rIdx,
            optionCode: g.rows[rIdx].optionCode,
            status: normalizeStatusValue(g.rows[rIdx].status),
            rowGroup: normalizeRowGroupName(g.rows[rIdx].group),
            groupCode: g.codeItemPos.code,
            positionLabel,
            isGroupFirstRow: rIdx === 0,
            groupRowCount: rowCount,
            descriptionCell: { rowSpan: descMap[rIdx].rowSpan, value: (descMap[rIdx].value as string | null) },
            availabilityCell: { rowSpan: availMap[rIdx].rowSpan, value: (availMap[rIdx].value as RuleNode | null | undefined) },
            isNewRow: !!g.rows[rIdx].isNew,
            posRangeLen,
          })
        }
      }
    })
    return rows
  }, [getCodeItemDraftRowGroups, groups, tableRows, searchQuery])

  const table = useReactTable<TableRow>({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => `${row.gIdx}:${row.rIdx}`,
  })

  const inlineEditingGroupTitleGIdx = inlineEdit?.kind === 'groupTitle' ? inlineEdit.gIdx : null
  const inlineEditingRowGroupTitleGIdx = inlineEdit?.kind === 'rowGroupTitle' ? inlineEdit.gIdx : null
  const inlineEditingRowGroupTitleSectionKey = inlineEdit?.kind === 'rowGroupTitle' ? inlineEdit.sectionKey : null

  const isDiffProject = Boolean(isProjectMode && activeProject && !isMainProject(activeProject))
  const canToggleCodeSheetDiff = Boolean(
    isDiffProject
    && activeProject
    && !DIFF_UNAVAILABLE_PROJECT_STATUSES.has(String(activeProject.status || ''))
  )
  const diffReady = isDiffProject && Boolean(projectComparisonTree && baseComparisonTree && liveComparisonTree) && !comparisonError
  const isCodeSheetDiffDisplayed = canToggleCodeSheetDiff && diffVisible
  const codeSheetDiff = useMemo(() => buildCodeSheetDiffModel({
    liveGroups: diffReady ? liveComparisonGroups : [],
    projectGroups: diffReady ? groups : [],
    liveTree: diffReady ? liveComparisonTree : null,
    projectTree: diffReady ? projectComparisonTree : null,
    baseGroups: diffReady ? baseComparisonGroups : undefined,
    baseTree: diffReady ? baseComparisonTree : null,
    arrivedChanges: diffReady ? arrivedComparisonChanges : [],
    authorBaseGroups: diffReady ? reviewBaseComparisonGroups : undefined,
    authorBaseTree: diffReady ? reviewBaseComparisonTree : null,
    conflicts: diffReady ? comparisonConflicts : undefined,
  }), [arrivedComparisonChanges, baseComparisonGroups, baseComparisonTree, comparisonConflicts, diffReady, groups, liveComparisonGroups, liveComparisonTree, projectComparisonTree, reviewBaseComparisonGroups, reviewBaseComparisonTree])
  const changedOnlyActive = diffReady && isCodeSheetDiffDisplayed && changedRowsOnly

  const retryComparison = useCallback(() => {
    if (!id || !activeProjectId) return
    const mainProject = projects.find(isMainProject)
    if (!mainProject?.id) {
      setComparisonError('The live Main project is not available for comparison.')
      return
    }
    void loadComparisonData(id, activeProjectId, mainProject.id)
  }, [activeProjectId, id, loadComparisonData, projects])

  const visibleCodeItemModels = useMemo<CodeItemRenderModel[]>(() => {
    const query = searchQuery.trim().toLowerCase()

    return groups.reduce<CodeItemRenderModel[]>((accumulator, group, gIdx) => {
      const descMap = buildRowMapFromGroupIds<string>(group.rows, 'description')
      const availMap = buildRowMapFromGroupIds<RuleNode | null | undefined>(group.rows, 'availability')
      const [start, end] = group.codeItemPos.posRange
      const positionLabel = formatPositionLabel(start, end)
      const posRangeLen = Math.max(0, (end - start + 1))
      const titleText = typeof group.title === 'string' ? group.title.trim() : ''
      const groupDiff = codeSheetDiff.groupByProjectIndex.get(gIdx)
      const groupHasDiff = diffReady && isCodeSheetDiffDisplayed && Boolean(
        groupDiff && (groupDiff.status !== 'unchanged' || groupDiff.arrivedStatus !== 'unchanged')
      )
      const isFocusedGroupChange = diffReady && groupDiff?.key === activeDiffEntryKey
      const deletedRows = diffReady && isCodeSheetDiffDisplayed
        ? (codeSheetDiff.deletedRowsByProjectGroupIndex.get(gIdx) || []).filter((entry) => (
            !query || entry.searchableText.includes(query)
          ))
        : []
      const arrivedRows = diffReady && isCodeSheetDiffDisplayed
        ? (codeSheetDiff.arrivedRowsByProjectGroupIndex.get(gIdx) || []).filter((entry) => (
            !query || entry.searchableText.includes(query)
          ))
        : []
      const groupHasDeletedRows = deletedRows.length > 0
      const groupHasArrivedRows = arrivedRows.length > 0
      const showTitleRow = titleText.length > 0 || inlineEditingGroupTitleGIdx === gIdx || Boolean(persistedTitleValidationErrors[gIdx]) || groupHasDiff || isFocusedGroupChange
      const draftSections = getCodeItemDraftRowGroups(gIdx).map<CodeItemRenderSection>((section) => ({
        key: `draft:${section.id}`,
        name: section.name,
        rowIndices: [],
        isDraft: true,
        draftId: section.id,
        valueStart: null,
        valueEnd: null,
      }))
      const persistedSections = buildRowGroupSections(group.rows).map<CodeItemRenderSection>((section) => ({
        key: section.key,
        name: section.name,
        rowIndices: section.rowIndices,
        isDraft: false,
        draftId: null,
        valueStart: null,
        valueEnd: null,
      }))
      const compositeSections = isCompositeCodeItem(group)
        ? positionSubgroups(resolveCodeItemSubgroups(group), getCodeItemDigits(group)).map<CodeItemRenderSection>((subgroup) => ({
            key: `composite:${subgroup.localKey}`,
            name: subgroup.name,
            rowIndices: group.rows.reduce<number[]>((indices, row, rowIndex) => {
              if (normalizeRowGroupName(row.group)?.toLowerCase() === subgroup.name.toLowerCase()) indices.push(rowIndex)
              return indices
            }, []),
            isDraft: false,
            draftId: null,
            valueStart: subgroup.start,
            valueEnd: subgroup.end,
          }))
        : []
      const allSections = isCompositeCodeItem(group)
        ? [...compositeSections, ...draftSections]
        : [...persistedSections, ...draftSections]
      const sections = changedOnlyActive
        ? allSections
            .map((section) => ({
              ...section,
              rowIndices: section.rowIndices.filter((rowIndex) => {
                const rowDiff = codeSheetDiff.rowByProjectPosition.get(`${gIdx}:${rowIndex}`)
                return Boolean(rowDiff && (rowDiff.status !== 'unchanged' || rowDiff.arrivedStatus !== 'unchanged'))
              }),
            }))
            .filter((section) => section.rowIndices.length > 0)
        : allSections
      const visibleSectionHeaderCount = sections.filter((section) => (
        Boolean(section.name) ||
        (inlineEditingRowGroupTitleGIdx === gIdx && inlineEditingRowGroupTitleSectionKey === section.key)
      )).length
      const visibleDataRowCount = sections.reduce((count, section) => count + section.rowIndices.length, 0)
      const effectiveShowTitleRow = changedOnlyActive
        ? Boolean(groupHasDiff || groupHasDeletedRows)
        : showTitleRow
      const renderedRowCount = visibleDataRowCount +
        visibleSectionHeaderCount +
        arrivedRows.length +
        (effectiveShowTitleRow ? 1 : 0)

      if (renderedRowCount === 0 && deletedRows.length === 0 && arrivedRows.length === 0) {
        return accumulator
      }

      const matchesQuery = !query || (
        [
          group.codeItemPos.code,
          positionLabel,
          titleText,
          ...normalizeAttributeSchemaScopes(group.attributeSchemas).flatMap((schema) => [
            schema.group || '',
            ...schema.attributes.flatMap((attribute) => [
              attribute.name,
              attribute.type,
              attribute.unit || '',
              ...(attribute.allowedValues || []),
            ])
          ])
        ].some((value) => String(value || '').toLowerCase().includes(query)) ||
        sections.some((section) => String(section.name || '').toLowerCase().includes(query)) ||
        group.rows.some((row, rIdx) => {
          const fields = [
            String(row.optionCode || ''),
            normalizeStatusValue(row.status),
            String(row.group || ''),
            group.codeItemPos.code,
            positionLabel,
            String(group.title || ''),
            String(descMap[rIdx].value || ''),
            ruleToString(row.rule),
            ...getApplicableAttributeDefinitions(group, row).map((attribute) => String(normalizeAttributeValues(row.attributeValues)[attribute.id] || '')),
          ]

          return fields.some((value) => String(value).toLowerCase().includes(query))
        }) || deletedRows.length > 0 || arrivedRows.length > 0
      )

      const hasChangedRows = sections.some((section) => section.rowIndices.length > 0) || groupHasDeletedRows || groupHasArrivedRows
      if (!matchesQuery || (changedOnlyActive && !groupHasDiff && !hasChangedRows)) {
        return accumulator
      }

      accumulator.push({
        gIdx,
        group,
        positionLabel,
        posRangeLen,
        titleText,
        showTitleRow: effectiveShowTitleRow,
        descMap,
        availMap,
        sections,
        renderedRowCount,
      })

      return accumulator
    }, [])
  }, [activeDiffEntryKey, changedOnlyActive, codeSheetDiff, diffReady, getCodeItemDraftRowGroups, groups, inlineEditingGroupTitleGIdx, inlineEditingRowGroupTitleGIdx, inlineEditingRowGroupTitleSectionKey, isCodeSheetDiffDisplayed, persistedTitleValidationErrors, searchQuery])

  function clearSelectionAndInsertUi() {
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setAddMenuAnchor(null)
    setCodeItemActionMenu(null)
  }

  function getAllAttributeDefinitions(schemas: CodeItemAttributeSchemaScope[]): CodeItemAttributeDefinition[] {
    return normalizeAttributeSchemaScopes(schemas).flatMap((schema) => schema.attributes)
  }

  async function handleSaveAttributeSchemas(gIdx: number, schemas: CodeItemAttributeSchemaScope[]) {
    if (!canMutateCodeSheet) return

    const group = groups[gIdx]
    if (!group) return

    const normalizedSchemas = normalizeAttributeSchemaScopes(schemas)
    const validationError = validateAttributeSchemaScopes(normalizedSchemas)
    if (validationError) {
      await showErrorModal({
        title: 'Unable to Save Attributes',
        description: validationError,
        confirmText: 'OK',
      })
      return
    }

    const allAttributes = getAllAttributeDefinitions(normalizedSchemas)

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Attributes configured successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    applyGroupsUpdate((prev) => {
      const next = [...prev]
      const currentGroup = next[gIdx]
      if (!currentGroup) return prev

      next[gIdx] = {
        ...currentGroup,
        attributeSchemas: normalizedSchemas,
        rows: currentGroup.rows.map((row) => ({
          ...row,
          attributeValues: pruneAttributeValues(row.attributeValues, allAttributes),
        })),
      }

      return next
    })
    setHasChanges(true)
    setPublished(false)
    setAttributeSchemaTarget(null)
  }

  function handleSaveValueAttributes(values: Record<string, string | null>) {
    if (!canMutateCodeSheet) return
    if (!valueAttributesTarget) return

    const { gIdx, rIdx } = valueAttributesTarget
    const group = groups[gIdx]
    const row = group?.rows[rIdx]
    if (!group || !row) return

    const allAttributes = getAllAttributeDefinitions(group.attributeSchemas || [])
    const mergedValues = {
      ...normalizeAttributeValues(row.attributeValues),
      ...normalizeAttributeValues(values),
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Attribute values updated', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    applyGroupsUpdate((prev) => {
      const next = [...prev]
      const currentGroup = next[gIdx]
      if (!currentGroup) return prev

      const rows = [...currentGroup.rows]
      const currentRow = rows[rIdx]
      if (!currentRow) return prev

      rows[rIdx] = {
        ...currentRow,
        attributeValues: pruneAttributeValues(mergedValues, allAttributes),
      }
      next[gIdx] = {
        ...currentGroup,
        rows,
      }

      return next
    })
    setHasChanges(true)
    setPublished(false)
    setValueAttributesTarget(null)
  }

  // ---- Appendix references ----

  function resolveAppendixRefTarget(target: AppendixRefTarget | null): {
    group: CodeItemGroup
    row: CodeRow | null
    sectionName: string | null
  } | null {
    if (!target) return null
    const group = groups[target.gIdx]
    if (!group) return null
    if (target.kind === 'value') {
      const row = group.rows[target.rIdx]
      return row ? { group, row, sectionName: normalizeRowGroupName(row.group) } : null
    }
    if (target.kind === 'group') return { group, row: null, sectionName: target.sectionName }
    return { group, row: null, sectionName: null }
  }

  function describeAppendixRefTargetOf(target: AppendixRefTarget | null): string {
    const resolved = resolveAppendixRefTarget(target)
    if (!target || !resolved) return ''
    return describeAppendixRefTarget(target.kind, resolved.group, resolved.row, resolved.sectionName)
  }

  function refsForAppendixRefTarget(target: AppendixRefTarget | null): ResolvedAppendixRef[] {
    const resolved = resolveAppendixRefTarget(target)
    if (!resolved) return []
    return resolveAppendixRefs(resolved.group, resolved.row, resolved.sectionName, appendixSummariesByKey)
  }

  // Appendices the Reference modal must not offer again, with the reason.
  const referenceAppendixDisabledKeys = useMemo(() => {
    const disabled = new Map<string, string>()
    refsForAppendixRefTarget(referenceAppendixTarget).forEach(ref => {
      if (ref.source === 'own') disabled.set(ref.key, 'Already referenced')
      else disabled.set(ref.key, `Already applies · ${ref.sourceLabel}`)
    })
    return disabled
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referenceAppendixTarget, groups, appendixSummariesByKey])

  function updateAppendixRefsOfTarget(
    target: AppendixRefTarget,
    update: (refs: string[] | undefined) => string[],
    toastMessage: string
  ) {
    if (!canMutateCodeSheet) return
    if (!groups[target.gIdx]) return

    addToHistory(groups, hasChanges)
    setUndoToast({ message: toastMessage, ...TEMPORARY_SUCCESS_TOAST_PROPS })
    applyGroupsUpdate((prev) => {
      const next = [...prev]
      const currentGroup = next[target.gIdx]
      if (!currentGroup) return prev

      if (target.kind === 'value') {
        const rows = [...currentGroup.rows]
        const currentRow = rows[target.rIdx]
        if (!currentRow) return prev
        rows[target.rIdx] = { ...currentRow, appendixRefs: update(currentRow.appendixRefs) }
        next[target.gIdx] = { ...currentGroup, rows }
        return next
      }

      if (target.kind === 'group') {
        const groupRefs = normalizeGroupAppendixRefs(currentGroup.groupAppendixRefs)
        const updated = update(groupRefs[target.sectionName])
        const { [target.sectionName]: _removed, ...rest } = groupRefs
        next[target.gIdx] = {
          ...currentGroup,
          groupAppendixRefs: updated.length > 0 ? { ...rest, [target.sectionName]: updated } : rest,
        }
        return next
      }

      next[target.gIdx] = { ...currentGroup, appendixRefs: update(currentGroup.appendixRefs) }
      return next
    })
    setHasChanges(true)
    setPublished(false)
  }

  function handleReferenceAppendix(key: string) {
    if (!referenceAppendixTarget) return
    updateAppendixRefsOfTarget(referenceAppendixTarget, refs => addAppendixRef(refs, key), 'Appendix referenced')
    setRecentAppendixPicks(pushRecentAppendixPick(id, key))
    setReferenceAppendixTarget(null)
  }

  function handleRemoveAppendixRef(key: string) {
    if (!referencedAppendicesTarget) return
    updateAppendixRefsOfTarget(referencedAppendicesTarget, refs => removeAppendixRef(refs, key), 'Appendix reference removed')
  }

  const canReferenceAppendices = canMutateCodeSheet && !hasPendingRowChanges

  const renderAppendixPill = (visible: boolean, onClick: () => void, label: string) => {
    if (!canReferenceAppendices) return null
    return (
      <button
        type="button"
        aria-label={label}
        data-pdf-hide
        onClick={(event) => { event.stopPropagation(); onClick() }}
        onDoubleClick={(event) => event.stopPropagation()}
        className={getInfoBadgeClassName(
          'empty',
          `shrink-0 cursor-pointer gap-0.5 rounded-full px-2 py-0 text-[11px] leading-5 text-[#1D5176] transition-all hover:border-[#1D74D8] hover:bg-[#EEF6FF] ${visible ? 'opacity-100' : 'pointer-events-none opacity-0'}`
        )}
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        Appendix
      </button>
    )
  }

  function activateAvailabilityConfigRow(row: TableRow) {
    if (!canMutateCodeSheet || row.isNewRow) return

    setAvailabilityConfigRow({ gIdx: row.gIdx, rIdx: row.rIdx })
  }

  function restoreInlineEditFocus() {
    window.requestAnimationFrame(() => {
      const input = inlineEditInputRef.current
      if (!input) return

      input.focus()
      if (input instanceof HTMLInputElement) {
        input.select()
      }
    })
  }

  function updateInlineEditDraft(nextValue: string) {
    setInlineEdit((current) => (
      current
        ? {
            ...current,
            draftValue: nextValue,
            error: null,
          }
        : current
    ))
  }

  function updateInlineOptionCodeDraft(event: React.ChangeEvent<HTMLInputElement>, maxLength: number) {
    const nativeEvent = event.nativeEvent as InputEvent | Event
    const inputType = 'inputType' in nativeEvent ? nativeEvent.inputType : undefined
    const inputData = 'data' in nativeEvent && typeof nativeEvent.data === 'string'
      ? nativeEvent.data
      : null

    setInlineEdit((current) => {
      if (current?.kind !== 'optionCode') return current

      const { value, removedInvalidCharacters } = getLiveOptionCodeInputValue(
        event.target.value,
        current.draftValue,
        maxLength,
        inputType,
        inputData
      )

      return {
        ...current,
        draftValue: value,
        error: removedInvalidCharacters ? OPTION_CODE_ALLOWED_CHARACTERS_ERROR : null,
      }
    })
  }

  function updateInlineStatusDraft(nextValue: string) {
    const normalizedValue = normalizeStatusValue(nextValue)
    setInlineEdit((current) => {
      if (current?.kind !== 'status') return current

      return {
        ...current,
        draftValue: normalizedValue,
        error: getStatusValidationError(normalizedValue),
      }
    })
  }

  function setInlineEditError(message: string) {
    setInlineEdit((current) => (
      current
        ? {
            ...current,
            error: message,
          }
        : current
    ))
    restoreInlineEditFocus()
  }

  function beginOptionCodeInlineEdit(row: TableRow, event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input') || target?.closest('select')) return
    if (!canMutateCodeSheet || hasPendingRowChanges || row.isNewRow) return

    const group = groups[row.gIdx]
    const currentRow = group?.rows[row.rIdx]
    if (!group || !currentRow) return

    if (isCompositeCodeItem(group)) {
      clearSelectionAndInsertUi()
      setCompositeOptionTarget({ gIdx: row.gIdx, rIdx: row.rIdx })
      return
    }

    clearSelectionAndInsertUi()
    setInlineEdit({
      kind: 'optionCode',
      gIdx: row.gIdx,
      rIdx: row.rIdx,
      originalValue: String(currentRow.optionCode ?? ''),
      draftValue: String(currentRow.optionCode ?? ''),
      error: null,
    })
  }

  function beginStatusInlineEdit(row: TableRow, event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input') || target?.closest('select')) return
    if (!canMutateCodeSheet || hasPendingRowChanges || row.isNewRow) return

    const group = groups[row.gIdx]
    const currentRow = group?.rows[row.rIdx]
    if (!group || !currentRow) return

    clearSelectionAndInsertUi()
    setInlineEdit({
      kind: 'status',
      gIdx: row.gIdx,
      rIdx: row.rIdx,
      originalValue: normalizeStatusValue(currentRow.status),
      draftValue: normalizeStatusValue(currentRow.status),
      error: null,
    })
  }

  function beginDescriptionInlineEdit(row: TableRow, event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input') || target?.closest('select')) return
    if (!canMutateCodeSheet || hasPendingRowChanges || row.isNewRow || row.descriptionCell.rowSpan <= 0) return

    const group = groups[row.gIdx]
    const currentRow = group?.rows[row.rIdx]
    if (!group || !currentRow) return

    clearSelectionAndInsertUi()
    setInlineEdit({
      kind: 'description',
      gIdx: row.gIdx,
      rIdx: row.rIdx,
      blockId: getBlockId(currentRow, 'description'),
      originalValue: row.descriptionCell.value || '',
      draftValue: row.descriptionCell.value || '',
      error: null,
    })
  }

  function beginGroupTitleInlineEdit(gIdx: number, event: React.MouseEvent<HTMLElement>) {
    const target = event.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input') || target?.closest('select')) return
    if (!canMutateCodeSheet || hasPendingRowChanges) return

    const group = groups[gIdx]
    if (!group) return

    clearSelectionAndInsertUi()
    setInlineEdit({
      kind: 'groupTitle',
      gIdx,
      originalValue: String(group.title || ''),
      draftValue: String(group.title || ''),
      error: null,
    })
  }

  function beginRowGroupTitleInlineEdit(
    gIdx: number,
    sectionKey: string,
    rowIndices: number[],
    draftId: string | null,
    currentName: string,
    event: React.MouseEvent<HTMLElement>
  ) {
    const target = event.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input') || target?.closest('select')) return
    if (!canMutateCodeSheet || hasPendingRowChanges) return

    const group = groups[gIdx]
    if (!group) return

    clearSelectionAndInsertUi()
    setInlineEdit({
      kind: 'rowGroupTitle',
      gIdx,
      sectionKey,
      rowIndices,
      draftId,
      originalValue: currentName,
      draftValue: currentName,
      error: null,
    })
  }

  function cancelInlineEdit() {
    setInlineEdit(null)
  }

  async function commitInlineEdit(): Promise<boolean> {
    if (!inlineEdit) return true

    try {
      if (inlineEdit.kind === 'optionCode') {
        const group = groups[inlineEdit.gIdx]
        const row = group?.rows[inlineEdit.rIdx]
        if (!group || !row) {
          setInlineEdit(null)
          return true
        }

        const nextValue = finalizeOptionCode(inlineEdit.draftValue, getGroupDigits(group))
        const currentValue = finalizeOptionCode(row.optionCode, getGroupDigits(group))

        if (nextValue === currentValue) {
          setInlineEdit(null)
          return true
        }

        const optionCodeError = getOptionCodeSaveValidationError(inlineEdit.draftValue, getGroupDigits(group))
        if (optionCodeError) {
          setInlineEditError(optionCodeError)
          return false
        }

        if (hasDuplicateOptionCodeInGroup(group, inlineEdit.rIdx, nextValue)) {
          setInlineEditError(getDuplicateOptionCodeError(group.codeItemPos.code, nextValue))
          return false
        }

        const referencedValueGuard = getReferencedValueActionGuard([
          {
            gIdx: inlineEdit.gIdx,
            rIdx: inlineEdit.rIdx,
            codeItemCode: group.codeItemPos.code,
            optionCode: currentValue,
          },
        ], 'change')

        if (referencedValueGuard) {
          flushSync(() => {
            setInlineEdit(null)
          })
          await showReferencedValueBlockedModal(
            'Unable to Update Value',
            referencedValueGuard.message,
            referencedValueGuard.items,
            referencedValueGuard.navigationTarget
          )
          return false
        }

        addToHistory(groups, hasChanges)
        setUndoToast({ message: 'Option updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
        applyGroupsUpdate((prev) => {
          const next = [...prev]
          const nextGroup = { ...next[inlineEdit.gIdx] }
          const rows = [...nextGroup.rows]
          rows[inlineEdit.rIdx] = {
            ...rows[inlineEdit.rIdx],
            optionCode: nextValue,
          }
          nextGroup.rows = syncRowPositions(rows)
          next[inlineEdit.gIdx] = nextGroup
          return next
        })
        setHasChanges(true)
        setInlineEdit(null)
        return true
      }

      if (inlineEdit.kind === 'status') {
        const group = groups[inlineEdit.gIdx]
        const row = group?.rows[inlineEdit.rIdx]
        if (!group || !row) {
          setInlineEdit(null)
          return true
        }

        const nextValue = normalizeStatusValue(inlineEdit.draftValue)
        const currentValue = normalizeStatusValue(row.status)

        if (nextValue === currentValue) {
          setInlineEdit(null)
          return true
        }

        const statusError = getStatusValidationError(nextValue)
        if (statusError) {
          setInlineEditError(statusError)
          return false
        }

        addToHistory(groups, hasChanges)
        setUndoToast({ message: 'Status updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
        applyGroupsUpdate((prev) => {
          const next = [...prev]
          const nextGroup = { ...next[inlineEdit.gIdx] }
          const rows = [...nextGroup.rows]
          rows[inlineEdit.rIdx] = {
            ...rows[inlineEdit.rIdx],
            status: nextValue,
          }
          nextGroup.rows = syncRowPositions(rows)
          next[inlineEdit.gIdx] = nextGroup
          return next
        })
        setHasChanges(true)
        setInlineEdit(null)
        return true
      }

      if (inlineEdit.kind === 'description') {
        const group = groups[inlineEdit.gIdx]
        if (!group) {
          setInlineEdit(null)
          return true
        }

        const descriptionError = getDescriptionValidationError(inlineEdit.draftValue)
        if (descriptionError) {
          setInlineEditError(descriptionError)
          return false
        }

        const nextValue = inlineEdit.draftValue.trim()
        const currentValue = group.rows[inlineEdit.rIdx]?.description ?? null

        if (nextValue === currentValue) {
          setInlineEdit(null)
          return true
        }

        addToHistory(groups, hasChanges)
        setUndoToast({ message: 'Description updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
        applyGroupsUpdate((prev) => {
          const next = [...prev]
          const nextGroup = { ...next[inlineEdit.gIdx] }
          nextGroup.rows = nextGroup.rows.map((row) => (
            getBlockId(row, 'description') === inlineEdit.blockId
              ? { ...row, description: nextValue }
              : row
          ))
          next[inlineEdit.gIdx] = nextGroup
          return next
        })
        setHasChanges(true)
        setInlineEdit(null)
        return true
      }

      if (inlineEdit.kind === 'groupTitle') {
        const group = groups[inlineEdit.gIdx]
        if (!group) {
          setInlineEdit(null)
          return true
        }

        const nextTitle = normalizeGroupTitle(inlineEdit.draftValue)
        if (!nextTitle) {
          setInlineEditError(REQUIRED_FIELD_ERROR)
          return false
        }

        const currentTitle = normalizeGroupTitle(String(group.title || ''))

        if (nextTitle === currentTitle) {
          setInlineEdit(null)
          return true
        }

        addToHistory(groups, hasChanges)
        setUndoToast({ message: 'Title updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
        applyGroupsUpdate((prev) => {
          const next = [...prev]
          next[inlineEdit.gIdx] = {
            ...next[inlineEdit.gIdx],
            title: nextTitle,
          }
          return next
        })
        setHasChanges(true)
        setInlineEdit(null)
        return true
      }

      const group = groups[inlineEdit.gIdx]
      if (!group) {
        setInlineEdit(null)
        return true
      }

      const normalizedNextName = normalizeRowGroupName(inlineEdit.draftValue)
      const editedCompositeSubgroup = isCompositeCodeItem(group) && inlineEdit.sectionKey.startsWith('composite:')
        ? resolveCodeItemSubgroups(group).find(subgroup => `composite:${subgroup.localKey}` === inlineEdit.sectionKey)
        : null
      const duplicateNames = editedCompositeSubgroup
        ? resolveCodeItemSubgroups(group)
            .filter(subgroup => subgroup.localKey !== editedCompositeSubgroup.localKey)
            .map(subgroup => subgroup.name)
        : getExistingRowGroupNames(group, getCodeItemDraftRowGroups(inlineEdit.gIdx), {
            excludeRowIndices: inlineEdit.rowIndices,
            excludeDraftId: inlineEdit.draftId,
          })

      if (normalizedNextName && duplicateNames.some((name) => name.toLowerCase() === normalizedNextName.toLowerCase())) {
        setInlineEditError(`The group "${normalizedNextName}" already exists in this Code Item.`)
        return false
      }

      const currentName = editedCompositeSubgroup
        ? editedCompositeSubgroup.name
        : inlineEdit.draftId
        ? normalizeRowGroupName(getCodeItemDraftRowGroups(inlineEdit.gIdx).find((section) => section.id === inlineEdit.draftId)?.name || '')
        : normalizeRowGroupName(group.rows[inlineEdit.rowIndices[0]]?.group)

      if (inlineEdit.draftId && !normalizedNextName) {
        addToHistory(groups, hasChanges)
        updateDraftRowGroupsForCodeItem(getCodeItemLocalKey(group, inlineEdit.gIdx), (current) => current.filter((section) => section.id !== inlineEdit.draftId))
        setUndoToast({ message: 'Group removed' })
        setHasChanges(true)
        setInlineEdit(null)
        return true
      }

      if (normalizedNextName === currentName) {
        setInlineEdit(null)
        return true
      }

      addToHistory(groups, hasChanges)
      if (inlineEdit.draftId) {
        updateDraftRowGroupsForCodeItem(getCodeItemLocalKey(group, inlineEdit.gIdx), (current) => current.map((section) => (
          section.id === inlineEdit.draftId
            ? { ...section, name: normalizedNextName || section.name }
            : section
        )))
      } else {
        applyGroupsUpdate((prev) => {
          const next = [...prev]
          const nextGroup = next[inlineEdit.gIdx]
          if (!nextGroup) return prev

          next[inlineEdit.gIdx] = {
            ...nextGroup,
            subgroups: editedCompositeSubgroup
              ? resolveCodeItemSubgroups(nextGroup).map(subgroup => (
                  subgroup.localKey === editedCompositeSubgroup.localKey
                    ? { ...subgroup, name: normalizedNextName || subgroup.name }
                    : subgroup
                ))
              : nextGroup.subgroups,
            attributeSchemas: editedCompositeSubgroup
              ? normalizeAttributeSchemaScopes(nextGroup.attributeSchemas).map(schema => (
                  schema.scope === 'rowGroup' && normalizeRowGroupName(schema.group)?.toLowerCase() === currentName?.toLowerCase()
                    ? { ...schema, group: normalizedNextName }
                    : schema
                ))
              : nextGroup.attributeSchemas,
            // Group-level appendix references follow the group name.
            groupAppendixRefs: rekeyGroupAppendixRefs(nextGroup.groupAppendixRefs, currentName, normalizedNextName),
            rows: syncRowPositions(nextGroup.rows.map((row, rowIndex) => (
              editedCompositeSubgroup
                ? normalizeRowGroupName(row.group)?.toLowerCase() === currentName?.toLowerCase()
                  ? { ...row, group: normalizedNextName }
                  : row
                : inlineEdit.rowIndices.includes(rowIndex)
                  ? { ...row, group: normalizedNextName }
                  : row
            ))),
          }
          return next
        })
      }
      setUndoToast({ message: 'Group updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
      setHasChanges(true)
      setInlineEdit(null)
      return true
    } catch (error) {
      console.error('Failed to commit inline edit', error)
      await showErrorModal({
        title: 'Unable to Save Changes',
        description: 'The change could not be saved. The original value was restored.',
        confirmText: 'OK',
      })
      setInlineEdit(null)
      return false
    }
  }

  commitInlineEditRef.current = commitInlineEdit

  function handleInlineEditBlur() {
    if (inlineEditBlurActionRef.current) {
      inlineEditBlurActionRef.current = null
      return
    }

    void commitInlineEdit()
  }

  function handleInlineEditKeyDown(event: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) {
    if (event.key === 'Escape') {
      event.preventDefault()
      inlineEditBlurActionRef.current = 'cancel'
      cancelInlineEdit()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      inlineEditBlurActionRef.current = 'commit'
      void commitInlineEdit()
      return
    }

    if (event.key === 'Tab') {
      event.preventDefault()
      void commitInlineEdit()
    }
  }

  useEffect(() => {
    if (!inlineEdit) return

    const handleDocumentMouseDown = (event: MouseEvent) => {
      const input = inlineEditInputRef.current
      const target = event.target as Node | null

      if (!input || !target) return
      if (input === target || input.contains(target)) return

      void commitInlineEditRef.current()
    }

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (document.activeElement === inlineEditInputRef.current) return

      event.preventDefault()
      inlineEditBlurActionRef.current = 'cancel'
      setInlineEdit(null)
    }

    document.addEventListener('mousedown', handleDocumentMouseDown, true)
    document.addEventListener('keydown', handleDocumentKeyDown, true)

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true)
      document.removeEventListener('keydown', handleDocumentKeyDown, true)
    }
  }, [inlineEdit])

  function handleOptionCellClick(row: TableRow) {
    if (isPreviewMode) return
    if (hasPendingRowChanges) return
    if (row.isNewRow) return
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setInsertTarget({ gIdx: row.gIdx, rIdx: row.rIdx })
  }

  function handlePositionDoubleClick(gIdx: number, e: React.MouseEvent<HTMLElement>) {
    if (isPreviewMode) return
    const target = e.target as HTMLElement | null
    if (target?.closest('button') || target?.closest('input')) return
    if (!canMutateCodeSheet || hasPendingRowChanges) return

    const group = groups[gIdx]
    if (!group) return

    e.stopPropagation()

    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(gIdx)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setAddMenuAnchor(null)
    setCodeItemEditorTarget({ mode: 'edit', gIdx })
  }

  function applyEditedCodeItem(gIdx: number, value: CodeItemEditorValue) {
    if (!canMutateCodeSheet) return
    const group = groups[gIdx]
    if (!group) return

    const nextDigits = Math.max(1, Math.floor(value.digits) || 1)
    const currentDigits = getGroupDigits(group)
    const currentAuthoringMode: CodeItemAuthoringMode = isCompositeCodeItem(group) ? 'composite' : 'simple'
    const nextAuthoringMode = value.authoringMode
    const normalizedSubgroups = nextAuthoringMode === 'composite'
      ? normalizeCompositeSubgroups(value.subgroups, nextDigits)
      : []
    if (currentAuthoringMode === 'simple' && nextAuthoringMode === 'composite' && group.rows.length > 0) return
    if (currentAuthoringMode === 'composite' && nextAuthoringMode === 'simple' && group.rows.length > 0) return
    if (nextAuthoringMode === 'composite' && getCompositeGroupStructureErrors(normalizedSubgroups, nextDigits).length > 0) return

    const sourceGroup = currentAuthoringMode === nextAuthoringMode
      ? group
      : { ...group, authoringMode: nextAuthoringMode, subgroups: normalizedSubgroups }
    const transformed = transformCodeItemResize(sourceGroup, nextDigits, normalizedSubgroups)
    if (!transformed.result) {
      void showErrorModal({ title: 'Unable to Update Code Item', description: transformed.errors.join('\n'), confirmText: 'OK' })
      return
    }

    const { group: transformedTarget, patternMap } = transformed.result
    const previousByKey = new Map(resolveCodeItemSubgroups(group).map(subgroup => [subgroup.localKey, subgroup]))
    const descriptionMap = new Map<string, string>()
    normalizedSubgroups.forEach(subgroup => {
      const previous = previousByKey.get(subgroup.localKey)
      if (previous && previous.name !== subgroup.name.trim()) descriptionMap.set(previous.name, subgroup.name.trim())
    })

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Code Item updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    applyGroupsUpdate(prev => {
      const resized = nextDigits === currentDigits ? prev : resizeGroupDigits(prev, gIdx, nextDigits)
      return resized.map((item, index) => {
        const baseGroup = index === gIdx
          ? {
              ...transformedTarget,
              title: normalizeGroupTitle(value.title),
              authoringMode: nextAuthoringMode,
              subgroups: normalizedSubgroups,
              codeItemPos: item.codeItemPos,
            }
          : item
        return {
          ...baseGroup,
          rows: baseGroup.rows.map(row => ({
            ...row,
            rule: remapCompositeRulePositions(
              row.rule,
              group.codeItemPos.code,
              new Map<number, number>(),
              patternMap,
              { selective: true, digits: nextDigits, sourceDigits: currentDigits, descriptionMap }
            ),
          })),
        }
      })
    })
    if (nextAuthoringMode === 'composite') {
      const codeItemLocalKey = getCodeItemLocalKey(group, gIdx)
      updateDraftRowGroupsForCodeItem(codeItemLocalKey, () => [])
    }
    setHasChanges(true)
    setCodeItemEditorTarget(null)
  }

  function insertRowAtIndex(gIdx: number, insertIndex: number, rowGroupName: string | null, draftSectionId?: string) {
    if (!canMutateCodeSheet) return
    if (hasPendingRowChanges) return

    const targetGroup = groups[gIdx]
    const compositeSubgroup = targetGroup && isCompositeCodeItem(targetGroup)
      ? positionSubgroups(resolveCodeItemSubgroups(targetGroup), getCodeItemDigits(targetGroup)).find(subgroup => (
          subgroup.name.toLowerCase() === normalizeRowGroupName(rowGroupName)?.toLowerCase()
        ))
      : null
    setUndoToast({ message: 'Row created successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    setNewRowOptionValidation(null)
    setNewRowDescriptionValidation(null)
    applyGroupsUpdate(prev => {
      const next = [...prev]
      const g = { ...next[gIdx] }
      const rows = [...g.rows]
      rows.splice(insertIndex, 0, createNewCodeRow(
        g.codeItemPos.code,
        insertIndex,
        rowGroupName,
        null,
        null
      ))
      g.rows = normalizeRowsForRowGroups(rows)
      next[gIdx] = g
      return next
    })
    if (draftSectionId && (!targetGroup || !isCompositeCodeItem(targetGroup) || Boolean(compositeSubgroup))) {
      const group = groups[gIdx]
      if (group) {
        updateDraftRowGroupsForCodeItem(getCodeItemLocalKey(group, gIdx), (current) => current.filter((section) => section.id !== draftSectionId))
      }
    }
    setInsertTarget({ gIdx: null, rIdx: null })
    setRowGroupInsertTarget(null)
    setHasChanges(true)
    if (targetGroup && isCompositeCodeItem(targetGroup)) setCompositeOptionTarget({ gIdx, rIdx: insertIndex })
  }

  function addRowBelow(gIdx: number, rIdx: number, rowGroupName?: string | null) {
    const group = groups[gIdx]
    if (!group) return

    const inheritedGroupName = normalizeRowGroupName(rowGroupName ?? group.rows[rIdx]?.group)
    insertRowAtIndex(gIdx, rIdx + 1, inheritedGroupName)
  }

  function updateRowField(
    gIdx: number,
    rIdx: number,
    field: 'optionCode' | 'description' | 'status',
    value: any,
    inputType?: string,
    inputData?: string | null
  ) {
    if (!canMutateCodeSheet) return
    const groupDigits = getGroupDigits(groups[gIdx])
    const currentOptionValue = String(groups[gIdx]?.rows[rIdx]?.optionCode ?? '')
    const optionInputResult = field === 'optionCode'
      ? getLiveOptionCodeInputValue(String(value ?? ''), currentOptionValue, groupDigits, inputType, inputData)
      : null

    if (field === 'optionCode') {
      setNewRowOptionValidation((current) => (
        current?.gIdx === gIdx && current.rIdx === rIdx
          ? null
          : current
      ))
    } else if (field === 'description') {
      setNewRowDescriptionValidation((current) => (
        current?.gIdx === gIdx && current.rIdx === rIdx
          ? null
          : current
      ))
    }

    applyGroupsUpdate(prev => {
      const next = [...prev]
      const g = { ...next[gIdx] }
      const rows = [...g.rows]
      const row = { ...rows[rIdx] }
      if (field === 'optionCode') row.optionCode = optionInputResult?.value || ''
      if (field === 'description') row.description = value === '' ? null : String(value)
      if (field === 'status') row.status = normalizeStatusValue(String(value ?? ''))
      rows[rIdx] = row
      g.rows = rows
      next[gIdx] = g
      return next
    })

    if (field === 'optionCode' && optionInputResult?.removedInvalidCharacters) {
      setNewRowOptionValidation({
        gIdx,
        rIdx,
        error: OPTION_CODE_ALLOWED_CHARACTERS_ERROR,
      })
    }

    setHasChanges(true)
  }

  async function saveNewRow(gIdx: number, rIdx: number): Promise<boolean> {
    if (isPreviewMode) return false
    const group = groups[gIdx]
    if (!group) return false
    const row = group.rows[rIdx]
    if (!row) return false

    const normalizedOptionCode = finalizeOptionCode(row.optionCode, getGroupDigits(group))
    const optionCodeError = getOptionCodeSaveValidationError(row.optionCode, getGroupDigits(group))
    if (optionCodeError) {
      setNewRowOptionValidation({ gIdx, rIdx, error: optionCodeError })
      return false
    }

    if (hasDuplicateOptionCodeInGroup(group, rIdx, normalizedOptionCode)) {
      setNewRowOptionValidation({
        gIdx,
        rIdx,
        error: getDuplicateOptionCodeError(group.codeItemPos.code, normalizedOptionCode),
      })
      return false
    }

    const descriptionError = getDescriptionValidationError(row.description)
    if (descriptionError) {
      setNewRowDescriptionValidation({ gIdx, rIdx, error: descriptionError })
      return false
    }

    const normalizedStatus = normalizeStatusValue(row.status)
    const statusError = getStatusValidationError(normalizedStatus)
    if (statusError) {
      return false
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Row saved successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    setNewRowOptionValidation(null)
    setNewRowDescriptionValidation(null)
    applyGroupsUpdate(prev => {
      const next = [...prev]
      const g = { ...next[gIdx] }
      const rows = [...g.rows]
      const nextRow = {
        ...rows[rIdx],
        optionCode: normalizedOptionCode,
        status: normalizedStatus,
        description: row.description?.trim() || null,
        isNew: false,
      }
      rows[rIdx] = nextRow
      g.rows = syncRowPositions(rows)
      next[gIdx] = g
      return next
    })
    setHasChanges(true)
    return true
  }

  function cancelNewRow(gIdx: number, rIdx: number) {
    if (isPreviewMode) return
    setNewRowOptionValidation((current) => (
      current?.gIdx === gIdx && current.rIdx === rIdx
        ? null
        : current
    ))
    setNewRowDescriptionValidation((current) => (
      current?.gIdx === gIdx && current.rIdx === rIdx
        ? null
        : current
    ))
    const prev = groups
    const group = prev[gIdx]
    if (!group) return
    if (rIdx < 0 || rIdx >= group.rows.length) return
    const draftSections = getCodeItemDraftRowGroups(gIdx)

    if (group.rows.length === 1 && draftSections.length === 0) {
      const next = getGroupsAfterRemovingGroup(prev, gIdx)
      if (!next) return

      addToHistory(prev, hasChanges)
      setUndoToast({ message: 'Row removed' })
      applyGroupsUpdate(next)
      setInsertTarget({ gIdx: null, rIdx: null })
      setGroupInsertTarget(null)
      setRowGroupInsertTarget(null)
      setCodeItemEditorTarget(null)
      setCellSelection({})
      setAnchor({ gIdx: null, rIdx: null, col: null })
      setHasChanges(true)
      return
    }

    addToHistory(prev, hasChanges)
    setUndoToast({ message: 'Row removed' })
    applyGroupsUpdate(prevGroups => {
      const next = [...prevGroups]
      const g = { ...next[gIdx] }
      const rows = [...g.rows]
      rows.splice(rIdx, 1)
      g.rows = normalizeRowsForRowGroups(rows)
      next[gIdx] = g
      return next
    })
    setHasChanges(true)
  }

  function handleNewRowBlur(
    gIdx: number,
    rIdx: number,
    event: React.FocusEvent<HTMLTableRowElement>
  ) {
    const nextFocusedElement = event.relatedTarget as Node | null

    if (nextFocusedElement && event.currentTarget.contains(nextFocusedElement)) {
      return
    }

    if (newRowBlurActionRef.current) {
      newRowBlurActionRef.current = null
      return
    }

    void saveNewRow(gIdx, rIdx)
  }

  function handleNewRowKeyDown(
    gIdx: number,
    rIdx: number,
    event: React.KeyboardEvent<HTMLTableRowElement>
  ) {
    if (event.key === 'Escape') {
      event.preventDefault()
      newRowBlurActionRef.current = 'cancel'
      cancelNewRow(gIdx, rIdx)
      window.setTimeout(() => {
        if (newRowBlurActionRef.current === 'cancel') {
          newRowBlurActionRef.current = null
        }
      }, 0)
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      newRowBlurActionRef.current = 'commit'
      void saveNewRow(gIdx, rIdx)
      window.setTimeout(() => {
        if (newRowBlurActionRef.current === 'commit') {
          newRowBlurActionRef.current = null
        }
      }, 0)
    }
  }

  function handleGroupCellClick(gIdx: number) {
    if (isPreviewMode) return
    if (hasPendingRowChanges) return
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setInsertTarget({ gIdx: null, rIdx: null })
    setRowGroupInsertTarget(null)
    setGroupInsertTarget(prev => (prev === gIdx ? null : gIdx))
    setCodeItemEditorTarget(null)
  }

  function handleRowGroupCellClick(gIdx: number, sectionKey: string) {
    if (isPreviewMode) return
    if (hasPendingRowChanges) return
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setAddMenuAnchor(null)
    setCodeItemEditorTarget(null)
    setRowGroupInsertTarget((current) => (
      current?.gIdx === gIdx && current.sectionKey === sectionKey
        ? null
        : { gIdx, sectionKey }
    ))
  }

  function openGroupPlus(gIdx: number, e: React.MouseEvent<HTMLButtonElement>) {
    if (!canMutateCodeSheet) return
    if (hasPendingRowChanges) return
    e.stopPropagation()
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget({ mode: 'create', insertAfter: gIdx })
  }

  function clearGroupDragState() {
    setDraggedGroup(null)
    setGroupDropTarget(null)
  }

  async function applyCompositeOption(option: { pattern: string; valuePosition: number; valuePositionEnd: number }) {
    if (!compositeOptionTarget) return
    const { gIdx, rIdx } = compositeOptionTarget
    const group = groups[gIdx]
    const row = group?.rows[rIdx]
    if (!group || !row || !isCompositeCodeItem(group)) return

    const normalizedPattern = String(option.pattern || '').trim().toUpperCase()
    if (hasDuplicateOptionCodeInGroup(group, rIdx, normalizedPattern)) {
      setNewRowOptionValidation({ gIdx, rIdx, error: getDuplicateOptionCodeError(group.codeItemPos.code, normalizedPattern) })
      return
    }

    const positionChanged = row.valuePosition !== option.valuePosition || row.valuePositionEnd !== option.valuePositionEnd
    if (!row.isNew && row.optionCode && (row.optionCode !== normalizedPattern || positionChanged)) {
      const referencedValueGuard = getReferencedValueActionGuard([{
        gIdx,
        rIdx,
        codeItemCode: group.codeItemPos.code,
        optionCode: row.optionCode,
      }], 'change')
      if (referencedValueGuard) {
        setCompositeOptionTarget(null)
        await showReferencedValueBlockedModal(
          'Unable to Update Value',
          referencedValueGuard.message,
          referencedValueGuard.items,
          referencedValueGuard.navigationTarget
        )
        return
      }
      addToHistory(groups, hasChanges)
    }

    applyGroupsUpdate(currentGroups => currentGroups.map((currentGroup, currentGroupIndex) => (
      currentGroupIndex !== gIdx
        ? currentGroup
        : {
            ...currentGroup,
            rows: currentGroup.rows.map((currentRow, currentRowIndex) => (
              currentRowIndex === rIdx
                ? {
                    ...currentRow,
                    optionCode: normalizedPattern,
                    valuePosition: option.valuePosition,
                    valuePositionEnd: option.valuePositionEnd,
                  }
                : currentRow
            )),
          }
    )))
    const codeItemLocalKey = getCodeItemLocalKey(group, gIdx)
    const rowGroupName = normalizeRowGroupName(row.group)
    if (rowGroupName) {
      updateDraftRowGroupsForCodeItem(codeItemLocalKey, current => (
        current.filter(section => normalizeRowGroupName(section.name)?.toLowerCase() !== rowGroupName.toLowerCase())
      ))
    }
    setNewRowOptionValidation(null)
    setUndoToast({ message: row.isNew ? 'Option configured' : 'Option updated successfully', ...TEMPORARY_SUCCESS_TOAST_PROPS })
    setHasChanges(true)
    setCompositeOptionTarget(null)
  }

  function commitCompositeGroupStructure(gIdx: number, subgroups: CodeItemSubgroup[], message = 'Composite groups updated successfully') {
    if (!canMutateCodeSheet) return false
    const targetGroup = groups[gIdx]
    if (!targetGroup || !isCompositeCodeItem(targetGroup)) return false
    const transformed = transformCompositeGroupStructure(targetGroup, subgroups)
    if (!transformed.result) {
      void showErrorModal({
        title: 'Unable to Update Groups',
        description: transformed.errors.join('\n'),
        confirmText: 'OK',
      })
      return false
    }

    const { group: transformedTarget, patternMap } = transformed.result
    const digits = getCodeItemDigits(targetGroup)
    const previousByKey = new Map(resolveCodeItemSubgroups(targetGroup).map(subgroup => [subgroup.localKey, subgroup]))
    const descriptionMap = new Map<string, string>()
    subgroups.forEach((subgroup) => {
      const previous = previousByKey.get(subgroup.localKey)
      if (previous && previous.name !== subgroup.name.trim()) descriptionMap.set(previous.name, subgroup.name.trim())
    })
    addToHistory(groups, hasChanges)
    applyGroupsUpdate(currentGroups => currentGroups.map((currentGroup, currentGroupIndex) => {
      const baseGroup = currentGroupIndex === gIdx ? transformedTarget : currentGroup
      return {
        ...baseGroup,
        rows: baseGroup.rows.map(row => ({
          ...row,
          rule: remapCompositeRulePositions(
            row.rule,
            targetGroup.codeItemPos.code,
            new Map<number, number>(),
            patternMap,
            { selective: true, digits, descriptionMap }
          ),
        })),
      }
    }))
    const codeItemLocalKey = getCodeItemLocalKey(targetGroup, gIdx)
    const subgroupNames = new Set(subgroups.map(subgroup => subgroup.name.trim().toLowerCase()))
    updateDraftRowGroupsForCodeItem(codeItemLocalKey, current => (
      current.filter(section => !subgroupNames.has(section.name.trim().toLowerCase()))
    ))
    setUndoToast({ message, ...TEMPORARY_SUCCESS_TOAST_PROPS })
    setHasChanges(true)
    return true
  }

  function moveCompositeSubgroup(
    gIdx: number,
    subgroupKey: string,
    destination: -1 | 1 | { targetIndex: number }
  ) {
    const targetGroup = groups[gIdx]
    if (!targetGroup || !isCompositeCodeItem(targetGroup)) return
    const currentSubgroups = resolveCodeItemSubgroups(targetGroup)
    const fromIndex = currentSubgroups.findIndex(subgroup => subgroup.localKey === subgroupKey)
    const toIndex = typeof destination === 'number' ? fromIndex + destination : destination.targetIndex
    if (fromIndex < 0 || toIndex < 0 || toIndex >= currentSubgroups.length) return
    if (fromIndex === toIndex) return

    const nextSubgroups = [...currentSubgroups]
    const [movedSubgroup] = nextSubgroups.splice(fromIndex, 1)
    nextSubgroups.splice(toIndex, 0, movedSubgroup)
    commitCompositeGroupStructure(gIdx, nextSubgroups, 'Subgroup reordered successfully')
  }

  function setHoveredGroupReorderMatch(gIdx: number) {
    setHoveredGroupReorder(gIdx)
  }

  function clearHoveredGroupReorderMatch(gIdx: number) {
    setHoveredGroupReorder(current => (current === gIdx ? null : current))
  }

  function handleGroupDragStart(gIdx: number, event: React.DragEvent<HTMLButtonElement>) {
    if (!canMutateCodeSheet) {
      event.preventDefault()
      return
    }
    if (hasPendingRowChanges) {
      event.preventDefault()
      return
    }

    event.stopPropagation()
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `group:${gIdx}`)

    setHoveredGroupReorder(gIdx)
    setDraggedGroup(gIdx)
    setGroupDropTarget(null)
    clearRowDragState()
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setAddMenuAnchor(null)
  }

  function handleGroupDragOver(gIdx: number, event: React.DragEvent<HTMLTableCellElement>) {
    if (draggedGroup === null) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const position = getRowDropPosition(event)
    setGroupDropTarget(prev => {
      if (prev?.gIdx === gIdx && prev.position === position) {
        return prev
      }

      return { gIdx, position }
    })
  }

  function handleGroupDrop(gIdx: number, event: React.DragEvent<HTMLTableCellElement>) {
    if (!canMutateCodeSheet) {
      clearGroupDragState()
      return
    }
    if (draggedGroup === null) {
      clearGroupDragState()
      return
    }

    event.preventDefault()

    const position = groupDropTarget?.gIdx === gIdx
      ? groupDropTarget.position
      : getRowDropPosition(event)

    const rawTargetIndex = position === 'before' ? gIdx : gIdx + 1
    const nextTargetIndex = draggedGroup < rawTargetIndex ? rawTargetIndex - 1 : rawTargetIndex

    if (nextTargetIndex === draggedGroup) {
      clearGroupDragState()
      return
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Code Item reordered successfully' })
    applyGroupsUpdate(prev => reorderCodeItemGroups(prev, draggedGroup, nextTargetIndex))
    setHasChanges(true)
    clearGroupDragState()
  }

  function handleGroupDragEnd() {
    setHoveredGroupReorder(null)
    clearGroupDragState()
  }

  function clearRowDragState() {
    setDraggedRow(null)
    setRowDropTarget(null)
  }

  function setHoveredReorderRowMatch(gIdx: number, rIdx: number) {
    setHoveredReorderRow({ gIdx, rIdx })
  }

  function clearHoveredReorderRowMatch(gIdx: number, rIdx: number) {
    setHoveredReorderRow(current => (
      current?.gIdx === gIdx && current.rIdx === rIdx
        ? null
        : current
    ))
  }

  function getRowDropPosition(event: React.DragEvent<HTMLElement>): RowDropPosition {
    const rect = event.currentTarget.getBoundingClientRect()
    return event.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
  }

  function handleRowDragStart(row: TableRow, event: React.DragEvent<HTMLButtonElement>) {
    if (!canMutateCodeSheet) {
      event.preventDefault()
      return
    }
    if (hasPendingRowChanges || row.isNewRow) {
      event.preventDefault()
      return
    }

    event.stopPropagation()
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', `${row.gIdx}:${row.rIdx}`)

    clearGroupDragState()
    setDraggedRow({ gIdx: row.gIdx, rIdx: row.rIdx })
    setRowDropTarget(null)
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCodeItemEditorTarget(null)
    setCellSelection({})
    setAnchor({ gIdx: null, rIdx: null, col: null })
    setAddMenuAnchor(null)
  }

  function handleRowDragOver(row: TableRow, event: React.DragEvent<HTMLTableCellElement>) {
    if (!draggedRow || draggedRow.gIdx !== row.gIdx) return

    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'

    const position = getRowDropPosition(event)
    setRowDropTarget(prev => {
      if (prev?.gIdx === row.gIdx && prev.rIdx === row.rIdx && prev.position === position) {
        return prev
      }

      return { gIdx: row.gIdx, rIdx: row.rIdx, position }
    })
  }

  function moveDraggedRowToTarget(gIdx: number, rawTargetIndex: number, nextRowGroup: string | null) {
    if (!canMutateCodeSheet) {
      clearRowDragState()
      return
    }
    if (!draggedRow || draggedRow.gIdx !== gIdx) {
      clearRowDragState()
      return
    }

    const targetGroup = groups[gIdx]
    if (!targetGroup) {
      clearRowDragState()
      return
    }

    if (
      isCompositeCodeItem(targetGroup)
      && normalizeRowGroupName(targetGroup.rows[draggedRow.rIdx]?.group)?.toLowerCase() !== normalizeRowGroupName(nextRowGroup)?.toLowerCase()
    ) {
      clearRowDragState()
      return
    }

    const nextTargetIndex = draggedRow.rIdx < rawTargetIndex ? rawTargetIndex - 1 : rawTargetIndex
    const currentRowGroup = normalizeRowGroupName(targetGroup.rows[draggedRow.rIdx]?.group)
    const normalizedNextRowGroup = normalizeRowGroupName(nextRowGroup)

    if (nextTargetIndex === draggedRow.rIdx && currentRowGroup === normalizedNextRowGroup) {
      clearRowDragState()
      return
    }

    addToHistory(groups, hasChanges)
    setUndoToast({ message: 'Row reordered successfully' })
    applyGroupsUpdate(prev => {
      const next = [...prev]
      const group = next[gIdx]
      if (!group) return prev

      next[gIdx] = {
        ...group,
        rows: moveRowWithinGroupSections(group.rows, draggedRow.rIdx, nextTargetIndex, normalizedNextRowGroup),
      }

      return next
    })
    setHasChanges(true)
    clearRowDragState()
  }

  function handleRowDrop(row: TableRow, event: React.DragEvent<HTMLTableCellElement>) {
    if (!draggedRow || draggedRow.gIdx !== row.gIdx) {
      clearRowDragState()
      return
    }

    event.preventDefault()

    const position = rowDropTarget?.gIdx === row.gIdx && rowDropTarget.rIdx === row.rIdx
      ? rowDropTarget.position
      : getRowDropPosition(event)

    const rawTargetIndex = position === 'before' ? row.rIdx : row.rIdx + 1
    moveDraggedRowToTarget(row.gIdx, rawTargetIndex, row.rowGroup)
  }

  function handleRowDragEnd() {
    clearRowDragState()
  }

  function handleCellClick(row: TableRow, col: ColumnKey, e: React.MouseEvent) {
    if (isPreviewMode) return
    if (hasPendingRowChanges) return
    const id = `${row.gIdx}:${row.rIdx}:${col}`
    setInsertTarget({ gIdx: null, rIdx: null })
    setGroupInsertTarget(null)
    setRowGroupInsertTarget(null)
    setCellSelection(prev => {
      const next: Record<string, boolean> = { ...prev }
      if (e.shiftKey && anchor.gIdx === row.gIdx && anchor.col === col && anchor.rIdx !== null) {
        for (const k of Object.keys(next)) {
          const [gStr, , c] = k.split(':')
          if (Number(gStr) !== row.gIdx || (c as ColumnKey) !== col) delete next[k]
        }
        const start = anchor.rIdx as number
        const end = row.rIdx
        const s = Math.min(start, end)
        const ee = Math.max(start, end)
        for (let r = s; r <= ee; r++) next[`${row.gIdx}:${r}:${col}`] = true
        return next
      }
      for (const k of Object.keys(next)) {
        const [gStr, , c] = k.split(':')
        if (Number(gStr) !== row.gIdx || (c as ColumnKey) !== col) delete next[k]
      }
      next[id] = !next[id]
      return next
    })
    setAnchor({ gIdx: row.gIdx, rIdx: row.rIdx, col })
  }

  useEffect(() => {
    if (hasEffectiveChanges) setPublished(false)
  }, [hasEffectiveChanges])

  async function showBlockingOptionValidationError(action: 'save' | 'submit') {
    if (firstBrokenRuleReferenceIssue) {
      const navigationTarget = firstBrokenRuleReferenceIssue.sourceGroupIndex === undefined || firstBrokenRuleReferenceIssue.sourceRowIndex === undefined
        ? null
        : {
            gIdx: firstBrokenRuleReferenceIssue.sourceGroupIndex,
            rIdx: firstBrokenRuleReferenceIssue.sourceRowIndex,
          }

      await showReferencedValueBlockedModal(
        action === 'save' ? 'Unable to Save Changes' : 'Unable to Submit for Review',
        getMissingRuleValueErrorMessage(firstBrokenRuleReferenceIssue),
        brokenRuleReferenceIssues.map((issue, issueIndex) => ({
          id: `broken-rule:${issueIndex}`,
          description: buildBrokenRuleReferenceDescription(issue),
          navigationTarget: issue.sourceGroupIndex === undefined || issue.sourceRowIndex === undefined
            ? null
            : {
                gIdx: issue.sourceGroupIndex,
                rIdx: issue.sourceRowIndex,
              },
        })),
        navigationTarget,
        'Go to blocking rule'
      )
      return
    }

    if (blockingRuleValidationIssues.length > 0) {
      const firstRuleIssue = blockingRuleValidationIssues[0]
      await showReferencedValueBlockedModal(
        action === 'save' ? 'Unable to Save Changes' : 'Unable to Submit for Review',
        buildRuleValidationIssueDescription(firstRuleIssue),
        blockingRuleValidationIssues.map((issue, issueIndex) => ({
          id: `invalid-rule:${issueIndex}`,
          description: buildRuleValidationIssueDescription(issue),
          navigationTarget: { gIdx: issue.gIdx, rIdx: issue.rIdx },
        })),
        { gIdx: firstRuleIssue.gIdx, rIdx: firstRuleIssue.rIdx },
        'Go to blocking rule'
      )
      return
    }

    if (invalidPositionableRuleIssues.length > 0) {
      const firstIssue = invalidPositionableRuleIssues[0]
      await showReferencedValueBlockedModal(
        action === 'save' ? 'Unable to Save Changes' : 'Unable to Submit for Review',
        firstIssue.reason,
        invalidPositionableRuleIssues.map((issue, issueIndex) => ({
          id: `invalid-position-rule:${issueIndex}`,
          description: issue.reason,
          navigationTarget: { gIdx: issue.sourceGroupIndex, rIdx: issue.sourceRowIndex },
        })),
        { gIdx: firstIssue.sourceGroupIndex, rIdx: firstIssue.sourceRowIndex },
        'Go to blocking rule'
      )
      return
    }

    await showErrorModal({
      title: action === 'save' ? 'Unable to Save Changes' : 'Unable to Submit for Review',
      description: blockingValidationReason || 'Fix invalid Options before continuing.',
      confirmText: 'OK',
    })
  }

  // Rules the author did not touch only warn, and the report opens the first time it has something
  // new to say: repeating it on every save attempt only gets in the way. Saving is the moment worth
  // the full chain search over the whole sheet, so it runs here rather than on every edit.
  function collectAdvisoryRuleValidationIssues(): RuleValidationIssue[] {
    const issues = collectRuleValidationIssues(true).filter(issue => (
      issue.severity === 'warning' || !touchedRuleKeys.has(issue.ruleKey)
    ))
    if (issues.every(issue => reportedAdvisoryRuleKeys.has(issue.ruleKey))) return []
    return issues
  }

  async function showAdvisoryRuleValidationModal(issues: RuleValidationIssue[]): Promise<boolean> {
    setReportedAdvisoryRuleKeys((previous) => {
      const next = new Set(previous)
      issues.forEach(issue => next.add(issue.ruleKey))
      return next
    })

    const firstIssue = issues[0]
    return showReferencedValueBlockedModal(
      'Review Existing Rules',
      buildRuleValidationIssueDescription(firstIssue),
      issues.map((issue, issueIndex) => ({
        id: `advisory-rule:${issueIndex}`,
        description: buildRuleValidationIssueDescription(issue),
        navigationTarget: { gIdx: issue.gIdx, rIdx: issue.rIdx },
      })),
      { gIdx: firstIssue.gIdx, rIdx: firstIssue.rIdx },
      'Go to blocking rule'
    )
  }

  async function handleSaveChanges() {
    if (!(revisionTarget ? reviseChangeAccess.allowed : saveChangesAccess.allowed) || isPreviewMode || !hasEffectiveChanges || !id || hasPendingRowChanges) return
    if (!activeProjectId) return
    if (hasBlockingValidationErrors) {
      await showBlockingOptionValidationError('save')
      return
    }

    const advisoryIssues = collectAdvisoryRuleValidationIssues()
    if (advisoryIssues.length > 0 && await showAdvisoryRuleValidationModal(advisoryIssues)) return

    if (isInitialMainSetup) {
      const confirmed = await showConfirmModal({
        title: 'Save Initial Code Sheet?',
        description: 'Once these changes are saved, you will no longer be able to edit the live Code Sheet directly. Future changes must be made through a project and submitted for approval. Do you want to continue?',
        confirmText: 'Save Changes',
        cancelText: 'Cancel',
      })
      if (!confirmed) return
    }

    try {
      setSaving(true)
      showLoadingModal({
        title: 'Saving Changes',
        message: 'Please wait while your changes are being saved...',
      })
      let response: PublishFormatResponse | BranchTree | undefined
      if (revisionTarget) {
        const targetElementId = revisionTree?.nodes.find(node => node.id === revisionTarget.nodeId)?.elementId
        if (!targetElementId) {
          throw new Error('The rejected change no longer maps to a node in this project.')
        }
        if (getCodeSheetStructureSignature(originalGroups) !== getCodeSheetStructureSignature(latestGroupsRef.current)) {
          throw new Error('Rejected-change revisions cannot add, delete, or reorder Code Sheet content.')
        }

        const revisionRequest = buildBranchTreeUpdateRequest(originalGroups, latestGroupsRef.current)
        if (revisionRequest.updates.length === 0) {
          throw new Error('Update the rejected item before saving the revision.')
        }
        if (revisionRequest.updates.some(update => update.elementId !== targetElementId)) {
          throw new Error('Only the item covered by the selected rejected change can be revised.')
        }
        if (revisionTarget.path) {
          const path = revisionTarget.path
          const optionAlias = path === 'value' ? 'name' : path === 'name' ? 'value' : null
          if (revisionRequest.updates.some(update => Object.keys(update.properties).some(key => key !== path && key !== optionAlias))) {
            throw new Error('Only the field covered by the selected rejected change can be revised.')
          }
          revisionRequest.updates = revisionRequest.updates.map(update => ({
            ...update, properties: Object.fromEntries(Object.entries(update.properties).filter(([key]) => key === path)),
          })).filter(update => Object.keys(update.properties).length > 0)
          if (revisionRequest.updates.length === 0) throw new Error('Update the selected rejected field before saving.')
        }
        response = await updateProjectBranchTree(id, activeProjectId, {
          ...revisionRequest,
          changeId: revisionTarget.id,
        })
        setOriginalGroups(cloneState(latestGroupsRef.current))
        setRevisionSaved(true)
        setActiveDiffEntryKey(null)
        setActiveChangeFocusTone('navigation')
      } else {
        const payload = buildCodeItemsPublishPayload(latestGroupsRef.current as any)
        response = await publishCodeIndexCodeItems(id, payload, activeProjectId)
      }
      setHasChanges(false)
      setCodesheetSavedAt(current => current + 1)
      setTouchedRuleKeys(new Set<string>())
      // The check result rides on the save response; a revision save does not reload the project.
      const savedReport = response?.unselectableValues ?? null
      if (savedReport) setUnselectableReport(savedReport)
      const savedAppendixReport = response?.appendixConsistency ?? null
      if (savedAppendixReport) setAppendixConsistencyReport(savedAppendixReport)
      const savedChecksDescription = getSavedChecksDescription(savedReport, savedAppendixReport)
      if (!revisionTarget && isInitialMainSetup) {
        allowNavigationWithoutPrompt()
        navigate(`/details/${encodeURIComponent(id)}`, { replace: true })
      } else if (!revisionTarget) {
        await loadData()
      }
      await loadHeader()
      closeLoadingModal()
      await showSuccessModal({
        title: 'Changes Saved',
        description: savedChecksDescription
          ?? (getApiMessage(response) || 'Changes saved successfully.'),
        confirmText: 'OK',
      })
    } catch (e) {
      console.error('Failed to save changes', e)
      logSystemError(user, {
        action: 'save_codesheet_changes',
        error: e,
        resource: 'codesheet',
        resourceId: id,
        context: activeProjectId ? { projectId: activeProjectId } : undefined,
      })
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Save Changes',
        description: getApiMessage(e) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleOpenSubmitForReviewDialog() {
    if (!isProjectMode) return
    if (!submitChangesAccess.allowed || isPreviewMode || !id || !activeProjectId || !activeProject || hasPendingRowChanges) return
    if (pageMutationDisabledReason) return
    if (hasBlockingValidationErrors) {
      await showBlockingOptionValidationError('submit')
      return
    }
    if (outstandingUnselectableReason) {
      await showErrorModal({
        title: 'Unable to Submit for Review',
        description: outstandingUnselectableReason,
        confirmText: 'OK',
      })
      return
    }

    // Appendices pointing at code items or values that no longer exist block the submit (the server checks again).
    const incompleteAppendices = appendixSummaries.filter(appendix => appendix.unresolvedCount > 0)
    if (incompleteAppendices.length > 0) {
      setUnresolvedAppendices(incompleteAppendices.map(appendix => ({
        appendixKey: appendix.key || appendix.id,
        appendixId: appendix.id,
        title: appendix.title,
        letter: appendix.letter,
        unresolvedCount: appendix.unresolvedCount,
      })))
      return
    }

    const advisoryIssues = collectAdvisoryRuleValidationIssues()
    if (advisoryIssues.length > 0 && await showAdvisoryRuleValidationModal(advisoryIssues)) return

    const projectSubmitReason = getProjectSubmitForReviewReason(activeProject, hasEffectiveChanges)
    if (projectSubmitReason) {
      await showErrorModal({
        title: 'Unable to Submit for Review',
        description: projectSubmitReason,
        confirmText: 'OK',
      })
      return
    }

    setSubmitForReviewOpen(true)
  }

  async function handleSubmitProjectForReview(description: string) {
    if (!isProjectMode) return
    if (!submitChangesAccess.allowed || isPreviewMode || !id || !activeProjectId || !activeProject || hasPendingRowChanges) return
    if (pageMutationDisabledReason) return
    if (!description.trim()) return

    try {
      setSaving(true)
      setSubmitForReviewOpen(false)
      showLoadingModal({
        title: 'Submitting for Review',
        message: 'Please wait while the project is being submitted for review...',
      })

      await createPullRequest(id, {
        sourceProjectId: activeProject.id,
        targetProjectId: null,
        title: activeProject.name || activeProject.id,
        description: description.trim(),
      })

      closeLoadingModal()
      await showSuccessModal({
        title: 'Project Submitted',
        description: 'Project submitted for review.',
        confirmText: 'OK',
      })

      await loadData()
      await loadHeader()
    } catch (e) {
      console.error('Failed to submit project for review', e)
      logSystemError(user, {
        action: 'submit_project_for_review',
        error: e,
        resource: 'codesheet',
        resourceId: id,
        context: activeProjectId ? { projectId: activeProjectId } : undefined,
      })

      // The server re-ran the check and found values still flagged; show them like a save would.
      const rejectedReport = extractUnselectableValuesReport(e)
      if (rejectedReport) setUnselectableReport(rejectedReport)
      const rejectedAppendixReport = extractAppendixConsistencyReport(e)
      if (rejectedAppendixReport) setAppendixConsistencyReport(rejectedAppendixReport)

      closeLoadingModal()
      // Appendices with entries that no longer resolve are listed so each one can be opened and fixed.
      const unresolved = extractUnresolvedAppendices(e)
      if (unresolved) {
        setUnresolvedAppendices(unresolved)
        return
      }
      await showErrorModal({
        title: 'Unable to Submit for Review',
        description: getApiMessage(e) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const focusCodeSheetChange = useCallback((change: Change, tone: 'navigation' | 'revision') => {
    setSearchQuery('')
    setActiveChangeFocusTone(tone)
    setActiveChangeFocusRequest(current => current + 1)
    setActiveDiffEntryKey(change.nodeId
      ? codeSheetDiff.entryKeyByTargetDomainId.get(change.nodeId) || null
      : null)
  }, [codeSheetDiff.entryKeyByTargetDomainId])

  // An appendix change is revised in the appendix editor, not in the code sheet table.
  function openAppendixChangeEditor(change: Change, mode: 'readonly' | 'revision'): boolean {
    const summary = change.nodeId ? appendixSummariesByKey.get(change.nodeId) : undefined
    if (!summary) {
      void showErrorModal({
        title: mode === 'revision' ? 'Unable to Revise Change' : 'Appendix Not Found',
        description: 'This appendix is no longer in the project.',
        confirmText: 'OK',
      })
      return false
    }
    setAppendixReviewEditor({ appendixId: summary.id, mode, change })
    return true
  }

  async function handleAppendixRevisionSaved() {
    const target = appendixReviewEditor
    setAppendixReviewEditor(null)
    if (!id || !target || target.mode !== 'revision') return

    try {
      setSaving(true)
      showLoadingModal({ title: 'Resubmitting Change', message: 'Sending the revised appendix back for review...' })
      await resubmitPullRequestChange(id, target.change.pullRequestId, target.change.id)
      setChangesRequestedRefreshKey(previous => previous + 1)
      closeLoadingModal()
      await Promise.all([loadData(), loadHeader()])
      await showSuccessModal({
        title: 'Change Resubmitted',
        description: 'The revised appendix is pending review again.',
        confirmText: 'OK',
      })
    } catch (error) {
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Resubmit Change',
        description: getApiMessage(error) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleStartRejectedChangeRevision(change: Change) {
    if (!id || !activeProjectId || !reviseChangeAccess.allowed || !change.nodeId) return

    if (change.nodeType === 'Appendix') {
      openAppendixChangeEditor(change, 'revision')
      return
    }

    try {
      const tree = await fetchProjectBranchTree(id, activeProjectId)
      if (!tree.nodes.some(node => node.id === change.nodeId)) {
        throw new Error('The rejected change no longer maps to a node in this project.')
      }
      setRevisionTarget(change)
      setRevisionTree(tree)
      setRevisionSaved(false)
      setHasChanges(false)
      setOriginalGroups(cloneState(latestGroupsRef.current))
      focusCodeSheetChange(change, 'revision')
    } catch (error) {
      await showErrorModal({
        title: 'Unable to Revise Change',
        description: getApiMessage(error) || (error instanceof Error ? error.message : 'Action failed. Please try again.'),
        confirmText: 'OK',
      })
    }
  }

  async function handleResubmitRejectedChange() {
    if (!id || !revisionTarget || hasEffectiveChanges || !revisionSaved) return

    try {
      setSaving(true)
      showLoadingModal({ title: 'Resubmitting Change', message: 'Sending the revised change back for review...' })
      await resubmitPullRequestChange(id, revisionTarget.pullRequestId, revisionTarget.id)
      setChangesRequestedRefreshKey(previous => previous + 1)
      setRevisionTarget(null)
      setRevisionTree(null)
      setRevisionSaved(false)
      closeLoadingModal()
      await Promise.all([loadData(), loadHeader()])
      await showSuccessModal({
        title: 'Change Resubmitted',
        description: 'The same change request is pending review again.',
        confirmText: 'OK',
      })
    } catch (error) {
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Resubmit Change',
        description: getApiMessage(error) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleCancelProject() {
    if (!id || !activeProjectId || !activeProject || !cancelProjectAccess.allowed) return

    try {
      const preview = await fetchProjectCancellationPreview(id, activeProjectId)
      if (!preview.canCancel) {
        throw new Error(`Project cannot be cancelled while it is ${preview.currentStatus}.`)
      }
      const confirmed = await showConfirmModal({
        title: 'Cancel Project',
        description: `Cancel "${preview.projectName}" with ${preview.changeCount} active change${preview.changeCount === 1 ? '' : 's'}? The project will remain available as read-only history.`,
        confirmText: 'Cancel Project',
        cancelText: 'Keep Project',
      })
      if (!confirmed) return

      setSaving(true)
      showLoadingModal({ title: 'Cancelling Project', message: 'Closing the project and its review request...' })
      await cancelProject(id, activeProjectId, preview.changeCount)
      setRevisionTarget(null)
      setRevisionTree(null)
      setRevisionSaved(false)
      closeLoadingModal()
      await Promise.all([loadData(), loadHeader()])
      await showSuccessModal({
        title: 'Project Cancelled',
        description: 'The project is now read-only and remains available in project history.',
        confirmText: 'OK',
      })
    } catch (error) {
      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Cancel Project',
        description: getApiMessage(error) || (error instanceof Error ? error.message : 'Action failed. Please try again.'),
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  async function handleMergeToMain() {
    // TEMP: Merge_Changeset UI validation is bypassed while Test environment role data is corrected.
    if (mergeToMainDisabledReason || !isActiveProjectReadyToMerge || !id || !activeProjectId || !activeProject?.id) return

    try {
      setSaving(true)
      showLoadingModal({
        title: 'Merging Project',
        message: 'Please wait while the approved changes are merged to Main and made live...',
      })

      const pullRequests = await fetchPullRequests(id, activeProject.id)
      const pullRequest = findReadyToMergePullRequest(activeProject.id, pullRequests)

      if (!pullRequest?.id) {
        closeLoadingModal()
        await showErrorModal({
          title: 'Unable to Merge Project',
          description: 'No approved pull request was found for this project.',
          confirmText: 'OK',
        })
        return
      }

      await mergePullRequest(id, pullRequest.id)

      closeLoadingModal()
      await showSuccessModal({
        title: 'Project Merged',
        description: 'Project merged to Main and is now live to all users.',
        confirmText: 'OK',
      })
      navigate(`/details/${encodeURIComponent(id)}`, {
        replace: true,
        state: { mergedToMain: true, codeIndexId: id },
      })
    } catch (e) {
      console.error('Failed to merge project to main', e)
      logSystemError(user, {
        action: 'merge_project_to_main',
        error: e,
        resource: 'codesheet',
        resourceId: id,
        context: activeProjectId ? { projectId: activeProjectId } : undefined,
      })

      closeLoadingModal()
      await showErrorModal({
        title: 'Unable to Merge Project',
        description: getApiMessage(e) || 'Action failed. Please try again.',
        confirmText: 'OK',
      })
    } finally {
      setSaving(false)
    }
  }

  const navigateToProject = useCallback((project: ProjectBranch) => {
    if (!id || !project.id) return
    navigate(`/details/${encodeURIComponent(id)}?projectId=${encodeURIComponent(project.id)}`)
    setProjectsDrawerOpen(false)
  }, [id, navigate])

  const handleProjectCreated = useCallback((project: ProjectBranch) => {
    setCreateProjectOpen(false)
    setProjects((current) => [project, ...current.filter((item) => item.id !== project.id)])
    navigateToProject(project)
  }, [navigateToProject])

  const handleReturnToMainCodesheet = useCallback(() => {
    if (!id) return
    navigate(`/details/${encodeURIComponent(id)}`)
  }, [id, navigate])

  const handleDiffVisibleChange = useCallback((visible: boolean) => {
    setDiffVisible(visible)
    if (!visible) setChangedRowsOnly(false)
  }, [])

  const handleGoToCodeSheetChange = useCallback((change: Change) => {
    if (change.nodeType === 'Appendix') {
      const summary = change.nodeId ? appendixSummariesByKey.get(change.nodeId) : undefined
      if (!summary) {
        void showErrorModal({
          title: 'Appendix Not Found',
          description: 'This appendix is no longer in the project.',
          confirmText: 'OK',
        })
        return
      }
      setAppendixReviewEditor({ appendixId: summary.id, mode: 'readonly', change })
      return
    }
    focusCodeSheetChange(change, 'navigation')
  }, [appendixSummariesByKey, focusCodeSheetChange])

  const resolveAppendixChangeLabel = useCallback((nodeId: string | null | undefined): string | null => {
    const summary = nodeId ? appendixSummariesByKey.get(nodeId) : undefined
    return summary ? formatAppendixName(summary) : null
  }, [appendixSummariesByKey])

  useEffect(() => {
    if (!activeDiffEntryKey || activeChangeFocusTone !== 'navigation') return

    const focusedEntryKey = activeDiffEntryKey
    const timeout = window.setTimeout(() => {
      setActiveDiffEntryKey(current => current === focusedEntryKey ? null : current)
    }, CHANGE_NAVIGATION_FOCUS_DURATION_MS)

    return () => window.clearTimeout(timeout)
  }, [activeChangeFocusRequest, activeChangeFocusTone, activeDiffEntryKey])

  useLayoutEffect(() => {
    if (!activeDiffEntryKey) return
    const frame = window.requestAnimationFrame(() => {
      const scrollContainer = tableScrollRef.current
      const target = Array.from(document.querySelectorAll<HTMLElement>('[data-diff-entry-key]'))
        .find((element) => element.dataset.diffEntryKey === activeDiffEntryKey)
      if (!scrollContainer || !target || !scrollContainer.contains(target)) return

      const containerRect = scrollContainer.getBoundingClientRect()
      const targetRect = target.getBoundingClientRect()
      const centeredTop = scrollContainer.scrollTop
        + targetRect.top
        - containerRect.top
        - ((scrollContainer.clientHeight - targetRect.height) / 2)

      scrollContainer.scrollTo({
        top: Math.max(0, centeredTop),
        behavior: 'smooth',
      })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [activeChangeFocusRequest, activeDiffEntryKey, visibleCodeItemModels])

  const visibleDeletedDiffEntries = useMemo(() => {
    if (!diffReady || !isCodeSheetDiffDisplayed) return []
    const query = searchQuery.trim().toLowerCase()
    return codeSheetDiff.entries
      .filter((entry) => entry.status === 'deleted' && (!query || entry.searchableText.includes(query)))
      .sort((left, right) => {
        const groupOrder = (left.liveGroupIndex ?? Number.MAX_SAFE_INTEGER) - (right.liveGroupIndex ?? Number.MAX_SAFE_INTEGER)
        if (groupOrder !== 0) return groupOrder
        if (left.kind !== right.kind) return left.kind === 'group' ? -1 : 1
        return (left.liveRowIndex ?? -1) - (right.liveRowIndex ?? -1)
      })
  }, [codeSheetDiff.entries, diffReady, isCodeSheetDiffDisplayed, searchQuery])

  const visibleArrivedGroups = useMemo(() => {
    if (!diffReady || !isCodeSheetDiffDisplayed) return []
    const query = searchQuery.trim().toLowerCase()
    return codeSheetDiff.arrivedGroups.filter((entry) => (
      !query
      || entry.searchableText.includes(query)
      || codeSheetDiff.entries.some((candidate) => (
        candidate.kind === 'row'
        && candidate.groupKey === entry.groupKey
        && candidate.arrivedStatus === 'added'
        && candidate.searchableText.includes(query)
      ))
    ))
  }, [codeSheetDiff.arrivedGroups, codeSheetDiff.entries, diffReady, isCodeSheetDiffDisplayed, searchQuery])

  const activeChangeOutlineColor = activeChangeFocusTone === 'revision' ? '#B3261E' : '#1D74D8'

  function renderCodeItemRows(model: CodeItemRenderModel): React.ReactNode[] {
    const { gIdx, group, positionLabel, posRangeLen, titleText, showTitleRow, descMap, availMap, sections, renderedRowCount } = model
    const groupRows = group.rows || []
    const renderedNodes: React.ReactNode[] = []
    let groupIdentityCellsRendered = false
    const leftContentColSpan = 2
    const trailingColSpan = 2
    const codeItemAttributes = getCodeItemAttributeDefinitions(group)
    const groupDiffEntry = diffReady ? codeSheetDiff.groupByProjectIndex.get(gIdx) || null : null
    const groupDiff = isCodeSheetDiffDisplayed ? groupDiffEntry : null
    const groupDiffBackground = groupDiff?.status === 'added'
      ? '#EFFAF2'
      : groupDiff?.status === 'modified'
        ? '#FFF9E8'
        : undefined
    const isFocusedGroupDiff = Boolean(groupDiffEntry && groupDiffEntry.key === activeDiffEntryKey)
    const isFocusedAddedGroupDiff = isFocusedGroupDiff && groupDiffEntry?.status === 'added'

    const isGroupSelected = groupInsertTarget === gIdx
    const isTitleInsertSelected =
      rowGroupInsertTarget?.gIdx === gIdx &&
      rowGroupInsertTarget.sectionKey === TITLE_ROW_INSERT_KEY
    const groupCellClass = `border border-[#d0d7de] py-1.5 px-4 align-middle relative cursor-pointer text-left whitespace-nowrap ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(isGroupSelected)} ${groupInsertTarget === gIdx ? 'border-b-2 border-b-[#1D74D8]' : ''}`
    const positionCellClass = `border border-[#d0d7de] py-1.5 px-2 align-middle text-left whitespace-nowrap cursor-pointer ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(isGroupSelected)}`
    const titleCellClass = `border border-[#d0d7de] py-2 px-4 text-left align-middle relative ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(isGroupSelected || isTitleInsertSelected)} ${hasPendingRowChanges ? '' : 'cursor-pointer'}`
    const sectionCellClass = `border border-[#d0d7de] py-2.5 px-4 text-left align-middle relative ${TABLE_BODY_TYPOGRAPHY} ${hasPendingRowChanges ? '' : 'cursor-pointer'}`
    const groupTrailingCellClass = `border border-[#d0d7de] py-1.5 px-4 ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(isGroupSelected)}`
    const attributeHeaderCellClass = `border border-[#d0d7de] py-1.5 px-3 ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(isGroupSelected)}`

    const isDraggedGroup = draggedGroup === gIdx
    const isGroupDropTarget = groupDropTarget?.gIdx === gIdx && !isDraggedGroup
    const groupDropPosition = isGroupDropTarget ? groupDropTarget.position ?? null : null
    const groupDragHighlightStyle = getGroupDragHighlightStyle(isDraggedGroup)
    const groupDropIndicatorStyle = getRowDropIndicatorStyle(isGroupDropTarget, groupDropPosition)
    const groupCellStyle = {
      ...TABLE_BODY_TYPOGRAPHY_STYLE,
      ...groupDropIndicatorStyle,
      ...groupDragHighlightStyle,
      ...(groupDiffBackground ? { backgroundColor: groupDiffBackground } : {}),
      ...(isFocusedAddedGroupDiff
        ? { boxShadow: `inset 3px 0 0 ${activeChangeOutlineColor}, inset 0 3px 0 ${activeChangeOutlineColor}, inset -3px 0 0 ${activeChangeOutlineColor}, inset 0 -3px 0 ${activeChangeOutlineColor}` }
        : {}),
    }
    const positionCellStyle = { ...TABLE_BODY_TYPOGRAPHY_STYLE, ...groupDropIndicatorStyle, ...groupDragHighlightStyle, ...(groupDiffBackground ? { backgroundColor: groupDiffBackground } : {}) }
    const titleCellStyle = { ...TABLE_BODY_TYPOGRAPHY_STYLE, ...groupDragHighlightStyle, ...(groupDiffBackground ? { backgroundColor: groupDiffBackground } : {}) }
    const sectionCellStyle = { ...TABLE_BODY_TYPOGRAPHY_STYLE, ...groupDragHighlightStyle }
    const groupTrailingSurfaceStyle = {
      ...TABLE_BODY_TYPOGRAPHY_STYLE,
      ...groupDragHighlightStyle,
      ...(groupDiffBackground ? { backgroundColor: groupDiffBackground } : {}),
    }
    const canDragGroup = canModifyCodeSheetStructure && !hasPendingRowChanges
    const isGroupHandleVisible = canDragGroup && (isDraggedGroup || hoveredGroupReorder === gIdx)
    const canOpenCodeItemActions = canModifyCodeSheetStructure && !hasPendingRowChanges
    const scopedAttributeGridStyle = (attributes: CodeItemAttributeDefinition[]): React.CSSProperties => ({
      gridTemplateColumns: `repeat(${Math.max(attributes.length, 1)}, minmax(72px, 1fr))`,
      columnGap: '8px',
    })
    const getAttributeHeaderCellStyle = (hasScopedAttributes: boolean) => ({
      ...groupTrailingSurfaceStyle,
      ...(hasScopedAttributes ? { backgroundColor: '#f6f8fa' } : {}),
    })
    const renderScopedAttributeHeaders = (
      attributes: CodeItemAttributeDefinition[],
      reserveCodeItemActionsSpace = false
    ) => {
      if (!hasAttributeColumn || attributes.length === 0) return null

      return (
        <div
          className="grid w-full"
          style={scopedAttributeGridStyle(attributes)}
        >
          {attributes.map((attribute, attributeIndex) => (
            <div
              key={attribute.id}
              className={`min-w-0 py-1.5 text-left text-gray-900 ${TABLE_HEADER_TYPOGRAPHY} ${reserveCodeItemActionsSpace && attributeIndex === 0 ? 'pl-8 pr-2' : 'px-2'} ${attributeIndex > 0 ? 'border-l border-[#d0d7de]' : ''}`}
              title={getAttributeDisplayLabel(attribute)}
            >
              <span className="block truncate">{getAttributeDisplayLabel(attribute)}</span>
            </div>
          ))}
        </div>
      )
    }
    const renderCodeItemActionsButton = () => (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation()
          setCodeItemActionMenu({ element: event.currentTarget, gIdx })
        }}
        disabled={!canOpenCodeItemActions}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded border border-transparent text-gray-400 transition-colors ${
          canOpenCodeItemActions
            ? 'hover:border-[#d0d7de] hover:bg-[#f6f8fa] hover:text-[#1D74D8]'
            : 'cursor-not-allowed opacity-40'
        }`}
        title="Code Item actions"
        aria-label="Code Item actions"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
    )
    const renderAttributeHeaderWithActions = (attributes: CodeItemAttributeDefinition[]) => {
      const scopedHeaders = renderScopedAttributeHeaders(attributes, true)

      if (!scopedHeaders) {
        return renderCodeItemActionsButton()
      }

      return (
        <div className="relative w-full min-w-0">
          <div data-pdf-hide className="absolute left-0 top-1/2 z-[1] -translate-y-1/2">
            {renderCodeItemActionsButton()}
          </div>
          {scopedHeaders}
        </div>
      )
    }

    const renderGroupIdentityCells = () => {
      groupIdentityCellsRendered = true

      return (
        <React.Fragment>
          <td
            rowSpan={renderedRowCount}
            data-diff-entry-key={groupDiffEntry?.status === 'added' ? groupDiffEntry.key : undefined}
            className={groupCellClass}
            style={groupCellStyle}
            onDragOver={(e) => handleGroupDragOver(gIdx, e)}
            onDrop={(e) => handleGroupDrop(gIdx, e)}
            onMouseEnter={() => setHoveredGroupReorderMatch(gIdx)}
            onMouseLeave={() => clearHoveredGroupReorderMatch(gIdx)}
            onClick={() => handleGroupCellClick(gIdx)}
          >
            <div className="flex gap-1.5 items-center">
              <button
                type="button"
                draggable={canDragGroup}
                onDragStart={(e) => handleGroupDragStart(gIdx, e)}
                onDragEnd={handleGroupDragEnd}
                onClick={(e) => e.stopPropagation()}
                className={`flex h-6 w-6 items-center justify-center rounded border border-transparent text-gray-400 transition-all ${canDragGroup ? (isGroupHandleVisible ? 'opacity-100' : 'pointer-events-none opacity-0') : 'pointer-events-none opacity-0'} ${canDragGroup ? 'cursor-grab hover:border-[#d0d7de] hover:bg-[#f6f8fa] hover:text-[#1D74D8] active:cursor-grabbing' : 'cursor-not-allowed'}`}
                title={canDragGroup ? 'Drag to reorder this Code Item' : 'Finish editing before reordering'}
                aria-label="Drag code item to reorder"
                data-pdf-hide
              >
                <GripVertical className="h-4 w-4" />
              </button>
              {groupDiff?.status === 'added' ? <DiffIndicator status="added" /> : null}
              {groupDiff?.status === 'modified' && groupDiff.fields.codeItem ? (
                <div>
                  <DiffText diff={groupDiff.fields.codeItem} />
                  {groupDiff.conflictFields?.includes('codeItem') ? <ConflictLabel revision={groupDiff.arrivedRevision} /> : null}
                </div>
              ) : groupDiff?.arrivedFields?.codeItem ? (
                <ArrivedText value={groupDiff.arrivedFields.codeItem.after} revision={groupDiff.arrivedRevision} />
              ) : (
                <span className={groupDiff?.status === 'added' ? 'font-medium text-[#1E7A3F]' : 'text-gray-700'}>{group.codeItemPos.code}</span>
              )}
              <AppendixRefBadges
                refs={resolveAppendixRefs(group, null, null, appendixSummariesByKey)}
                onOpen={() => setReferencedAppendicesTarget({ kind: 'codeItem', gIdx })}
              />
            </div>
            {groupDiff?.status === 'modified' && groupDiff.fields.appendixRefs ? (
              <div className="mt-1 text-[11px]">
                <DiffText diff={toAppendixLetterDiff(groupDiff.fields.appendixRefs, appendixSummariesByKey)} />
              </div>
            ) : null}
            <div className="mt-1 flex justify-center">
              {renderAppendixPill(
                hoveredGroupReorder === gIdx,
                () => setReferenceAppendixTarget({ kind: 'codeItem', gIdx }),
                `Reference an appendix on code item ${group.codeItemPos.code}`
              )}
            </div>
            {canModifyCodeSheetStructure && !hasPendingRowChanges && groupInsertTarget === gIdx && !(
              codeItemEditorTarget?.mode === 'create' && codeItemEditorTarget.insertAfter === gIdx
            ) && (
              <button
                onClick={(e) => { e.stopPropagation(); openGroupPlus(gIdx, e) }}
                title="Insert Role below"
                data-pdf-hide
                className="absolute left-1/2 bottom-[-2px] -translate-x-1/2 translate-y-1/2 bg-[#1D74D8] text-white w-[26px] h-[26px] rounded-full border-none shadow-[0_2px_6px_rgba(0,0,0,0.25)] leading-[26px] text-center font-bold cursor-pointer z-[3]"
              >
                +
              </button>
            )}
          </td>
          <td
            rowSpan={renderedRowCount}
            className={positionCellClass}
            style={positionCellStyle}
            onDragOver={(e) => handleGroupDragOver(gIdx, e)}
            onDrop={(e) => handleGroupDrop(gIdx, e)}
            onMouseEnter={() => setHoveredGroupReorderMatch(gIdx)}
            onMouseLeave={() => clearHoveredGroupReorderMatch(gIdx)}
            onClick={() => handleGroupCellClick(gIdx)}
            onDoubleClick={(e) => handlePositionDoubleClick(gIdx, e)}
            title={hasPendingRowChanges ? undefined : 'Double click to edit number of digits'}
          >
            {groupDiff?.status === 'modified' && groupDiff.fields.position ? (
              <div>
                <DiffText diff={groupDiff.fields.position} />
                {groupDiff.conflictFields?.includes('position') ? <ConflictLabel revision={groupDiff.arrivedRevision} /> : null}
              </div>
            ) : groupDiff?.arrivedFields?.position ? (
              <ArrivedText value={groupDiff.arrivedFields.position.after} revision={groupDiff.arrivedRevision} />
            ) : (
              <span className={groupDiff?.status === 'added' ? 'font-medium text-[#1E7A3F]' : 'text-gray-700'}>{positionLabel}</span>
            )}
          </td>
        </React.Fragment>
      )
    }

    const renderSectionHeaderRow = (section: CodeItemRenderSection) => {
      const isEditingSection = inlineEdit?.kind === 'rowGroupTitle' && inlineEdit.gIdx === gIdx && inlineEdit.sectionKey === section.key
      if (!section.name && !isEditingSection) return null

      const isSectionSelected =
        rowGroupInsertTarget?.gIdx === gIdx &&
        rowGroupInsertTarget.sectionKey === section.key
      const showSectionInsertUI = canModifyCodeSheetStructure && !hasPendingRowChanges && isSectionSelected && !isEditingSection
      const sectionInsertIndex = section.isDraft
        ? groupRows.length
        : (section.rowIndices[section.rowIndices.length - 1] ?? -1) + 1
      const sectionCellClassName = `${sectionCellClass} ${getTableCellBackgroundClass(isGroupSelected || isSectionSelected)}`
      const codeItemLocalKey = getCodeItemLocalKey(group, gIdx)
      const sectionInlineError = isEditingSection ? inlineEdit.error : null
      const sectionAttributes = getRowGroupAttributeDefinitions(group, section.name)
      const compositeSubgroupIndex = isCompositeCodeItem(group)
        ? resolveCodeItemSubgroups(group).findIndex(subgroup => `composite:${subgroup.localKey}` === section.key)
        : -1
      const sectionInlineStyle = isEditingSection
        ? {
            ...sectionCellStyle,
            backgroundColor: '#FFFDF5',
            boxShadow: 'inset 0 0 0 2px #1D74D8',
          }
        : sectionCellStyle
      const handleDropIntoSection = (event: React.DragEvent<HTMLTableCellElement>) => {
        if (!draggedRow || draggedRow.gIdx !== gIdx) return
        event.preventDefault()

        if (section.isDraft) {
          moveDraggedRowToTarget(gIdx, groupRows.length, section.name)
          if (section.draftId) {
            updateDraftRowGroupsForCodeItem(codeItemLocalKey, (current) => current.filter((draftSection) => draftSection.id !== section.draftId))
          }
          return
        }

        const rawTargetIndex = section.rowIndices[0] ?? 0
        moveDraggedRowToTarget(gIdx, rawTargetIndex, section.name)
      }

      return (
        <tr
          key={`section:${gIdx}:${section.key}`}
          onMouseEnter={() => setHoveredSection(`${gIdx}:${section.key}`)}
          onMouseLeave={() => setHoveredSection(current => (current === `${gIdx}:${section.key}` ? null : current))}
        >
          {!groupIdentityCellsRendered && renderGroupIdentityCells()}
          <td
            colSpan={leftContentColSpan}
            data-row-group-title-cell={`${gIdx}:${section.key}`}
            draggable={compositeSubgroupIndex >= 0 && canModifyCodeSheetStructure && !hasPendingRowChanges}
            className={sectionCellClassName}
            style={sectionInlineStyle}
            onDragStart={(event) => {
              if (compositeSubgroupIndex < 0 || !canModifyCodeSheetStructure || hasPendingRowChanges) return
              const subgroupKey = section.key.replace(/^composite:/, '')
              event.dataTransfer.effectAllowed = 'move'
              event.dataTransfer.setData('text/plain', `composite-subgroup:${gIdx}:${subgroupKey}`)
              setDraggedCompositeSubgroup({ gIdx, subgroupKey })
            }}
            onDragEnd={() => setDraggedCompositeSubgroup(null)}
            onDragOver={(event) => {
              if (draggedCompositeSubgroup?.gIdx === gIdx && compositeSubgroupIndex >= 0) {
                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
                return
              }
              if (!draggedRow || draggedRow.gIdx !== gIdx) return
              event.preventDefault()
              event.dataTransfer.dropEffect = 'move'
            }}
            onDrop={(event) => {
              if (draggedCompositeSubgroup?.gIdx === gIdx && compositeSubgroupIndex >= 0) {
                event.preventDefault()
                event.stopPropagation()
                moveCompositeSubgroup(gIdx, draggedCompositeSubgroup.subgroupKey, { targetIndex: compositeSubgroupIndex })
                setDraggedCompositeSubgroup(null)
                return
              }
              handleDropIntoSection(event)
            }}
            onClick={() => {
              if (isEditingSection) return
              handleRowGroupCellClick(gIdx, section.key)
            }}
            onDoubleClick={(event) => {
              beginRowGroupTitleInlineEdit(gIdx, section.key, section.rowIndices, section.draftId, section.name || '', event)
            }}
          >
            <div className="flex flex-col gap-1 px-1 py-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <div className="flex min-w-0 items-center gap-2">
                  <span className={`h-4 w-[2px] shrink-0 rounded-full ${isSectionSelected ? 'bg-[#1D74D8]' : 'bg-[#BFDBFE]'}`} aria-hidden="true" />
                  {isEditingSection ? (
                    <input
                      ref={setInlineEditInputElementRef}
                      value={inlineEdit.draftValue}
                      onChange={(event) => updateInlineEditDraft(event.target.value)}
                      onBlur={handleInlineEditBlur}
                      onKeyDown={handleInlineEditKeyDown}
                      placeholder="Group"
                      aria-invalid={sectionInlineError ? 'true' : 'false'}
                      className={`w-full border py-1 px-1.5 rounded ${TABLE_BODY_TYPOGRAPHY} ${sectionInlineError ? 'border-[#B3261E]' : 'border-[#1D74D8]'}`}
                      style={TABLE_BODY_TYPOGRAPHY_STYLE}
                    />
                  ) : (
                    <>
                      <span className={`${ROW_GROUP_HEADER_TYPOGRAPHY_CLASS} truncate pl-1 ${isSectionSelected ? 'text-[#0F5FC6]' : 'text-[#1D74D8]'}`}>{section.name}</span>
                      {section.valueStart !== null && section.valueEnd !== null && (
                        <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-[#1D5176]">
                          Pos {section.valueStart + 1}{section.valueEnd === section.valueStart ? '' : `-${section.valueEnd + 1}`}
                        </span>
                      )}
                      {section.name ? (
                        <AppendixRefBadges
                          refs={resolveAppendixRefs(group, null, section.name, appendixSummariesByKey)}
                          onOpen={() => setReferencedAppendicesTarget({ kind: 'group', gIdx, sectionName: section.name as string })}
                        />
                      ) : null}
                      {section.name && !section.isDraft && section.rowIndices.length > 0
                        ? renderAppendixPill(
                            hoveredSection === `${gIdx}:${section.key}`,
                            () => setReferenceAppendixTarget({ kind: 'group', gIdx, sectionName: section.name as string }),
                            `Reference an appendix on group ${section.name}`
                          )
                        : null}
                    </>
                  )}
                </div>
                {compositeSubgroupIndex >= 0 && canModifyCodeSheetStructure && !hasPendingRowChanges && (
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      type="button"
                      disabled={compositeSubgroupIndex === 0}
                      onClick={(event) => {
                        event.stopPropagation()
                        moveCompositeSubgroup(gIdx, section.key.replace(/^composite:/, ''), -1)
                      }}
                      className="rounded px-1.5 py-0.5 text-xs text-[#1D5176] hover:bg-blue-100 disabled:opacity-30"
                      aria-label={`Move ${section.name} up`}
                    >↑</button>
                    <button
                      type="button"
                      disabled={compositeSubgroupIndex === resolveCodeItemSubgroups(group).length - 1}
                      onClick={(event) => {
                        event.stopPropagation()
                        moveCompositeSubgroup(gIdx, section.key.replace(/^composite:/, ''), 1)
                      }}
                      className="rounded px-1.5 py-0.5 text-xs text-[#1D5176] hover:bg-blue-100 disabled:opacity-30"
                      aria-label={`Move ${section.name} down`}
                    >↓</button>
                  </div>
                )}
              </div>
              {sectionInlineError && (
                <div className="pl-4 text-xs text-[#B3261E]" role="alert">
                  {sectionInlineError}
                </div>
              )}
            </div>
            {showSectionInsertUI && (
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  setAddMenuAnchor({
                    element: event.currentTarget,
                    gIdx,
                    rIdx: null,
                    sectionKey: section.key,
                    insertIndex: sectionInsertIndex,
                    rowGroupName: section.name,
                    draftSectionId: section.draftId ?? null,
                  })
                }}
                title="Insert options"
                data-pdf-hide
                className="absolute left-1/2 bottom-[-2px] -translate-x-1/2 translate-y-1/2 bg-[#1D74D8] text-white w-[26px] h-[26px] rounded-full border-none shadow-[0_2px_6px_rgba(0,0,0,0.25)] leading-[26px] text-center font-bold cursor-pointer z-[3]"
              >
                +
              </button>
            )}
          </td>
          {hasAttributeColumn && (
            <td
              className={attributeHeaderCellClass}
              style={getAttributeHeaderCellStyle(sectionAttributes.length > 0)}
            >
              {renderScopedAttributeHeaders(sectionAttributes)}
            </td>
          )}
          <td colSpan={trailingColSpan} className={groupTrailingCellClass} style={groupTrailingSurfaceStyle} />
        </tr>
      )
    }

    const renderDataRow = (rowIndex: number) => {
      const rowData = groupRows[rowIndex]
      if (!rowData) return null

      const row: TableRow = {
        gIdx,
        rIdx: rowIndex,
        optionCode: rowData.optionCode,
        status: normalizeStatusValue(rowData.status),
        rowGroup: normalizeRowGroupName(rowData.group),
        groupCode: group.codeItemPos.code,
        positionLabel,
        isGroupFirstRow: rowIndex === 0,
        groupRowCount: groupRows.length,
        descriptionCell: { rowSpan: descMap[rowIndex]?.rowSpan ?? 0, value: (descMap[rowIndex]?.value as string | null) ?? null },
        availabilityCell: { rowSpan: availMap[rowIndex]?.rowSpan ?? 0, value: (availMap[rowIndex]?.value as RuleNode | null | undefined) },
        isNewRow: !!rowData.isNew,
        posRangeLen,
      }

      const rowDiffEntry = diffReady
        ? codeSheetDiff.rowByProjectPosition.get(`${gIdx}:${rowIndex}`) || null
        : null
      const rowDiff = isCodeSheetDiffDisplayed ? rowDiffEntry : null
      const rowDiffBackground = rowDiff?.status === 'added'
        ? '#EFFAF2'
        : rowDiff?.status === 'modified'
          ? '#FFF9E8'
          : undefined
      const isFocusedRowDiff = Boolean(rowDiffEntry && rowDiffEntry.key === activeDiffEntryKey)
      const isOptionFocusedDiff = isFocusedRowDiff && Boolean(
        rowDiffEntry?.fields.optionCode
        || rowDiffEntry?.fields.order
        || rowDiffEntry?.fields.valuePosition
        || rowDiffEntry?.fields.rowGroup
      )
      const focusedRowStyle = isFocusedRowDiff && !isOptionFocusedDiff
        ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' }
        : undefined

      const isNewRow = !!row.isNewRow
      const isCompositeRow = isCompositeCodeItem(group)
      const isInlineEditingOption = inlineEdit?.kind === 'optionCode' && inlineEdit.gIdx === row.gIdx && inlineEdit.rIdx === row.rIdx
      const isInlineEditingStatus = inlineEdit?.kind === 'status' && inlineEdit.gIdx === row.gIdx && inlineEdit.rIdx === row.rIdx
      const isInlineEditingDescription = inlineEdit?.kind === 'description' && inlineEdit.gIdx === row.gIdx && inlineEdit.rIdx === row.rIdx
      const isEditable = isNewRow
      const isAvailabilityConfigEnabled = isEditable || (
        availabilityConfigRow?.gIdx === row.gIdx &&
        availabilityConfigRow.rIdx === row.rIdx
      )
      const isRowSelected = insertTarget.gIdx === row.gIdx && insertTarget.rIdx === row.rIdx
      const selectedRowIdx = insertTarget.gIdx === row.gIdx ? insertTarget.rIdx : null
      const rightSideSelected = isGroupSelected || isRowSelected
      const showInsertUI = canModifyCodeSheetStructure && !hasPendingRowChanges && isRowSelected

      const optionCellClass = `border border-[#d0d7de] py-1.5 px-4 text-left align-middle relative cursor-pointer break-words ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(rightSideSelected)} ${showInsertUI ? 'border-b-2 border-b-[#1D74D8]' : ''}`
      const descSelected = !!cellSelection[`${row.gIdx}:${row.rIdx}:description`]
      const descBlockSelected =
        isGroupSelected ||
        (selectedRowIdx !== null && row.descriptionCell.rowSpan > 0 && doesBlockCoverRow(groupRows, 'description', row.rIdx, selectedRowIdx))
      const descCellClass = `border border-[#d0d7de] py-1.5 px-4 text-left align-middle cursor-pointer break-words ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(descBlockSelected || descSelected)}`
      const availSelected = !!cellSelection[`${row.gIdx}:${row.rIdx}:availability`]
      const availBlockSelected =
        isGroupSelected ||
        (selectedRowIdx !== null && row.availabilityCell.rowSpan > 0 && doesBlockCoverRow(groupRows, 'availability', row.rIdx, selectedRowIdx))
      const availCellClass = `border border-[#d0d7de] py-1.5 px-4 align-middle cursor-pointer break-words ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(availBlockSelected || availSelected)}`
      const statusCellClass = `border border-[#d0d7de] py-1.5 px-3 text-left align-middle break-words ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(rightSideSelected)} ${isEditable || isInlineEditingStatus ? '' : 'cursor-pointer'}`

      const isHighlighted = highlightedRow?.gIdx === row.gIdx && highlightedRow?.rIdx === row.rIdx
      const optionHighlightShadow = isHighlighted ? getRowHighlightShadow('start') : undefined
      const descriptionHighlightShadow = isHighlighted ? getRowHighlightShadow('middle') : undefined
      const availabilityHighlightShadow = isHighlighted ? getRowHighlightShadow('middle') : undefined
      const statusHighlightShadow = isHighlighted ? getRowHighlightShadow('end') : undefined
      const isDraggedRow = draggedRow?.gIdx === row.gIdx && draggedRow?.rIdx === row.rIdx
      const isDropTargetRow =
        rowDropTarget?.gIdx === row.gIdx &&
        rowDropTarget?.rIdx === row.rIdx &&
        !isDraggedRow
      const rowDropIndicatorStyle = getRowDropIndicatorStyle(isDropTargetRow, isDropTargetRow ? rowDropTarget?.position ?? null : null)
      const optionInlineError = isInlineEditingOption ? inlineEdit.error : null
      const statusInlineError = isInlineEditingStatus ? inlineEdit.error : null
      const newRowOptionError = isEditable && newRowOptionValidation?.gIdx === row.gIdx && newRowOptionValidation.rIdx === row.rIdx
        ? newRowOptionValidation.error
        : null
      const newRowDescriptionError = isEditable && newRowDescriptionValidation?.gIdx === row.gIdx && newRowDescriptionValidation.rIdx === row.rIdx
        ? newRowDescriptionValidation.error
        : null
      const persistedOptionValidationError = !isEditable && !isInlineEditingOption
        ? persistedOptionValidationErrors[`${row.gIdx}:${row.rIdx}`] || null
        : null
      const unselectableEntry = !isEditable
        ? unselectableByRowKey[`${row.gIdx}:${row.rIdx}`] || null
        : null
      const ruleRowId = groups[row.gIdx]?.rows[row.rIdx]?.id
      const appendixDisagreements = !isEditable && ruleRowId
        ? ruleAppendixDisagreements.get(String(ruleRowId)) ?? null
        : null
      const persistedDescriptionValidationError = !isEditable && !isInlineEditingDescription
        ? persistedDescriptionValidationErrors[`${row.gIdx}:${row.rIdx}`] || null
        : null
      const persistedStatusValidationError = !isEditable && !isInlineEditingStatus
        ? persistedStatusValidationErrors[`${row.gIdx}:${row.rIdx}`] || null
        : null
      const optionCellStyle = {
        ...TABLE_BODY_TYPOGRAPHY_STYLE,
        ...groupDragHighlightStyle,
        ...rowDropIndicatorStyle,
        ...(rowDiffBackground ? { backgroundColor: rowDiffBackground } : {}),
        ...(unselectableEntry && !isInlineEditingOption ? { backgroundColor: '#FFF5F5' } : {}),
        ...((newRowOptionError || persistedOptionValidationError)
          ? {
              backgroundColor: '#FFF5F5',
              boxShadow: 'inset 0 0 0 2px #B3261E',
            }
          : {}),
        ...(isInlineEditingOption
          ? {
              backgroundColor: '#FFFDF5',
              boxShadow: 'inset 0 0 0 2px #1D74D8',
            }
          : {}),
        ...(optionHighlightShadow
          ? {
              boxShadow: appendBoxShadow(
                (((newRowOptionError || persistedOptionValidationError)
                  ? 'inset 0 0 0 2px #B3261E'
                  : undefined) || (isInlineEditingOption ? 'inset 0 0 0 2px #1D74D8' : undefined)),
                optionHighlightShadow
              ),
            }
          : {}),
        ...(isOptionFocusedDiff
          ? { boxShadow: `inset 3px 0 0 ${activeChangeOutlineColor}, inset 0 3px 0 ${activeChangeOutlineColor}, inset -3px 0 0 ${activeChangeOutlineColor}, inset 0 -3px 0 ${activeChangeOutlineColor}` }
          : {}),
      }
      const descriptionCellStyle = {
        ...TABLE_BODY_TYPOGRAPHY_STYLE,
        ...groupDragHighlightStyle,
        ...rowDropIndicatorStyle,
        ...(rowDiffBackground ? { backgroundColor: rowDiffBackground } : {}),
        ...((newRowDescriptionError || persistedDescriptionValidationError)
          ? {
              backgroundColor: '#FFF5F5',
              boxShadow: 'inset 0 0 0 2px #B3261E',
            }
          : {}),
        ...(isInlineEditingDescription
          ? {
              backgroundColor: '#FFFDF5',
              boxShadow: 'inset 0 0 0 2px #1D74D8',
            }
          : {}),
        ...(descriptionHighlightShadow
          ? {
              boxShadow: appendBoxShadow(
                (((newRowDescriptionError || persistedDescriptionValidationError)
                  ? 'inset 0 0 0 2px #B3261E'
                  : undefined) || (isInlineEditingDescription ? 'inset 0 0 0 2px #1D74D8' : undefined)),
                descriptionHighlightShadow
              ),
            }
          : {}),
      }
      const availabilityCellStyle = {
        minHeight: '40px',
        ...TABLE_BODY_TYPOGRAPHY_STYLE,
        ...groupDragHighlightStyle,
        ...rowDropIndicatorStyle,
        ...(rowDiffBackground ? { backgroundColor: rowDiffBackground } : {}),
        ...(availabilityHighlightShadow
          ? {
              boxShadow: appendBoxShadow(undefined, availabilityHighlightShadow),
            }
          : {}),
      }
      const statusCellStyle = {
        ...TABLE_BODY_TYPOGRAPHY_STYLE,
        ...groupDragHighlightStyle,
        ...rowDropIndicatorStyle,
        ...(rowDiffBackground ? { backgroundColor: rowDiffBackground } : {}),
        ...((persistedStatusValidationError)
          ? {
              backgroundColor: '#FFF5F5',
              boxShadow: 'inset 0 0 0 2px #B3261E',
            }
          : {}),
        ...(isInlineEditingStatus
          ? {
              backgroundColor: '#FFFDF5',
              boxShadow: 'inset 0 0 0 2px #1D74D8',
            }
          : {}),
        ...(statusHighlightShadow
          ? {
              boxShadow: appendBoxShadow(
                ((persistedStatusValidationError)
                  ? 'inset 0 0 0 2px #B3261E'
                  : undefined) || (isInlineEditingStatus ? 'inset 0 0 0 2px #1D74D8' : undefined),
                statusHighlightShadow
              ),
            }
          : {}),
      }
      const canDragRow = canModifyCodeSheetStructure && !hasPendingRowChanges && !isEditable
      const isReorderHandleVisible = canDragRow && (
        isDraggedRow ||
        (hoveredReorderRow?.gIdx === row.gIdx && hoveredReorderRow?.rIdx === row.rIdx)
      )
      const descriptionInlineError = isInlineEditingDescription ? inlineEdit.error : null
      const statusSelectValue = isInlineEditingStatus ? inlineEdit.draftValue : row.status
      const statusSelectOptions = isAllowedStatusValue(statusSelectValue)
        ? [...STATUS_ALLOWED_VALUES]
        : [normalizeStatusValue(statusSelectValue), ...STATUS_ALLOWED_VALUES]
      const rowAttributeDefinitions = hasAttributeColumn
        ? getApplicableAttributeDefinitions(group, rowData)
        : []
      const attrHighlightShadow = isHighlighted ? getRowHighlightShadow('middle') : undefined
      const attrsCellClass = `border border-[#d0d7de] py-1.5 px-3 align-middle break-words ${TABLE_BODY_TYPOGRAPHY} ${getTableCellBackgroundClass(rightSideSelected)}`
      const normalizedRowAttributeValues = normalizeAttributeValues(rowData.attributeValues)
      const rowAttributeError = rowAttributeDefinitions
        .map((attribute) => persistedAttributeValidationErrors[`${row.gIdx}:${row.rIdx}:${attribute.id}`] || null)
        .find((error): error is string => Boolean(error)) || null
      const attrsCellStyle = {
        ...TABLE_BODY_TYPOGRAPHY_STYLE,
        ...groupDragHighlightStyle,
        ...rowDropIndicatorStyle,
        ...(rowDiffBackground ? { backgroundColor: rowDiffBackground } : {}),
        ...(rowAttributeError
          ? {
              backgroundColor: '#FFF5F5',
              boxShadow: 'inset 0 0 0 2px #B3261E',
            }
          : {}),
        ...(attrHighlightShadow
          ? {
              boxShadow: appendBoxShadow(rowAttributeError ? 'inset 0 0 0 2px #B3261E' : undefined, attrHighlightShadow),
            }
          : {}),
      }
      const attributeButtonDisabled = !canMutateCodeSheet || hasPendingRowChanges || isEditable || rowAttributeDefinitions.length === 0
      const renderScopedAttributeValues = () => {
        if (rowAttributeDefinitions.length === 0) return null

        return (
          <div
            className="grid w-full bg-white"
            style={scopedAttributeGridStyle(rowAttributeDefinitions)}
          >
            {rowAttributeDefinitions.map((attribute, attributeIndex) => {
              const attributeValue = String(normalizedRowAttributeValues[attribute.id] || '').trim()
              const attributeError = persistedAttributeValidationErrors[`${row.gIdx}:${row.rIdx}:${attribute.id}`] || null
              const valueButtonClassName = attributeValue
                ? `inline-flex min-h-[24px] max-w-full items-center justify-center rounded px-1 text-center text-[#0D47A1] transition-colors ${
                    attributeButtonDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:bg-[#D8EFFF]'
                  }`
                : `${getInfoBadgeClassName('empty', 'mx-auto max-w-full transition-colors')} ${
                    attributeButtonDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-[#0D47A1] hover:text-[#0D47A1]'
                  }`

              return (
                <div
                  key={attribute.id}
                  className={`flex min-h-[32px] min-w-0 flex-col items-center justify-center bg-white px-2 py-1 ${attributeIndex > 0 ? 'border-l border-[#d0d7de]' : ''}`}
                >
                  <button
                    type="button"
                    disabled={attributeButtonDisabled}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (attributeButtonDisabled) return
                      setValueAttributesTarget({ gIdx: row.gIdx, rIdx: row.rIdx, attributeId: attribute.id })
                    }}
                    className={valueButtonClassName}
                    title={attributeValue || 'empty'}
                    aria-label={`${getAttributeDisplayLabel(attribute)} attributes`}
                  >
                    <span className="truncate">{attributeValue || 'empty'}</span>
                  </button>
                  {attributeError && (
                    <div className="text-xs text-[#B3261E]">
                      {attributeError}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      }

      return (
        <tr
          key={`row:${gIdx}:${rowIndex}`}
          data-row-id={`${row.gIdx}:${row.rIdx}`}
          data-diff-entry-key={rowDiffEntry?.key}
          data-unselectable={unselectableEntry ? 'true' : undefined}
          data-appendix-disagreement={appendixDisagreements ? 'true' : undefined}
          style={focusedRowStyle ?? (unselectableEntry || appendixDisagreements ? UNSELECTABLE_ROW_OUTLINE_STYLE : undefined)}
          className={isDraggedRow ? 'opacity-60' : undefined}
          onDoubleClick={() => activateAvailabilityConfigRow(row)}
          onBlur={isEditable ? (event) => handleNewRowBlur(row.gIdx, row.rIdx, event) : undefined}
          onKeyDown={isEditable ? (event) => handleNewRowKeyDown(row.gIdx, row.rIdx, event) : undefined}
        >
          {!groupIdentityCellsRendered && renderGroupIdentityCells()}
          <td
            className={optionCellClass}
            style={optionCellStyle}
            onDragOver={(e) => handleRowDragOver(row, e)}
            onDrop={(e) => handleRowDrop(row, e)}
            onMouseEnter={() => setHoveredReorderRowMatch(row.gIdx, row.rIdx)}
            onMouseLeave={() => clearHoveredReorderRowMatch(row.gIdx, row.rIdx)}
            onClick={() => { if (!isEditable) handleOptionCellClick(row) }}
            onDoubleClick={(event) => beginOptionCodeInlineEdit(row, event)}
          >
            <div className={rowDiff?.status === 'added'
              ? 'flex items-center gap-1.5'
              : rowDiff?.status === 'modified' && rowDiff.fields.order
                ? 'flex min-w-0 items-center gap-2'
                : undefined}
            >
              {rowDiff?.status === 'added' ? <DiffIndicator status="added" /> : null}
              {rowDiff?.status === 'modified' && rowDiff.fields.optionCode && !isInlineEditingOption ? (
              <div>
                <DiffText diff={rowDiff.fields.optionCode} />
                {rowDiff.conflictFields?.includes('optionCode') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
              </div>
            ) : rowDiff?.arrivedFields?.optionCode && !isInlineEditingOption ? (
              <ArrivedText value={rowDiff.arrivedFields.optionCode.after} revision={rowDiff.arrivedRevision} />
            ) : isEditable && isCompositeRow ? (
              <div className="flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation()
                    setCompositeOptionTarget({ gIdx: row.gIdx, rIdx: row.rIdx })
                  }}
                  className={`w-full rounded border px-2 py-1 text-left font-mono text-sm ${newRowOptionError ? 'border-[#B3261E]' : 'border-[#1D74D8]'} bg-blue-50 text-[#1D5176]`}
                >
                  {row.optionCode || 'Configure option'}
                </button>
                {newRowOptionError && (
                  <div className={OPTION_ERROR_TEXT_CLASS} role="alert">{newRowOptionError}</div>
                )}
              </div>
            ) : isEditable ? (
              <div className="flex flex-col gap-1.5">
                <input
                  value={row.optionCode}
                  onChange={(e) => {
                    const nativeEvent = e.nativeEvent as InputEvent | Event
                    updateRowField(
                      row.gIdx,
                      row.rIdx,
                      'optionCode',
                      e.target.value,
                      'inputType' in nativeEvent ? nativeEvent.inputType : undefined,
                      'data' in nativeEvent && typeof nativeEvent.data === 'string' ? nativeEvent.data : null
                    )
                  }}
                  aria-invalid={newRowOptionError ? 'true' : 'false'}
                  placeholder={getOptionCodePlaceholder(posRangeLen)}
                  className={`w-full border py-1 px-1.5 rounded ${TABLE_BODY_TYPOGRAPHY} ${newRowOptionError ? 'border-[#B3261E]' : 'border-[#d0d7de]'}`}
                  style={TABLE_BODY_TYPOGRAPHY_STYLE}
                />
                {newRowOptionError && (
                  <div className={OPTION_ERROR_TEXT_CLASS} role="alert">
                    {newRowOptionError}
                  </div>
                )}
              </div>
            ) : isInlineEditingOption ? (
              <div className="flex flex-col gap-1.5">
                <input
                  ref={setInlineEditInputElementRef}
                  value={inlineEdit.draftValue}
                  onChange={(e) => updateInlineOptionCodeDraft(e, posRangeLen)}
                  onBlur={handleInlineEditBlur}
                  onKeyDown={handleInlineEditKeyDown}
                  aria-invalid={optionInlineError ? 'true' : 'false'}
                  placeholder={getOptionCodePlaceholder(posRangeLen)}
                  className={`w-full border py-1 px-1.5 rounded ${TABLE_BODY_TYPOGRAPHY} ${optionInlineError ? 'border-[#B3261E]' : 'border-[#1D74D8]'}`}
                  style={TABLE_BODY_TYPOGRAPHY_STYLE}
                />
                {optionInlineError && (
                  <div className={OPTION_ERROR_TEXT_CLASS} role="alert">
                    {optionInlineError}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    draggable={canDragRow}
                    onDragStart={(e) => handleRowDragStart(row, e)}
                    onDragEnd={handleRowDragEnd}
                    onClick={(e) => e.stopPropagation()}
                    className={`flex h-6 w-6 items-center justify-center rounded border border-transparent text-gray-400 transition-all ${canDragRow ? (isReorderHandleVisible ? 'opacity-100' : 'pointer-events-none opacity-0') : 'pointer-events-none opacity-0'} ${canDragRow ? 'cursor-grab hover:border-[#d0d7de] hover:bg-[#f6f8fa] hover:text-[#1D74D8] active:cursor-grabbing' : 'cursor-not-allowed'}`}
                    title={canDragRow ? 'Drag to reorder within this Code Item' : 'Finish editing before reordering'}
                    aria-label="Drag row to reorder"
                    data-pdf-hide
                  >
                    <GripVertical className="h-4 w-4" />
                  </button>
                  <span className={persistedOptionValidationError ? 'text-[#B3261E]' : 'text-gray-700'}>
                    {row.optionCode}
                  </span>
                  {!row.isNewRow ? (
                    <AppendixRefBadges
                      refs={resolveAppendixRefs(group, group.rows[row.rIdx], normalizeRowGroupName(group.rows[row.rIdx]?.group), appendixSummariesByKey)}
                      onOpen={() => setReferencedAppendicesTarget({ kind: 'value', gIdx: row.gIdx, rIdx: row.rIdx })}
                    />
                  ) : null}
                  {!row.isNewRow
                    ? renderAppendixPill(
                        hoveredReorderRow?.gIdx === row.gIdx && hoveredReorderRow?.rIdx === row.rIdx,
                        () => setReferenceAppendixTarget({ kind: 'value', gIdx: row.gIdx, rIdx: row.rIdx }),
                        `Reference an appendix on value ${row.optionCode}`
                      )
                    : null}
                </div>
                {persistedOptionValidationError && (
                  <div className={OPTION_ERROR_TEXT_CLASS}>
                    {persistedOptionValidationError}
                  </div>
                )}
                {unselectableEntry && (
                  <UnselectableValueBadge
                    item={unselectableEntry.item}
                    needsRecheck={unselectableEntry.needsRecheck}
                    testId={`unselectable-value-${row.gIdx}-${row.rIdx}`}
                  />
                )}
                {rowDiff?.status === 'modified' && rowDiff.fields.appendixRefs ? (
                  <div className="text-[11px]">
                    <DiffText diff={toAppendixLetterDiff(rowDiff.fields.appendixRefs, appendixSummariesByKey)} />
                  </div>
                ) : null}
              </div>
              )}
              {rowDiff?.status === 'modified' && rowDiff.fields.order ? (
                <div>
                  <InlineDiffText diff={rowDiff.fields.order} className="text-[11px]" />
                  {rowDiff.conflictFields?.includes('order') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
                </div>
              ) : rowDiff?.arrivedFields?.order ? (
                <ArrivedText value={rowDiff.arrivedFields.order.after} revision={rowDiff.arrivedRevision} className="text-[11px]" />
              ) : null}
            </div>
            {rowDiff?.status === 'modified' && (rowDiff.fields.rowGroup || rowDiff.fields.valuePosition) ? (
              <div className="mt-1 space-y-1 text-[11px]">
                {rowDiff.fields.rowGroup ? <DiffText diff={rowDiff.fields.rowGroup} /> : null}
                {rowDiff.fields.valuePosition ? <DiffText diff={rowDiff.fields.valuePosition} /> : null}
              </div>
            ) : null}
            {rowDiff && (rowDiff.arrivedFields?.rowGroup || rowDiff.arrivedFields?.valuePosition) ? (
              <div className="mt-1 space-y-1 text-[11px]">
                {rowDiff.arrivedFields.rowGroup && !rowDiff.fields.rowGroup ? <ArrivedText value={rowDiff.arrivedFields.rowGroup.after} revision={rowDiff.arrivedRevision} /> : null}
                {rowDiff.arrivedFields.valuePosition && !rowDiff.fields.valuePosition ? <ArrivedText value={rowDiff.arrivedFields.valuePosition.after} revision={rowDiff.arrivedRevision} /> : null}
              </div>
            ) : null}
            {showInsertUI && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setAddMenuAnchor({
                    element: e.currentTarget,
                    gIdx: row.gIdx,
                    rIdx: row.rIdx,
                    sectionKey: null,
                    insertIndex: null,
                    rowGroupName: null,
                    draftSectionId: null,
                  })
                }}
                title="Insert options"
                data-pdf-hide
                className="absolute left-1/2 bottom-[-2px] -translate-x-1/2 translate-y-1/2 bg-[#1D74D8] text-white w-[26px] h-[26px] rounded-full border-none shadow-[0_2px_6px_rgba(0,0,0,0.25)] leading-[26px] text-center font-bold cursor-pointer z-[3]"
              >
                +
              </button>
            )}
          </td>
          {row.descriptionCell.rowSpan > 0 && (
            <td
              rowSpan={row.descriptionCell.rowSpan}
              className={descCellClass}
              style={descriptionCellStyle}
              onMouseEnter={() => setHoveredReorderRowMatch(row.gIdx, row.rIdx)}
              onMouseLeave={() => clearHoveredReorderRowMatch(row.gIdx, row.rIdx)}
              onClick={(e) => !isEditable && handleCellClick(row, 'description', e)}
              onDoubleClick={(event) => beginDescriptionInlineEdit(row, event)}
            >
              {rowDiff?.status === 'modified' && rowDiff.fields.description && !isInlineEditingDescription ? (
                <div>
                  <DiffText diff={rowDiff.fields.description} />
                  {rowDiff.conflictFields?.includes('description') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
                </div>
              ) : rowDiff?.arrivedFields?.description && !isInlineEditingDescription ? (
                <ArrivedText value={rowDiff.arrivedFields.description.after} revision={rowDiff.arrivedRevision} />
              ) : isEditable ? (
                <div className="flex flex-col gap-1.5">
                  <input
                    value={typeof row.descriptionCell.value === 'string' ? (row.descriptionCell.value as string) : ''}
                    onChange={(e) => updateRowField(row.gIdx, row.rIdx, 'description', e.target.value)}
                    placeholder="Description"
                    aria-invalid={newRowDescriptionError ? 'true' : 'false'}
                    className={`w-full border py-1 px-1.5 rounded ${TABLE_BODY_TYPOGRAPHY} ${newRowDescriptionError ? 'border-[#B3261E]' : 'border-[#d0d7de]'}`}
                    style={TABLE_BODY_TYPOGRAPHY_STYLE}
                  />
                  {newRowDescriptionError && (
                    <div className="text-xs text-[#B3261E]" role="alert">
                      {newRowDescriptionError}
                    </div>
                  )}
                </div>
              ) : isInlineEditingDescription ? (
                <div className="flex flex-col gap-1.5">
                  <input
                    ref={setInlineEditInputElementRef}
                    value={inlineEdit.draftValue}
                    onChange={(e) => updateInlineEditDraft(e.target.value)}
                    onBlur={handleInlineEditBlur}
                    onKeyDown={handleInlineEditKeyDown}
                    aria-invalid={descriptionInlineError ? 'true' : 'false'}
                    placeholder="Description"
                    className={`w-full border py-1 px-1.5 rounded ${TABLE_BODY_TYPOGRAPHY} ${descriptionInlineError ? 'border-[#B3261E]' : 'border-[#1D74D8]'}`}
                    style={TABLE_BODY_TYPOGRAPHY_STYLE}
                  />
                  {descriptionInlineError && (
                    <div className="text-xs text-[#B3261E]" role="alert">
                      {descriptionInlineError}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  <span className={persistedDescriptionValidationError ? 'text-[#B3261E]' : 'text-gray-700'}>
                    {(row.descriptionCell.value as string) || ''}
                  </span>
                  {persistedDescriptionValidationError && (
                    <div className="text-xs text-[#B3261E]">
                      {persistedDescriptionValidationError}
                    </div>
                  )}
                </div>
              )}
            </td>
          )}
          {hasAttributeColumn && (
            <td
              className={attrsCellClass}
              style={attrsCellStyle}
              onMouseEnter={() => setHoveredReorderRowMatch(row.gIdx, row.rIdx)}
              onMouseLeave={() => clearHoveredReorderRowMatch(row.gIdx, row.rIdx)}
            >
              {rowDiff?.status === 'modified' && rowDiff.fields.attributes ? (
                <div>
                  <DiffText diff={rowDiff.fields.attributes} />
                  {rowDiff.conflictFields?.includes('attributes') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
                </div>
              ) : rowDiff?.arrivedFields?.attributes ? (
                <ArrivedText value={rowDiff.arrivedFields.attributes.after} revision={rowDiff.arrivedRevision} />
              ) : renderScopedAttributeValues()}
            </td>
          )}
          {row.availabilityCell.rowSpan > 0 && (
            <td
              rowSpan={row.availabilityCell.rowSpan}
              className={`${availCellClass} align-middle`}
              style={availabilityCellStyle}
              onClick={(e) => handleCellClick(row, 'availability', e)}
            >
              {rowDiff?.status === 'modified' && rowDiff.fields.availability ? (
                <div className="space-y-1.5" data-testid="modified-availability-diff">
                  <div className="rounded bg-[#FDECEC] p-1.5 text-[#B3261E] line-through">
                    {rowDiff.fields.availability.before ? (
                      <AvailabilityBadge value={rowDiff.fields.availability.before as RuleNode | null} disabled showConfigIcon={false} />
                    ) : (
                      <span className="text-xs italic">No rule</span>
                    )}
                  </div>
                  <div className="rounded bg-[#EAF7EE] p-1.5 text-[#1E7A3F]">
                    {row.availabilityCell.value ? (
                      <AvailabilityBadge
                        value={row.availabilityCell.value}
                        onConfigClick={() => handleOpenConditionDrawer(row.gIdx, row.rIdx)}
                        showConfigIcon={true}
                        disabled={!canMutateCodeSheet || !isAvailabilityConfigEnabled}
                      />
                    ) : (
                      <span className="text-xs italic">No rule</span>
                    )}
                  </div>
                  {rowDiff.conflictFields?.includes('availability') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
                </div>
              ) : rowDiff?.arrivedFields?.availability ? (
                <div className="space-y-1.5" data-testid="arrived-availability-change">
                  {rowDiff.arrivedFields.availability.after ? (
                    <AvailabilityBadge value={rowDiff.arrivedFields.availability.after as RuleNode} disabled showConfigIcon={false} />
                  ) : null}
                  <ArrivedText value={null} revision={rowDiff.arrivedRevision} />
                </div>
              ) : (
                <div className="w-full">
                  <AvailabilityBadge
                    value={row.availabilityCell.value}


                    onConfigClick={() => handleOpenConditionDrawer(row.gIdx, row.rIdx)}
                    showConfigIcon={true}
                    disabled={!canMutateCodeSheet || !isAvailabilityConfigEnabled}
                  />
                </div>
              )}
              {appendixDisagreements ? (
                <AppendixDisagreementBadge
                  disagreements={appendixDisagreements}
                  needsRecheck={appendixNeedsRecheck}
                  onOpenAppendix={handleOpenAppendix}
                  testId={`appendix-disagreement-${row.gIdx}-${row.rIdx}`}
                />
              ) : null}
              {rowDiff?.status === 'modified' && rowDiff.fields.regions ? (
                <div className="mt-1 text-[11px]"><DiffText diff={rowDiff.fields.regions} /></div>
              ) : rowDiff?.arrivedFields?.regions ? (
                <div className="mt-1 text-[11px]"><ArrivedText value={rowDiff.arrivedFields.regions.after} revision={rowDiff.arrivedRevision} /></div>
              ) : null}
            </td>
          )}
          <td
            className={statusCellClass}
            style={statusCellStyle}
            onMouseEnter={() => setHoveredReorderRowMatch(row.gIdx, row.rIdx)}
            onMouseLeave={() => clearHoveredReorderRowMatch(row.gIdx, row.rIdx)}
            onDoubleClick={(event) => beginStatusInlineEdit(row, event)}
          >
            {rowDiff?.status === 'modified' && rowDiff.fields.status && !isInlineEditingStatus ? (
              <div>
                <DiffText diff={rowDiff.fields.status} />
                {rowDiff.conflictFields?.includes('status') ? <ConflictLabel revision={rowDiff.arrivedRevision} /> : null}
              </div>
            ) : rowDiff?.arrivedFields?.status && !isInlineEditingStatus ? (
              <ArrivedText value={rowDiff.arrivedFields.status.after} revision={rowDiff.arrivedRevision} />
            ) : isEditable ? (
              <select
                aria-label="Status"
                value={row.status}
                onChange={(event) => updateRowField(row.gIdx, row.rIdx, 'status', event.target.value)}
                className={`w-full border py-1 px-1.5 rounded bg-white ${TABLE_BODY_TYPOGRAPHY} border-[#d0d7de]`}
                style={TABLE_BODY_TYPOGRAPHY_STYLE}
              >
                <option value=""> </option>
                <option value="X">X</option>
                <option value="NS">NS</option>
                <option value="NA">NA</option>
              </select>
            ) : isInlineEditingStatus ? (
              <div className="flex flex-col gap-1.5">
                <select
                  ref={setInlineEditSelectElementRef}
                  aria-label="Status"
                  value={statusSelectValue}
                  onChange={(event) => updateInlineStatusDraft(event.target.value)}
                  onBlur={handleInlineEditBlur}
                  onKeyDown={handleInlineEditKeyDown}
                  aria-invalid={statusInlineError ? 'true' : 'false'}
                  className={`w-full border py-1 px-1.5 rounded bg-white ${TABLE_BODY_TYPOGRAPHY} ${statusInlineError ? 'border-[#B3261E]' : 'border-[#1D74D8]'}`}
                  style={TABLE_BODY_TYPOGRAPHY_STYLE}
                >
                  {statusSelectOptions.map((statusOption, index) => (
                    <option
                      key={`${statusOption || 'blank'}-${index}`}
                      value={statusOption}
                    >
                      {statusOption || ' '}
                    </option>
                  ))}
                </select>
                {statusInlineError && (
                  <div className="text-xs text-[#B3261E]" role="alert">
                    {statusInlineError}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <span className={persistedStatusValidationError ? 'text-[#B3261E]' : 'text-gray-700'}>
                  {row.status}
                </span>
                {persistedStatusValidationError && (
                  <div className="text-xs text-[#B3261E]">
                    {persistedStatusValidationError}
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )
    }

    if (showTitleRow) {
      const isEditingCodeItemTitle = inlineEdit?.kind === 'groupTitle' && inlineEdit.gIdx === gIdx
      const showTitleInsertUI = canModifyCodeSheetStructure && !hasPendingRowChanges && isTitleInsertSelected && !isEditingCodeItemTitle
      const titleInlineError = isEditingCodeItemTitle ? inlineEdit.error : null
      const persistedTitleValidationError = !isEditingCodeItemTitle ? persistedTitleValidationErrors[gIdx] || null : null
      const titleInlineStyle = {
        ...titleCellStyle,
        ...((persistedTitleValidationError)
          ? {
              backgroundColor: '#FFF5F5',
              boxShadow: 'inset 0 0 0 2px #B3261E',
            }
          : {}),
        ...(isEditingCodeItemTitle
          ? {
              backgroundColor: '#FFFDF5',
              boxShadow: 'inset 0 0 0 2px #1D74D8',
            }
          : {}),
      }
      renderedNodes.push(
        <tr
          key={`group-title:${gIdx}`}
          data-diff-entry-key={groupDiffEntry?.status !== 'added' ? groupDiffEntry?.key : undefined}
          style={isFocusedGroupDiff && !isFocusedAddedGroupDiff
            ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' }
            : undefined}
        >
          {renderGroupIdentityCells()}
          <td
            colSpan={leftContentColSpan}
            data-group-title-cell={gIdx}
            className={titleCellClass}
            style={titleInlineStyle}
            onClick={() => {
              if (isEditingCodeItemTitle) return
              handleRowGroupCellClick(gIdx, TITLE_ROW_INSERT_KEY)
            }}
            onDoubleClick={(event) => beginGroupTitleInlineEdit(gIdx, event)}
          >
            <div className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {!hasAttributeColumn && renderCodeItemActionsButton()}
                <div className="min-w-0">
                  {isEditingCodeItemTitle ? (
                    <input
                      ref={setInlineEditInputElementRef}
                      value={inlineEdit.draftValue}
                      onChange={(e) => updateInlineEditDraft(e.target.value)}
                      onBlur={handleInlineEditBlur}
                      onKeyDown={handleInlineEditKeyDown}
                      placeholder="Title"
                      aria-invalid={titleInlineError ? 'true' : 'false'}
                      className={`w-full border py-1 px-1.5 rounded font-semibold uppercase text-gray-900 ${TABLE_BODY_TYPOGRAPHY} ${titleInlineError ? 'border-[#B3261E]' : 'border-[#1D74D8]'}`}
                      style={TABLE_BODY_TYPOGRAPHY_STYLE}
                    />
                  ) : groupDiff?.status === 'modified' && groupDiff.fields.title ? (
                    <div>
                      <DiffText diff={groupDiff.fields.title} />
                      {groupDiff.conflictFields?.includes('title') ? <ConflictLabel revision={groupDiff.arrivedRevision} /> : null}
                    </div>
                  ) : groupDiff?.arrivedFields?.title ? (
                    <ArrivedText value={groupDiff.arrivedFields.title.after} revision={groupDiff.arrivedRevision} />
                  ) : (
                    <span className={`block truncate font-semibold uppercase ${persistedTitleValidationError ? 'text-[#B3261E]' : 'text-gray-900'}`}>
                      {titleText}
                    </span>
                  )}
                </div>
              </div>
              {groupDiff?.status === 'modified' && (groupDiff.fields.attributeSchemas || groupDiff.fields.authoring || groupDiff.fields.subgroups) ? (
                <div className="space-y-1 text-[11px]">
                  {groupDiff.fields.authoring ? <DiffText diff={groupDiff.fields.authoring} /> : null}
                  {groupDiff.fields.subgroups ? <DiffText diff={groupDiff.fields.subgroups} /> : null}
                  {groupDiff.fields.attributeSchemas ? <DiffText diff={groupDiff.fields.attributeSchemas} /> : null}
                </div>
              ) : null}
              {groupDiff && (groupDiff.arrivedFields?.attributeSchemas || groupDiff.arrivedFields?.authoring || groupDiff.arrivedFields?.subgroups) ? (
                <div className="space-y-1 text-[11px]">
                  {groupDiff.arrivedFields.authoring && !groupDiff.fields.authoring ? <ArrivedText value={groupDiff.arrivedFields.authoring.after} revision={groupDiff.arrivedRevision} /> : null}
                  {groupDiff.arrivedFields.subgroups && !groupDiff.fields.subgroups ? <ArrivedText value={groupDiff.arrivedFields.subgroups.after} revision={groupDiff.arrivedRevision} /> : null}
                  {groupDiff.arrivedFields.attributeSchemas && !groupDiff.fields.attributeSchemas ? <ArrivedText value={groupDiff.arrivedFields.attributeSchemas.after} revision={groupDiff.arrivedRevision} /> : null}
                </div>
              ) : null}
              {(titleInlineError || persistedTitleValidationError) && (
                <div className="text-xs text-[#B3261E]" role="alert">
                  {titleInlineError || persistedTitleValidationError}
                </div>
              )}
            </div>
            {showTitleInsertUI && (
              <button
                onClick={(event) => {
                  event.stopPropagation()
                  setAddMenuAnchor({
                    element: event.currentTarget,
                    gIdx,
                    rIdx: null,
                    sectionKey: TITLE_ROW_INSERT_KEY,
                    insertIndex: null,
                    rowGroupName: null,
                    draftSectionId: null,
                  })
                }}
                title="Insert options"
                data-pdf-hide
                className="absolute left-1/2 bottom-[-2px] -translate-x-1/2 translate-y-1/2 bg-[#1D74D8] text-white w-[26px] h-[26px] rounded-full border-none shadow-[0_2px_6px_rgba(0,0,0,0.25)] leading-[26px] text-center font-bold cursor-pointer z-[3]"
              >
                +
              </button>
            )}
          </td>
          {hasAttributeColumn && (
            <td
              className={attributeHeaderCellClass}
              style={getAttributeHeaderCellStyle(codeItemAttributes.length > 0)}
            >
              {renderAttributeHeaderWithActions(codeItemAttributes)}
            </td>
          )}
          <td colSpan={trailingColSpan} className={groupTrailingCellClass} style={groupTrailingSurfaceStyle} />
        </tr>
      )
    }

    sections.forEach((section) => {
      const isEditingSection = inlineEdit?.kind === 'rowGroupTitle' && inlineEdit.gIdx === gIdx && inlineEdit.sectionKey === section.key
      if (section.name || isEditingSection) {
        const sectionHeaderRow = renderSectionHeaderRow(section)
        if (sectionHeaderRow) {
          renderedNodes.push(sectionHeaderRow)
        }
      }

      if (section.isDraft) {
        return
      }

      section.rowIndices.forEach((rowIndex) => {
        const rowNode = renderDataRow(rowIndex)
        if (rowNode) {
          renderedNodes.push(rowNode)
        }
      })
    })

    if (diffReady && isCodeSheetDiffDisplayed) {
      const query = searchQuery.trim().toLowerCase()
      ;(codeSheetDiff.arrivedRowsByProjectGroupIndex.get(gIdx) || [])
        .filter((entry) => !query || entry.searchableText.includes(query))
        .forEach((entry) => {
          if (!entry.liveRow) return
          renderedNodes.push(
            <tr
              key={`arrived:${entry.key}`}
              data-diff-entry-key={entry.key}
              style={entry.key === activeDiffEntryKey
                ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' }
                : undefined}
            >
              {!groupIdentityCellsRendered && renderGroupIdentityCells()}
              {renderArrivedRowCells(entry.liveRow, entry.arrivedRevision)}
            </tr>
          )
        })
    }

    visibleDeletedDiffEntries
      .filter((entry) => entry.kind === 'row' && entry.projectGroupIndex === gIdx)
      .forEach((entry) => {
        const deletedNode = renderDeletedDiffEntry(entry)
        if (deletedNode) renderedNodes.push(deletedNode)
      })

    return renderedNodes
  }

  function renderArrivedRowCells(row: CodeRow, revision?: string | null): React.ReactNode {
    const cellClass = `border border-[#d0d7de] px-3 py-2 align-middle ${TABLE_BODY_TYPOGRAPHY}`
    return (
      <React.Fragment>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <ArrivedText value={row.optionCode} revision={revision} />
          {row.group ? <div className="mt-1 text-[11px] text-gray-600">{row.group}</div> : null}
        </td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <ArrivedText value={row.description} revision={revision} />
        </td>
        {hasAttributeColumn ? (
          <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            <ArrivedText value={row.attributeValues || {}} revision={revision} />
          </td>
        ) : null}
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <ArrivedText value={ruleToString(row.rule)} revision={revision} />
          {row.regions?.length ? <div className="mt-1 text-[11px] text-gray-600">{row.regions.join(', ')}</div> : null}
        </td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <ArrivedText value={normalizeStatusValue(row.status)} revision={revision} />
        </td>
      </React.Fragment>
    )
  }

  function renderArrivedGroupEntry(entry: LogicalRowDiff): React.ReactNode {
    const liveGroup = entry.liveGroup
    if (!liveGroup) return null
    const [start, end] = liveGroup.codeItemPos.posRange
    const position = formatPositionLabel(start, end)
    const rowEntries = codeSheetDiff.entries
      .filter((candidate) => (
        candidate.kind === 'row'
        && candidate.groupKey === entry.groupKey
        && candidate.status === 'unchanged'
        && candidate.arrivedStatus === 'added'
      ))
      .sort((left, right) => (
        (left.liveRowIndex ?? Number.MAX_SAFE_INTEGER) - (right.liveRowIndex ?? Number.MAX_SAFE_INTEGER)
      ))
    const cellClass = `border border-[#d0d7de] px-3 py-2 align-middle ${TABLE_BODY_TYPOGRAPHY}`
    const isFocused = entry.key === activeDiffEntryKey

    return (
      <React.Fragment key={`arrived-group:${entry.key}`}>
        <tr
          data-diff-entry-key={entry.key}
          style={isFocused ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' } : undefined}
        >
          <td rowSpan={Math.max(1, rowEntries.length + 1)} className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            <ArrivedText value={liveGroup.codeItemPos.code} revision={entry.arrivedRevision} />
          </td>
          <td rowSpan={Math.max(1, rowEntries.length + 1)} className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            <ArrivedText value={position} revision={entry.arrivedRevision} />
          </td>
          <td colSpan={visibleColumnCount - 2} className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            <ArrivedText value={liveGroup.title} revision={entry.arrivedRevision} />
          </td>
        </tr>
        {rowEntries.map((rowEntry) => rowEntry.liveRow ? (
          <tr
            key={`arrived:${rowEntry.key}`}
            data-diff-entry-key={rowEntry.key}
            style={rowEntry.key === activeDiffEntryKey
              ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' }
              : undefined}
          >
            {renderArrivedRowCells(rowEntry.liveRow, rowEntry.arrivedRevision || entry.arrivedRevision)}
          </tr>
        ) : null)}
      </React.Fragment>
    )
  }

  function renderDeletedDiffEntry(entry: LogicalRowDiff): React.ReactNode {
    const liveGroup = entry.liveGroup
    if (!liveGroup) return null
    const [start, end] = liveGroup.codeItemPos.posRange
    const position = formatPositionLabel(start, end)
    const isFocused = entry.key === activeDiffEntryKey
    const rowStyle: React.CSSProperties = {
      backgroundColor: '#FFF1F1',
      color: '#B3261E',
      textDecoration: 'line-through',
      ...(isFocused ? { outline: `3px solid ${activeChangeOutlineColor}`, outlineOffset: '-3px' } : {}),
    }
    const cellClass = `border border-[#d0d7de] px-3 py-2 align-middle ${TABLE_BODY_TYPOGRAPHY}`

    if (entry.kind === 'group') {
      return (
        <tr key={`deleted:${entry.key}`} data-diff-entry-key={entry.key} style={rowStyle}>
          <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            <div className="flex items-center gap-2"><DiffIndicator status="deleted" />{liveGroup.codeItemPos.code}</div>
          </td>
          <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{position}</td>
          <td colSpan={visibleColumnCount - 2} className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
            {liveGroup.title || 'Deleted Code Item'}
          </td>
        </tr>
      )
    }

    const liveRow = entry.liveRow
    if (!liveRow) return null
    return (
      <tr key={`deleted:${entry.key}`} data-diff-entry-key={entry.key} style={rowStyle}>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{liveGroup.codeItemPos.code}</td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{position}</td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <div className="flex items-center gap-2"><DiffIndicator status="deleted" />{liveRow.optionCode || 'Empty'}</div>
          {liveRow.group ? <div className="mt-1 text-[11px]">{liveRow.group}</div> : null}
        </td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{liveRow.description || 'Empty'}</td>
        {hasAttributeColumn ? (
          <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{formatDiffValue(liveRow.attributeValues || {})}</td>
        ) : null}
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>
          <AvailabilityBadge value={liveRow.rule} disabled showConfigIcon={false} />
          {liveRow.regions?.length ? <div className="mt-1 text-[11px]">{liveRow.regions.join(', ')}</div> : null}
        </td>
        <td className={cellClass} style={TABLE_BODY_TYPOGRAPHY_STYLE}>{normalizeStatusValue(liveRow.status) || 'Empty'}</td>
      </tr>
    )
  }

  const attributeSchemaDialogGroup = attributeSchemaTarget !== null
    ? groups[attributeSchemaTarget] ?? null
    : null
  const attributeSchemaDialogRowGroupNames = useMemo(() => {
    if (attributeSchemaTarget === null) return []
    const group = groups[attributeSchemaTarget]
    if (!group) return []

    return getExistingRowGroupNames(group, getCodeItemDraftRowGroups(attributeSchemaTarget))
  }, [attributeSchemaTarget, getCodeItemDraftRowGroups, groups])
  const valueAttributesDialogContext = useMemo(() => {
    if (!valueAttributesTarget) {
      return {
        group: null,
        row: null,
        attributes: [] as CodeItemAttributeDefinition[],
        initialFocusAttributeId: null,
      }
    }

    const group = groups[valueAttributesTarget.gIdx] || null
    const row = group?.rows[valueAttributesTarget.rIdx] || null

    return {
      group,
      row,
      attributes: getApplicableAttributeDefinitions(group, row),
      initialFocusAttributeId: valueAttributesTarget.attributeId,
    }
  }, [groups, valueAttributesTarget])

  if (securityLoading) {
    return (
      <div className="px-4 py-6">
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="ci-dots-bars-loader" aria-hidden="true" />
          <span className="text-sm text-gray-600">details</span>
        </div>
      </div>
    )
  }

  if (!canViewCodeSheets) {
    return (
      <div className="px-4 py-6">
        <ForbiddenState />
      </div>
    )
  }

  if (!loading && loadError && loadErrorKind === 'not_found') {
    return (
      <ConnectionErrorState
        onRetry={loadData}
        title="Code Sheet not found"
        description="The requested Code Sheet could not be found. It may have been deleted or the link may be invalid."
      />
    )
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col pb-[10vh] box-border">
      <CodeIndexHeader
        header={header}
        groups={groups}
        published={published}
        projectStatus={displayedProjectStatus}
        projectRevision={activeProject?.targetRevision}
        saving={saving}
        hasChanges={hasEffectiveChanges}
        hasUnsavedNewRows={hasPendingRowChanges}
        hasBlockingValidationErrors={hasBlockingValidationErrors}
        blockingValidationReason={blockingValidationReason}
        allowBlockingActionClicks={Boolean(firstBrokenRuleReferenceIssue) || blockingRuleValidationIssues.length > 0}
        undoStackLength={canMutateCodeSheet ? undoStack.length : 0}
        activeUsers={filteredActiveUsers}
        isConnected={isConnected}
        codeIndexId={id}
        projectId={activeProjectId || undefined}
        changesRequestedRefreshKey={changesRequestedRefreshKey}
        onRefreshChangesRequested={async () => {
          await Promise.all([loadData(), loadHeader()])
        }}
        canViewChangesRequested={canViewChangesRequested}
        changesDisabledReason={changesRequestedAccess.message}
        canReviewChangesets={canReviewChangesets}
        changesReviewDisabledReason={reviewChangesetAccess.message}
        canReviseRejectedChanges={reviseChangeAccess.allowed && String(activeProject?.status || '') === 'SubmittedForReview'}
        revisionDisabledReason={reviseChangeAccess.message}
        onReviseChange={handleStartRejectedChangeRevision}
        onGoToChange={handleGoToCodeSheetChange}
        resolveAppendixLabel={resolveAppendixChangeLabel}
        saveDisabledReason={pageMutationDisabledReason || (isRevisingRejectedChange ? reviseChangeAccess.message : saveChangesAccess.message)}
        submitForReviewDisabledReason={isRevisingRejectedChange ? revisionResubmitDisabledReason : submitForReviewDisabledReason}
        outstandingUnselectableCount={isRevisingRejectedChange ? 0 : outstandingUnselectableCount}
        outstandingAppendixDisagreementCount={isRevisingRejectedChange ? 0 : outstandingAppendixDisagreementCount}
        mergeToMainDisabledReason={mergeToMainDisabledReason}
        showMutationActions={isProjectMode}
        showPublishedToggle={false}
        saveButtonLabel="Save Changes"
        savingLabel="Saving..."
        showSaveAction={canEditActiveProject || isRevisingRejectedChange}
        submitButtonLabel={isRevisingRejectedChange ? 'Resubmit Change' : 'Submit for Review'}
        onOpenProjects={!isProjectMode ? () => setProjectsDrawerOpen(true) : undefined}
        onCreateProject={!isProjectMode ? () => setCreateProjectOpen(true) : undefined}
        onSaveChanges={handleSaveChanges}
        onSubmitForReview={isRevisingRejectedChange
          ? handleResubmitRejectedChange
          : canEditActiveProject && !isInitialMainSetup ? handleOpenSubmitForReviewDialog : undefined}
        onMergeToMain={isActiveProjectReadyToMerge ? handleMergeToMain : undefined}
        onCancelProject={canCancelActiveProject ? handleCancelProject : undefined}
        cancelProjectDisabledReason={cancelProjectDisabledReason}
        onUndo={handleUndo}
      />

      {comparisonBaseUnavailable && (
        <CodeSheetStatusBanner testId="codesheet-legacy-base-banner" variant="warning">
          <span className="font-semibold">This project cannot be safely reviewed or merged.</span>
          <span className="ml-1">Its original Code Sheet snapshot is unavailable. Recreate the project to continue.</span>
        </CodeSheetStatusBanner>
      )}
      {comparisonHasConflicts && !comparisonBaseUnavailable && (
        <CodeSheetStatusBanner testId="codesheet-conflict-banner" variant="warning">
          <span className="font-semibold">This project conflicts with changes from a later revision.</span>
          <span className="ml-1">Conflicting changes cannot be approved. Reject and resolve them in Changes requested before merging.</span>
        </CodeSheetStatusBanner>
      )}
      {isProjectMode && activeProject && isActiveProjectReadyToMerge && !mergeToMainDisabledReason && (
        <CodeSheetStatusBanner
          action={(
            <button
              type="button"
              onClick={handleReturnToMainCodesheet}
              className="text-sm font-medium text-[#1D5176] underline"
            >
              Return to live codesheet
            </button>
          )}
          testId="codesheet-project-ready-banner"
          variant="success"
        >
          <span>All changes approved - ready to merge into the live sheet.</span>
        </CodeSheetStatusBanner>
      )}
      {isInitialMainSetup && (
        <CodeSheetStatusBanner testId="codesheet-initial-setup-banner" variant="warning">
          <span>This is a new Code Sheet. The first saved changes will become the live version. Make sure everything is ready before selecting Save Changes.</span>
        </CodeSheetStatusBanner>
      )}
      {!isProjectMode && mergedLiveBannerCodeIndexId === id && (
        <CodeSheetStatusBanner testId="codesheet-live-banner" variant="success">
          <span>Live — published to all users</span>
        </CodeSheetStatusBanner>
      )}
      {isProjectMode && activeProject && !isMainProject(activeProject) && !isActiveProjectReadyToMerge && (canEditActiveProject || isRevisingRejectedChange) && (
        <CodeSheetStatusBanner
          action={(
            <button
              type="button"
              onClick={handleReturnToMainCodesheet}
              className="text-sm font-medium text-[#1D5176] underline"
            >
              Return to live codesheet
            </button>
          )}
          testId="codesheet-editing-project-banner"
          variant="warning"
        >
          <span className="font-semibold">{isRevisingRejectedChange ? 'Revising rejected change: ' : 'Editing project: '}</span>
          <span>{activeProject.name || activeProject.id}</span>
          {isRevisingRejectedChange && revisionTarget ? (
            <span className="ml-2 rounded border border-amber-400 bg-amber-50 px-2 py-0.5 font-medium text-amber-900">
              {revisionTarget.nodeType}{revisionTarget.path ? ` / ${revisionTarget.path}` : ''}
            </span>
          ) : null}
          <span className="ml-2"> - changes won't affect the live sheet until merged and published.</span>
        </CodeSheetStatusBanner>
      )}
      {readOnlyBannerContent && (
        <CodeSheetStatusBanner
          action={isProjectMode
            && activeProject
            && !isMainProject(activeProject)
            && (
              String(activeProject.status || '') === 'SubmittedForReview'
              || PROJECT_COMPLETED_STATUSES.has(String(activeProject.status || ''))
            )
            && !isRevisingRejectedChange
            ? (
              <button
                type="button"
                onClick={handleReturnToMainCodesheet}
                className="text-sm font-medium text-[#1D5176] underline"
              >
                Return to live codesheet
              </button>
            )
            : undefined}
          testId="codesheet-read-only-banner"
          variant="info"
        >
          {readOnlyBannerContent}
        </CodeSheetStatusBanner>
      )}
      {appendixCheckMessage && (
        <CodeSheetStatusBanner variant="info" testId="appendix-live-check-status">
          {appendixCheckMessage}
        </CodeSheetStatusBanner>
      )}
      {isProjectMode && (outstandingUnselectableCount > 0 || outstandingAppendixDisagreementCount > 0) && (
        <UnselectableValuesBanner
          count={outstandingUnselectableCount}
          needsRecheck={unselectableNeedsRecheck || appendixNeedsRecheck}
          appendixResults={appendixConsistencyResults}
          onOpenAppendix={handleOpenAppendix}
        />
      )}

      <div className="flex min-h-0 flex-1 flex-col w-full px-4 py-3 pb-4 min-[1400px]:mx-auto min-[1400px]:max-w-[1920px] min-[1400px]:px-[18px] min-[1800px]:px-5">
        <CodeIndexToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          showDiffToggle={canToggleCodeSheetDiff}
          diffVisible={isCodeSheetDiffDisplayed}
          diffToggleDisabled={comparisonLoading || !diffReady}
          onDiffVisibleChange={handleDiffVisibleChange}
          canOpenAppendix={Boolean(id)}
          canSplit={canSplit}
          canMerge={canMerge}
          deleteDisabled={deleteDisabled}
          deleteTooltip={deleteTooltip}
          onOpenAppendix={() => setAppendixDrawerOpen(true)}
          onSplit={() => sel.column && actSplit(sel.column)}
          onMerge={() => sel.column && void actMerge(sel.column)}
          onDelete={handleDelete}
          onExportPdf={handleExportPdf}
          exportPdfDisabled={loading || groups.length === 0}
        />

        {canToggleCodeSheetDiff && (
          <CodeSheetDiffBar
            counts={codeSheetDiff.counts}
            diffVisible={isCodeSheetDiffDisplayed}
            changedOnly={changedRowsOnly}
            loading={comparisonLoading}
            error={comparisonError}
            onChangedOnlyChange={setChangedRowsOnly}
            onRetry={retryComparison}
          />
        )}

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div ref={tableScrollRef} data-pdf-scroll className="min-h-0 flex-1 overflow-x-auto overflow-y-auto relative pb-[15vh] box-border isolate">
            <table ref={tableExportRef} className={`w-full ${hasAttributeColumn ? 'min-w-[1080px]' : 'min-w-[940px]'} border-collapse table-auto`}>
              <colgroup>
                <col style={{ width: '1%' }} />
                <col style={{ width: '1%' }} />
                <col style={{ width: '5%'}} />
                <col style={{ width: hasAttributeColumn ? '22%' : '28%' }} />
                {hasAttributeColumn && <col style={{ width: '18%' }} />}
                <col style={{ width: hasAttributeColumn ? '22%' : '28%' }} />
                <col style={{ width: '8%' }} />
              </colgroup>
              <thead className="sticky top-0 z-20 bg-[#f6f8fa]">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={() => {
                          if (header.id === 'codeItemPos') {
                            console.log(JSON.stringify(groups, null, 2))
                          }
                        }}
                        className={`relative z-[1] border border-[#d0d7de] py-2.5 ${header.id === 'codeItemPos' ? 'px-4' : 'px-2'} bg-[#f6f8fa] ${TABLE_HEADER_TYPOGRAPHY} text-left whitespace-nowrap`}
                        style={{
                          ...TABLE_HEADER_TYPOGRAPHY_STYLE,
                          backgroundColor: '#f6f8fa',
                          boxShadow: 'inset 0 -1px 0 #d0d7de, inset 0 1px 0 #d0d7de, inset -1px 0 0 #d0d7de, inset 1px 0 0 #d0d7de'
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="relative z-0">
                {visibleCodeItemModels.flatMap((model) => renderCodeItemRows(model))}
                {visibleArrivedGroups.map((entry) => renderArrivedGroupEntry(entry))}
                {visibleDeletedDiffEntries
                  .filter((entry) => entry.projectGroupIndex === null)
                  .map((entry) => renderDeletedDiffEntry(entry))}
                {loading && (
                  <tr>
                    <td colSpan={visibleColumnCount} className="py-6">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <div className="ci-dots-bars-loader" aria-hidden="true" />
                        <span className="text-sm text-gray-600">details</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {compositeValidationErrors.length > 0 && (
            <div className="mx-4 mt-2 rounded-md border border-[#F2B8B5] bg-[#FFF4F4] px-3 py-2 text-xs text-[#B3261E]" role="alert">
              {compositeValidationErrors.slice(0, 3).map((error, index) => (
                <div key={`${error.gIdx}:${index}`}>Code {error.code}: {error.message}</div>
              ))}
              {compositeValidationErrors.length > 3 && <div>+{compositeValidationErrors.length - 3} more composite validation issue(s)</div>}
            </div>
          )}

        </div>
      </div>

      {!loading && loadError && (
        <ConnectionErrorState onRetry={loadData} />
      )}

      {!loading && !loadError && groups.length === 0 && canMutateCodeSheet && (
        <EmptyState onAdd={() => setCodeItemEditorTarget({ mode: 'create', insertAfter: null })} />
      )}

      {!loading && !loadError && groups.length === 0 && !canMutateCodeSheet && (
        <div className="px-4 py-6 text-center text-sm text-gray-600">
          No code items are available for this Code Sheet.
        </div>
      )}

      {undoToast && (
        <UndoToast
          message={undoToast.message}
          onUndo={handleUndo}
          onClose={() => setUndoToast(null)}
          autoHideDurationMs={undoToast.autoHideDurationMs}
          showCloseButton={undoToast.showCloseButton}
        />
      )}

      <ProjectsHistoryDrawer
        open={projectsDrawerOpen}
        onClose={() => setProjectsDrawerOpen(false)}
        codeIndexId={id}
        projects={projects}
        onProjectsLoaded={setProjects}
        onEnterProject={navigateToProject}
      />

      <AppendixDrawer
        open={appendixDrawerOpen}
        onClose={() => {
          setAppendixDrawerOpen(false)
          setAppendixToOpen(null)
        }}
        codeIndexId={id}
        projectId={activeProjectId || undefined}
        subtitle={appendixDrawerSubtitle}
        canEdit={canModifyCodeSheetStructure}
        codesheetSavedAt={codesheetSavedAt}
        createDisabledReason={hasUnsavedCodeSheetChanges ? 'Save your changes before adding an appendix' : undefined}
        onAppendixCreated={handleAppendixSavedFromDrawer}
        onAppendixUpdated={handleAppendixSavedFromDrawer}
        onAppendixDeleted={() => void loadData()}
        openAppendixId={appendixToOpen}
        consistencyReport={isProjectMode ? appendixConsistencyReport : null}
        consistencyMessage={appendixCheckMessage}
        onEditRule={handleEditRuleFromAppendix}
        onProposeRule={handleProposeRuleFromAppendix}
        onGoToCodeItem={handleGoToCodeItemFromAppendix}
      />

      <AppendixEditorModal
        open={Boolean(appendixReviewEditor)}
        codeIndexId={id ?? ''}
        projectId={activeProjectId || undefined}
        codesheetSavedAt={codesheetSavedAt}
        appendixId={appendixReviewEditor?.appendixId}
        readOnly={appendixReviewEditor?.mode !== 'revision'}
        revisionChangeId={appendixReviewEditor?.mode === 'revision' ? appendixReviewEditor.change.id : undefined}
        revisionPath={appendixReviewEditor?.change.path}
        revisionChangeType={appendixReviewEditor?.change.changeType}
        onClose={() => setAppendixReviewEditor(null)}
        onSaved={() => void handleAppendixRevisionSaved()}
        consistency={isProjectMode ? getConsistencyResult(appendixConsistencyReport, appendixReviewEditor?.appendixId) : null}
      />

      <UnresolvedAppendicesDialog
        items={unresolvedAppendices}
        formatName={(item) => {
          const summary = appendixSummariesByKey.get(item.appendixKey)
          const title = summary?.title || item.title
          const letter = summary?.letter || item.letter
          return letter ? `${letter} — ${title}` : title
        }}
        onOpenAppendix={(item) => {
          const appendixId = item.appendixId || appendixSummariesByKey.get(item.appendixKey)?.id
          setUnresolvedAppendices(null)
          if (appendixId) {
            setIncompleteAppendixId(appendixId)
          } else {
            setAppendixDrawerOpen(true)
          }
        }}
        onClose={() => setUnresolvedAppendices(null)}
      />

      <AppendixEditorModal
        open={Boolean(incompleteAppendixId)}
        codeIndexId={id ?? ''}
        projectId={activeProjectId || undefined}
        codesheetSavedAt={codesheetSavedAt}
        appendixId={incompleteAppendixId ?? undefined}
        readOnly={!canModifyCodeSheetStructure}
        saveDisabledReason={hasUnsavedCodeSheetChanges ? 'Save your changes before editing an appendix' : undefined}
        onClose={() => setIncompleteAppendixId(null)}
        onSaved={() => {
          setIncompleteAppendixId(null)
          void loadData()
        }}
      />

      <CreateProjectDialog
        open={createProjectOpen}
        onClose={() => setCreateProjectOpen(false)}
        codeIndexId={id}
        currentUserName={user?.name || user?.emailAddress || null}
        onCreated={handleProjectCreated}
      />

      <SubmitForReviewDialog
        open={submitForReviewOpen}
        saving={saving}
        onClose={() => setSubmitForReviewOpen(false)}
        onSubmit={(description) => void handleSubmitProjectForReview(description)}
      />

      {codeItemEditorTarget && (() => {
        const editGroup = codeItemEditorTarget.mode === 'edit'
          ? groups[codeItemEditorTarget.gIdx] || null
          : null
        if (codeItemEditorTarget.mode === 'edit' && !editGroup) return null
        const insertAfter = codeItemEditorTarget.mode === 'create' ? codeItemEditorTarget.insertAfter : null
        const insertionIndex = insertAfter === null ? 0 : insertAfter + 1
        const code = editGroup?.codeItemPos.code || String(insertionIndex + 1).padStart(2, '0')
        const start = editGroup?.codeItemPos.posRange[0]
          || (insertAfter === null ? 1 : (groups[insertAfter]?.codeItemPos.posRange[1] || 0) + 1)
        const initialValue: CodeItemEditorValue = editGroup
          ? {
              title: String(editGroup.title || ''),
              digits: getGroupDigits(editGroup),
              authoringMode: isCompositeCodeItem(editGroup) ? 'composite' : 'simple',
              simpleGroupNames: [],
              subgroups: resolveCodeItemSubgroups(editGroup),
            }
          : { title: '', digits: 1, authoringMode: 'simple', simpleGroupNames: [], subgroups: [] }
        return (
          <CodeItemEditorDialog
            open
            mode={codeItemEditorTarget.mode}
            code={code}
            start={start}
            group={editGroup}
            allGroups={groups}
            initialValue={initialValue}
            autoFocusCompositeAdd={codeItemEditorTarget.mode === 'edit' && Boolean(codeItemEditorTarget.autoFocusCompositeAdd)}
            onClose={() => setCodeItemEditorTarget(null)}
            onApply={(value) => {
              if (codeItemEditorTarget.mode === 'create') {
                createCodeItemFromEditor(codeItemEditorTarget.insertAfter, value)
              } else {
                applyEditedCodeItem(codeItemEditorTarget.gIdx, value)
              }
            }}
          />
        )
      })()}

      {compositeOptionTarget && (() => {
        const targetGroup = groups[compositeOptionTarget.gIdx]
        const targetRow = targetGroup?.rows[compositeOptionTarget.rIdx]
        const targetSubgroup = targetGroup && targetRow
          ? positionSubgroups(resolveCodeItemSubgroups(targetGroup), getCodeItemDigits(targetGroup)).find(subgroup => (
              subgroup.name.toLowerCase() === normalizeRowGroupName(targetRow.group)?.toLowerCase()
            ))
          : null
        if (!targetGroup || !targetRow) return null
        const fallbackStart = 0
        const fallbackEnd = getCodeItemDigits(targetGroup) - 1
        return (
          <CompositeOptionDialog
            open={true}
            digits={getCodeItemDigits(targetGroup)}
            subgroupName={targetSubgroup?.name || normalizeRowGroupName(targetRow.group) || 'Group'}
            start={targetSubgroup?.start ?? fallbackStart}
            end={targetSubgroup?.end ?? fallbackEnd}
            value={targetRow.optionCode}
            onClose={() => {
              setCompositeOptionTarget(null)
              if (targetRow.isNew && !targetRow.optionCode) cancelNewRow(compositeOptionTarget.gIdx, compositeOptionTarget.rIdx)
            }}
            onApply={(option) => void applyCompositeOption(option)}
          />
        )
      })()}

      <ConditionConfigDrawer
        open={conditionDrawerOpen}
        onClose={() => {
          setConditionDrawerOpen(false)
          setConditionDrawerTarget(null)
          setProposedRule(null)
        }}
        onApply={async (config) => {
          await handleApplyConditionConfig(config)
          setProposedRule(null)
        }}
        notice={proposedRule?.note ?? null}
        initialConfig={conditionDrawerInitialConfig}
        availableReferences={availableReferences}
        currentCodeItem={currentCodeItem}
        codeItemsData={codeItemsData}
        ruleValidationContext={ruleValidationContext}
      />

      <CommentsDrawer
        open={commentsDrawerOpen}
        onClose={() => {
          setCommentsDrawerOpen(false)
          setCommentsDrawerTarget(null)
        }}
        entityId={id}
        codeItem={commentsDrawerTarget ? groups[commentsDrawerTarget.gIdx]?.codeItemPos.code : undefined}
        optionCode={commentsDrawerTarget ? groups[commentsDrawerTarget.gIdx]?.rows[commentsDrawerTarget.rIdx]?.optionCode : undefined}
        currentUserId={user?.id}
        currentUserName={user?.name}
        canManageComments={canManageComments}
        accessMessage={commentsAccess.message}
        onCommentClick={handleCommentClick}
      />

      <AttributeSchemaDialog
        open={attributeSchemaTarget !== null}
        group={attributeSchemaDialogGroup}
        rowGroupNames={attributeSchemaDialogRowGroupNames}
        onClose={() => setAttributeSchemaTarget(null)}
        onSave={(schemas) => {
          if (attributeSchemaTarget !== null) {
            void handleSaveAttributeSchemas(attributeSchemaTarget, schemas)
          }
        }}
      />

      <ValueAttributesDialog
        open={valueAttributesTarget !== null}
        group={valueAttributesDialogContext.group}
        row={valueAttributesDialogContext.row}
        attributes={valueAttributesDialogContext.attributes}
        initialFocusAttributeId={valueAttributesDialogContext.initialFocusAttributeId}
        onClose={() => setValueAttributesTarget(null)}
        onSave={handleSaveValueAttributes}
      />

      <ReferenceAppendixModal
        open={referenceAppendixTarget !== null}
        subtitle={describeAppendixRefTargetOf(referenceAppendixTarget)}
        appendices={appendixSummaries}
        disabledKeys={referenceAppendixDisabledKeys}
        recentKeys={recentAppendixPicks}
        onClose={() => setReferenceAppendixTarget(null)}
        onReference={handleReferenceAppendix}
      />

      <ReferencedAppendicesModal
        open={referencedAppendicesTarget !== null}
        subtitle={describeAppendixRefTargetOf(referencedAppendicesTarget)}
        refs={refsForAppendixRefTarget(referencedAppendicesTarget)}
        canRemove={canReferenceAppendices}
        onClose={() => setReferencedAppendicesTarget(null)}
        onRemove={handleRemoveAppendixRef}
      />

      <Menu
        anchorEl={codeItemActionMenu?.element}
        open={Boolean(codeItemActionMenu)}
        onClose={() => setCodeItemActionMenu(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 210,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }
        }}
      >
        <MenuItem
          disabled={!canMutateCodeSheet || hasPendingRowChanges}
          onClick={() => {
            if (codeItemActionMenu) {
              setAttributeSchemaTarget(codeItemActionMenu.gIdx)
              setCodeItemActionMenu(null)
            }
          }}
          sx={{ fontSize: '14px', py: 1, px: 2 }}
        >
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Configure Attributes
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={addMenuAnchor?.element}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }
        }}
      >
        <MenuItem
          disabled={!canMutateCodeSheet || hasPendingRowChanges || addMenuAnchor?.sectionKey === TITLE_ROW_INSERT_KEY}
          onClick={() => {
            if (addMenuAnchor) {
              if (addMenuAnchor.rIdx !== null) {
                addRowBelow(addMenuAnchor.gIdx, addMenuAnchor.rIdx)
              } else {
                insertRowAtIndex(
                  addMenuAnchor.gIdx,
                  addMenuAnchor.insertIndex ?? 0,
                  addMenuAnchor.rowGroupName,
                  addMenuAnchor.draftSectionId || undefined
                )
              }
              setAddMenuAnchor(null)
            }
          }}
          sx={{ fontSize: '14px', py: 1, px: 2 }}
        >
          Add New Row
        </MenuItem>
        <MenuItem
          disabled={!canMutateCodeSheet || hasPendingRowChanges}
          onClick={() => {
            if (addMenuAnchor) {
              void handleCreateRowGroup(addMenuAnchor.gIdx)
              setAddMenuAnchor(null)
            }
          }}
          sx={{ fontSize: '14px', py: 1, px: 2 }}
        >
          Add New Group
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!canManageComments) return
            if (addMenuAnchor?.rIdx !== null && addMenuAnchor) {
              setCommentsDrawerTarget({ gIdx: addMenuAnchor.gIdx, rIdx: addMenuAnchor.rIdx })
              setCommentsDrawerOpen(true)
              setAddMenuAnchor(null)
            }
          }}
          disabled={addMenuAnchor?.rIdx === null || !canManageComments}
          sx={{ fontSize: '14px', py: 1, px: 2 }}
        >
          Add New Comment
        </MenuItem>
      </Menu>
    </div>
  )
}

export default CodeIndexDetailsPage
